import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { DEFAULT_CONFIG, TMS_MODES, PERMS, RESOLVE_OUTCOMES, getConfig, commitConfig, subscribe, inScope, ladderFor, currentRung, canApprove, setLive as setLiveFlag, getSaveError } from "../shared/brokerConfig.js";
import { freight } from "../lib/freightApi.js";
import { docketComplaint, DOCKET_MAX_DIGITS } from "../shared/docket.js";
import { brokerCarriersFromApi, brokerCarrierFromApi, brokerCustomerFromApi, brokerCustomersFromApi, brokerLoadsFromApi } from "./fromApi.js";

/* --------------------------------- tokens ---------------------------------- */

const T = { ink: "#000000", ink2: "#5A5A5A", ink3: "#8C8C8C", ink4: "#BFBFBF", hair: "#DCDCDC", hairSoft: "#EDEDED", wash: "#F5F5F5", surface: "#FFFFFF" };
const sans = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Inter, system-ui, sans-serif`;
const mono = `ui-monospace, "SF Mono", SFMono-Regular, Menlo, monospace`;
const MIN = 60000, HOUR = 3600000;

/* real timestamps + facility time zones, not clock strings */
//
// ⛔ A STOP WITHOUT AN APPOINTMENT WINDOW IS THE NORMAL STATE OF A NEW LOAD. `stops` is jsonb
// holding whatever the screen wrote, and a load created from the sheet has seq/type/name/city
// and nothing else — no apptStart, no apptEnd, no eta, no tz. These three guarded `ts == null`
// and then handed an undefined or unparseable value to Intl anyway: `new Date(undefined)` is an
// Invalid Date, `toLocaleTimeString` on it returns the string "Invalid Date", and
// `formatToParts` THROWS "Invalid time value" — which took the whole load inspector down.
//
// A time nobody has set is an em dash. A mis-stated time would be worse than none, because a
// dispatcher writes it down.
const ms = (ts) => {
  if (ts == null || ts === "") return null;
  const t = ts instanceof Date ? ts.getTime() : (typeof ts === "number" ? ts : Date.parse(ts));
  return Number.isFinite(t) ? t : null;
};
const safeZone = (tz) => {
  if (!tz) return undefined;
  try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); return tz; } catch { return undefined; }
};
const fmt = (ts, tz) => {
  const t = ms(ts);
  return t === null ? "\u2014" : new Date(t).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: safeZone(tz) });
};
/**
 * A calendar date, for a record somebody reads months later.
 * ⛔ NOT `fmtDay`, which is { weekday, hour, minute } — a block recorded three weeks ago
 * rendered "Tue, 9:14 AM". The schema's own note says this justification is "the half you
 * need months later in a chargeback or a bond claim"; a weekday is not that.
 */
const fmtDate = (ts) => {
  const t = ms(ts);
  return t === null ? null : new Date(t).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};
const fmtDay = (ts, tz) => {
  const t = ms(ts);
  return t === null ? "\u2014" : new Date(t).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit", timeZone: safeZone(tz) });
};
/** '' when there is no time or no zone to name one in — the caller renders nothing. */
const tzAbbr = (ts, tz) => {
  const t = ms(ts);
  const zone = safeZone(tz);
  if (t === null || !zone) return "";
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" })
      .formatToParts(new Date(t)).find((p) => p.type === "timeZoneName");
    return part ? part.value : "";
  } catch { return ""; }
};
const mins = (ms) => Math.round(ms / MIN);
const waitLabel = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
const durLabel = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const stamp = () => new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

/* --------------------------------- people ---------------------------------- */

/**
 * The board holds no configuration of its own. Everything below reads from the
 * config that onboarding wrote. DEMO_TEAM exists only to seed a demo config when
 * you open the board without having run onboarding first.
 */
const DEMO_TEAM = [
  { id: "u1", name: "Marisol Vega", role: "Carrier sales", branch: "Houston", phone: "(713) 555-0231", hours: "Mon–Fri, 7:00a–6:00p CT", onHours: true, ooo: false, notify: "Phone call", can: ["cover"] },
  { id: "u2", name: "Dev Ramaswamy", role: "Customer/account manager", branch: "Houston", phone: "(713) 555-0244", hours: "Mon–Fri, 7:00a–6:00p CT", onHours: true, ooo: false, notify: "Slack", can: ["cover"] },
  { id: "u3", name: "Anita Krause", role: "Compliance", branch: "All branches", phone: "(713) 555-0259", hours: "Mon–Fri, 8:00a–5:00p CT", onHours: true, ooo: false, notify: "Phone call", can: ["vetting", "claims"] },
  { id: "u4", name: "Paul Whitlock", role: "Accounting", branch: "All branches", phone: "(713) 555-0267", hours: "Mon–Fri, 8:00a–5:00p CT", onHours: true, ooo: false, notify: "Email", can: ["money"] },
  { id: "u5", name: "Terrance Bly", role: "Branch manager", branch: "All branches", phone: "(713) 555-0288", hours: "Any time", onHours: true, ooo: false, notify: "Phone call", can: ["cover", "vetting", "money", "claims"] },
  { id: "u6", name: "Jonah Reyes", role: "After hours", branch: "All branches", phone: "(713) 555-0290", hours: "Nights and weekends", onHours: false, ooo: true, notify: "Phone call", can: ["cover"] },
  { id: "u7", name: "Callie Nunez", role: "Carrier sales", branch: "Lafayette", phone: "(337) 555-0122", hours: "Mon–Fri, 7:00a–6:00p CT", onHours: true, ooo: false, notify: "Slack", can: ["cover"] },
];

const DEMO_CONFIG = {
  ...DEFAULT_CONFIG,
  live: true,
  tms: { ...DEFAULT_CONFIG.tms, connected: true, mode: "full", keyMask: "••••1a7d" },
  broker: { ...DEFAULT_CONFIG.broker, verified: true, callNumber: "(713) 999-0221", attestation: "A", callerId: "Gulf Bridge Freight" },
  scope: { ...DEFAULT_CONFIG.scope, branches: ["Houston", "Lafayette"], mode: "equipment", equipment: ["Dry van", "Reefer"] },
  team: DEMO_TEAM,
};

/* module-level mirror so helpers can read config without prop-drilling */
let CFG = null;
const setCfgMirror = (c) => { CFG = c; };
// ⛔ NEVER NULL, BECAUSE SEVEN CALLERS READ `.branch` AND `.can` OFF IT. A seat id that is not
// on the team is a real state — a rep removed from the roster while their tab is open, or a
// brokerage whose team has not been set up — and `me.branch` threw on it. Falls back to the
// first team member, then to a seat that can see everything and approve nothing.
const NO_SEAT = { id: null, name: "No seat selected", role: "", branch: "All branches", can: [] };
// ⛔ NO `|| team[0]`. An unknown seat id falling back to the FIRST roster member silently
// inherits that person's authority — margins visible, ceiling editable — and writes their name
// into the audit trail for an action they did not take. NO_SEAT fails closed: it can read
// everything and approve nothing, which is the right answer for "who is this?".
const byId = (id) => {
  const team = (CFG && Array.isArray(CFG.team)) ? CFG.team : [];
  return team.find((t) => t.id === id) || NO_SEAT;
};
const sees = (me, branch) => !!me && (me.branch === "All branches" || me.branch === branch);
const seatIds = () => (CFG ? CFG.team.filter((t) => !t.ooo).map((t) => t.id) : []);
const mode = () => (CFG ? TMS_MODES[CFG.tms.mode] : TMS_MODES.watch);
const canCall = () => !!CFG && CFG.live && mode().canCall;
const canWrite = () => !!CFG && CFG.live && mode().canWrite;

/* -------------------------------- customers -------------------------------- */

const CUSTOMERS = [
  { id: "k1", name: "Sunbelt Distribution", branch: "Houston", contact: "Priya Raman", phone: "(602) 555-0144",
    updateVia: "call", portal: null, detentionFree: 120, accessorial: "Up to $150 without approval", doNotCall: false,
    terms: "Net 30 · POD required with invoice", note: "Wants a live call at pickup and delivery. Will take a voicemail." },
  { id: "k2", name: "Beaumont Polymers", branch: "Houston", contact: "Lena Ortiz", phone: "(409) 555-0190",
    updateVia: "portal", portal: "supplier.beaumontpoly.com", detentionFree: 180, accessorial: "No accessorials without written approval", doNotCall: true,
    terms: "Net 45 · portal milestones mandatory", note: "Contractually requires portal milestone updates. A phone call instead of a portal update is a service failure." },
  { id: "k3", name: "Midwest Grocers", branch: "Houston", contact: "Grant Ellis", phone: "(816) 555-0177",
    updateVia: "edi", portal: "EDI 214 via SPS", detentionFree: 120, accessorial: "Detention only, at contract rate", doNotCall: true,
    terms: "Net 30 · temp recorder required", note: "EDI 214 on every milestone. Calls only for exceptions." },
  { id: "k4", name: "Delta Fabrication", branch: "Lafayette", contact: "Roy Chapman", phone: "(318) 555-0161",
    updateVia: "call", portal: null, detentionFree: 90, accessorial: "Tarp pay pre-approved to $100", doNotCall: false,
    terms: "Net 30", note: "Small shop, prefers a call. Roy answers his cell." },
  { id: "k5", name: "Metro Print Supply", branch: "Houston", contact: "Ada Nwosu", phone: "(404) 555-0133",
    updateVia: "email", portal: null, detentionFree: 120, accessorial: "Ask first", doNotCall: false, terms: "Net 30", note: "Email summary at pickup and delivery is enough." },
];
// ⛔ THE CUSTOMER LOOKUP MUST COME FROM THE FETCH, NOT A SEED ARRAY.
//
// `custById` is called from five places, three of them as `custById(l.customerId).name` with no
// guard — the load row, the search filter, the activity line and the loads list. It searched the
// CUSTOMERS constant, so the moment real loads arrive with real customer ids it returns null and
// every one of those throws. The carrier board hit the identical problem with its dispatcher
// lookup and solved it the same way: the fetch publishes into a registry and the lookup is
// unchanged, because threading live state through five call sites — several of them module-level
// helpers outside any component — would mean rewriting a lot of working interface.
let CUSTOMERS_LIVE = null;
// ⛔ AND WHETHER THE READ FAILED, which is a third fact. `setCustomerRegistry([])` told
// `custById` "the book is empty", and its stub says "Customer not on file" — a positive claim
// that this customer is absent, made on a read that never answered. The Loads, Overview and
// History tabs carry no explanation of their own, so five rows asserted an absence with
// nothing on screen saying why.
let CUSTOMERS_UNREADABLE = false;
/**
 * Publish the real customer list. Call it with `[]` for a brokerage that has none — that is a
 * different fact from never having asked, and `customerList` must not confuse them.
 *
 * ⛔ THIS HAD NO CALLER AT ALL until the Customers tab was wired, so `custById` fell back to
 * the seed array for every load on the board: a real load's customer rendered as one of five
 * invented accounts, complete with "120 minutes" of detention free time and an update channel
 * nobody had agreed to. The 11 lines of comment here described a fetch that did not exist.
 */
export function setCustomerRegistry(rows, { unreadable = false } = {}) {
  CUSTOMERS_LIVE = Array.isArray(rows) ? rows : null;
  CUSTOMERS_UNREADABLE = !!unreadable;
}
/** The live customer list, or the seeds while nothing has published one. */
const customerList = () => (CUSTOMERS_LIVE === null ? CUSTOMERS : CUSTOMERS_LIVE);
// ⛔ NEVER null. Three call sites read `.name` off this unguarded, and a load whose customer was
// deleted — or simply not loaded yet — is a legal row. An unnamed customer is a fact to show,
// not a crash.
const custById = (id) => customerList().find((c) => String(c.id) === String(id))
  || { id, name: CUSTOMERS_UNREADABLE ? "Customer file did not load" : "Customer not on file",
       branch: null, contact: null, phone: null,
       updateVia: null, portal: null, detentionFree: null, accessorial: null,
       // ⛔ null, not false — `false` is a positive claim that this customer takes phone
       // calls, and this stub stands for a customer nobody has on file at all.
       doNotCall: null, terms: null, note: null };
const UPDATE_VIA = { call: "Phone call", portal: "Their portal", edi: "EDI 214", email: "Email only" };
/**
 * How a customer wants to be updated — or that nobody has asked.
 *
 * ⛔ `UPDATE_VIA[c.updateVia]` RENDERED THE WORD "undefined". `broker_customers` stores name,
 * contact, phone, email, branch and notes; the channel, the free time, the accessorial
 * authority, the terms and the no-voice flag were all seed-only. Five sites read this one.
 */
export const updateChannel = (c) => UPDATE_VIA[c && c.updateVia] || "Not recorded";

/**
 * Must this account be reached by something other than a phone call?
 *
 * ⛔ TWO FACTS, EITHER OF WHICH IS ENOUGH. Both dial gates used to read `doNotCall === true`
 * alone — so an account whose recorded channel is a portal or EDI, with the separate "may we
 * phone them" question left unanswered, was dialable. This screen's own copy calls phoning a
 * portal-only customer a service failure, and the portal channel IS that mandate: nobody
 * records "updates go through their portal" about an account they are happy to have called.
 *
 * ⛔ AND IT IS NOT AN INFERENCE ABOUT CONSENT. It does not write `doNotCall`; it decides what
 * this screen may offer. An unanswered question stays unanswered, and the Inspector still says
 * so beside the button.
 */
export const mandatesNonVoice = (c) => !!c && (c.doNotCall === true || c.updateVia === "portal" || c.updateVia === "edi");
/** Minutes of free time, or that it is not on file. Never "null minutes". */
export const freeTimeLabel = (c) => (c && c.detentionFree !== null && c.detentionFree !== undefined
  ? `${c.detentionFree} minutes` : NOT_CHECKED);

// ⛔ ABSENT READS AS ABSENT. broker/fromApi.js deliberately returns null for every field in the
// fraud dossier the seeds carried and the database does not have — authority age, safety rating,
// out-of-service rate, record changes, VOIP, insurance, on-time, detention-free, tracking. This
// screen rendered all of them as though they were present: `carrier.ins.auto` threw on null, and
// `${carrier.safety} · ${carrier.oos}` printed "null · null".
//
// That matters more here than anywhere else in this app. A broker reads those fields to decide
// whether a stranger claiming to be a carrier is real, and double-brokering is the loss this
// product exists to prevent. "0% out of service" invented for a screenshot is the screen telling
// a broker a carrier is safe because a developer needed a number.
const NOT_CHECKED = "Not checked";
const absent = (v, fallback = NOT_CHECKED) => (v === null || v === undefined || v === "" ? fallback : v);
/** A fraction as a percentage, or an em dash. Never "0%" for an absent measurement. */
const pctOf = (v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? "\u2014" : `${Math.round(Number(v) * 100)}%`);
/**
 * ⛔ A MACHINE CODE IS NOT A SENTENCE. `auth/client.js` builds its Error from
 * `data.message || data.error`, and neither broker-carrier route sets `message` — so the
 * broker was shown `no_brokerage`, `not_found`, `Failed to fetch` and `HTTP 500` verbatim.
 * `no_brokerage` in particular has an actionable meaning the broker cannot possibly infer.
 * Anything unrecognised keeps its own text rather than being flattened to "an error".
 */
/**
 * What a broker is told when a status change did not land.
 *
 * ⛔ PURE AND EXPORTED, because the branch that builds it lives inside an async handler the
 * render tests cannot reach — and the first version of it appended the READ-failure warning
 * ("nothing on this tab is current") to a write failure, where it is false: the read
 * succeeded, the list is the carrier file. One variable carrying two meanings. A test can
 * only hold that apart if the sentence is reachable without a click.
 */
/**
 * What a loads list says when it has no rows to show — one of FOUR different facts.
 *
 * ⛔ EXTRACTED BECAUSE THE LOADS VIEW IS BEHIND A CLICK. `react-dom/server` runs no effects and
 * cannot change the view, so the ladder inside the JSX was unreachable by any render assertion
 * and a mutation to it passed the whole suite — the same reason `updateChannel` and
 * `freeTimeLabel` live outside the panel they feed.
 *
 * The order is the point. A failed read is NOT an empty board: "No loads on your board yet"
 * after a 500 tells a rep their morning is quiet when the truth is that nobody knows.
 */
export function loadListNote({ loading = false, loadError = null, total = 0, matching = 0, branch = "All" } = {}) {
  // ⛔ NOT "do not work from this list" — the failure path sets `loads` to [], so there is no
  // list to work from. What the reader needs is that the ABSENCE is not information.
  if (loadError) return `${loadError} This is not an empty board — nobody knows what is on it until the read succeeds.`;
  if (loading) return "Reading your board\u2026";
  if (total === 0) return "No loads on your board yet. Alfred works the loads your TMS or your reps put here; nothing is seeded for you.";
  if (matching === 0) return `No loads match${branch !== "All" ? ` in ${branch}` : ""}.`;
  return null;
}

/**
 * How a panel describes a board it cannot read FROM — in flight and failed are not the same.
 *
 * ⛔ THE FIRST VERSION COLLAPSED THEM. Every one of these panels was gated on a single
 * `loadsKnown = loads !== null && !loadError`, so on first paint — before the fetch has
 * answered — the Overview said "Could not read your board", the Customers tab said it, and a
 * carrier's profile said "The board could not be read." All four were asserting a failure that
 * had not happened, every single time the page loaded. An independent review caught it.
 */
export function boardStateNote({ loading = false, failed = false } = {}) {
  if (failed) return "Could not read your board";
  if (loading) return "Still reading your board";
  return null;
}

/**
 * Why a ceiling was refused before anything was sent, or null to send it.
 *
 * ⛔ THE REASON IS FOR THE PERSON TYPING. Both refusals used to write a line into the audit
 * trail on another tab and return, so the operator saw a button that did nothing.
 */
export function ceilingRefusal(v, load) {
  if (!Number.isFinite(v) || v <= 0) return "a ceiling must be above zero.";
  // ⛔ `load.sell != null`, NOT `load.sell`. `v >= null` is `v >= 0` — true for every ceiling —
  // so on an unpriced load this refused everything and blamed a customer rate that is unknown.
  if (load && load.sell != null && v >= load.sell) {
    return `a ceiling must be below the $${load.sell.toLocaleString()} customer rate — there is no margin in it otherwise.`;
  }
  return null;
}

/**
 * A 4xx means the server read the request and refused it, so the outcome is KNOWN: nothing was
 * written. Anything else — a 5xx, a timeout, a dropped connection — leaves it unknown.
 */
export const definiteRefusal = (err) => Number(err?.status) >= 400 && Number(err?.status) < 500;

/**
 * Refusals that also mean THIS SCREEN'S IDEA OF THE ROW IS WRONG.
 *
 * ⛔ A DEFINITE REFUSAL IS NOT ALWAYS A REASON TO STOP TALKING. Both of these are 4xx — nothing
 * was written — but `not_found` means the carrier is no longer in the file and `no_brokerage`
 * means there is no file, so a message that asserts what the stored status IS contradicts the
 * very reason it was given. An independent review printed it: "…that carrier is no longer in
 * your file — somebody may have changed it. This screen still shows it as 'not reviewed', which
 * is still the stored status." Both halves cannot be true, and the second is false of a row
 * that is gone.
 */
export const STALE_WRITE_CODES = new Set(["not_found", "no_brokerage"]);

/**
 * A ceiling write that failed, in the words of the load it was for.
 *
 * ⛔ IT DOES NOT CLAIM MORE THAN IT KNOWS, AND NOT LESS EITHER. Two review passes on this one
 * sentence:
 *
 *  - The first version said "was NOT changed … It is still $3,100" — two assertions about the
 *    database, made on exactly the failures where the browser cannot know (a dropped connection,
 *    or a 502 after the row committed).
 *  - The replacement said "This screen still shows $3,100", which is FALSE for the only user who
 *    can reach this error: setting a ceiling requires `canSetCeiling`, and that branch renders an
 *    INPUT holding the figure they just typed, not the stored one. It named the one number not
 *    on the screen.
 *
 * So: it names the SAVED ceiling as of the last read, and it distinguishes a definite refusal
 * (the server answered 4xx — nothing was written, and it said why) from an unknown outcome,
 * where a reload is the only way to find out. Telling someone to reload after a refusal the
 * server explained is noise.
 */
export const ceilingFailure = (load, err, { definite = definiteRefusal(err) } = {}) => {
  // ⛔ CLASSIFIED HERE BY DEFAULT, so a call site cannot forget. It took `definite` as a
  // required option and a mutation proved the handler could simply stop passing it — every
  // assertion about the two sentences stayed green because they called this function directly.
  // `writeFailure` derives it the same way. The option survives so a test can force a branch.
  // Both halves read as sentences with no ceiling on file, which "the saved ceiling was no
  // ceiling" did not.
  // ⛔ ONE CLAUSE, CASED AT USE. Building the capitalised form and then lower-casing it with
  // `.replace(/^No /, "no")` produced "noceiling was saved" — the replace ate the space. Built
  // lower-case once and capitalised where a sentence starts with it.
  const saved = load.ceiling == null
    ? "no ceiling was saved as of the last read"
    : `the saved ceiling was $${load.ceiling.toLocaleString()} as of the last read`;
  const Saved = saved.charAt(0).toUpperCase() + saved.slice(1);
  // ⛔ WHAT WAS WRITTEN, NOT WHAT IS STORED. This said "The saved ceiling is still $3,100",
  // which is the same overclaim an independent review found on `writeFailure`: the browser
  // cannot read the database, so "still" is a claim about the absence of any other writer. It
  // names what the last read returned, and the refusal says nothing was written.
  return definite
    ? `The ceiling on ${load.ref} was not changed — ${humanError(err)} Nothing was written; ${saved}.`
    : `The ceiling on ${load.ref} may not have been changed — ${humanError(err)} ${Saved}; reload to see where the change landed.`;
};

/**
 * A carrier's status write that failed, on the screen where a brokerage records that a carrier
 * defrauded them.
 *
 * ⛔ IT SAID BOTH THINGS AT ONCE. The sentence opened "X was NOT moved to do not use" — an
 * assertion that the database did not change — and closed with "reload to see where the change
 * landed", which concedes it cannot know. One message contradicting itself, and the comment
 * sitting between them claimed the first half did not happen. A review of the ceiling's
 * version of this sentence found the same defect here; the ceiling was fixed and this was left,
 * so the two screens disagreed about what a failed write may claim.
 *
 * Split the way `ceilingFailure` splits it: a 4xx means the server read the request and
 * refused it, so nothing was written and "was not moved" is TRUE — and telling somebody to
 * reload after a refusal the server explained is noise. Anything else (a 5xx, a timeout, a
 * dropped connection after the row committed) leaves the outcome unknown, and then the only
 * honest claims are what this screen is showing and how to find out.
 *
 * ⛔ "This screen still shows it as X" IS TRUE HERE, unlike on the ceiling. The carrier profile
 * renders the status as a chip beside this message; the ceiling's editable branch renders an
 * input holding the figure the operator just typed, which is why that one names the SAVED value
 * instead. Same rule, different screen — said out loud because the wording looks copy-pasteable
 * and is not.
 */
/**
 * One recorded status decision, as a sentence.
 *
 * ⛔ `fromStatus` IS NULL ON THE FIRST ONE. A carrier starts at `prospect` with no event, so
 * the first decision has nothing to come from — and "→ do not use" with a blank on the left
 * reads as data missing rather than as the beginning of the record.
 *
 * ⛔ AND IT USES THE BROKER'S WORDS. `prospect` / `approved` / `blocked` are column values;
 * this screen calls them "not reviewed" / "approved" / "do not use" everywhere else, and the
 * one place it did not was a solid black tag reading "prospect" above a control calling the
 * same state "Not reviewed".
 */
/**
 * What the decision trail says when it has no rows to show — one of FOUR facts.
 *
 * ⛔ EXTRACTED BECAUSE THE PROFILE IS BEHIND A CLICK, the same reason `loadListNote` is: the
 * carrier profile only renders once somebody opens a carrier, and `react-dom/server` runs no
 * effects and cannot click. A ladder inside the JSX would be unreachable by any render
 * assertion.
 *
 * ⛔ AND A FAILED READ IS NOT AN EMPTY TRAIL. On the screen that decides whether a carrier
 * defrauded this brokerage, "no status decision has been recorded" reads as "never been
 * blocked" — so the error has to outrank everything and say that it is not that.
 */
/**
 * ⛔ TWO IDS ARE THE SAME ID ONLY IF BOTH ARE REAL IDS.
 *
 * This board compares ids as `String(a) === String(b)` in a dozen places, which is right for
 * the real divergence (`pg` hands BIGINT back as a string, so a numeric 2 and "2" are one
 * carrier) and wrong for the absent ones: `String(null) === String(null)` is TRUE. So is
 * `String(undefined) === String(undefined)`, and `String({}) === String({})` — both sides
 * stringify to "[object Object]".
 *
 * That is not hypothetical here. This file has already shipped it: the profile's audit list
 * matched carriers on the MC number, which is null for most real broker_carriers rows, so
 * `null === null` attributed one carrier's do-not-use decision to EVERY carrier without an MC.
 * An independent review found the same hole reopening one coercion layer down in the decision
 * trail's stamp, and rendered it.
 *
 * So a key is a finite number or a non-blank string, and nothing else is an id. Absent, blank
 * or non-scalar on either side means "not the same" — which on the trail means the panel says
 * it is still reading, rather than naming a carrier the record does not belong to.
 */
export function sameId(a, b) {
  const key = (v) => (typeof v === "number" && Number.isFinite(v) ? String(v)
    : typeof v === "string" && v.trim() !== "" ? v
      : null);
  const ka = key(a);
  return ka !== null && ka === key(b);
}

export function statusTrailNote({ loading = false, error = null, count = 0, status = null, truncated = false } = {}) {
  if (error) return `${error} This is not a record of no earlier decisions — nobody knows what is on file until the read succeeds.`;
  if (loading) return "Reading the decisions on file\u2026";
  if (count === 0) {
    // ⛔ AN EMPTY TRAIL ON A CARRIER THAT IS NOT AT ITS CREATED STATUS IS NOT AN EMPTY RECORD —
    // IT IS A MISSING ONE, and the screen already holds the data to tell them apart.
    //
    // An independent review rendered this: a blocked carrier with no events showed
    //   "On file for this status: Double-brokered GB-88214 … anita@b.test"
    // and twenty words below it
    //   "nobody has approved or blocked them."
    // Three ways in, all real: the event insert is deliberately swallowed when it fails, so a
    // status can commit without its record; carriers blocked before this table existed have a
    // status and no events; and any direct SQL fix leaves the same shape. The false half is the
    // one a brokerage acts on.
    if (status && status !== "prospect") {
      return `This carrier is recorded as ${statusWord(status)}, and no decision is on file.`
        + " The record is incomplete — the status was set without one, or its record was lost."
        + " Do not read this as \u201cnever reviewed\u201d.";
    }
    return "No status decision has been recorded for this carrier. They are at the status they"
      + " were created with — nobody has approved or blocked them.";
  }
  // ⛔ AND THE CAP DROPS THE OLDEST. Ordered newest-first, so a capped list is missing exactly
  // the earliest decision — the one whose reason the current status has already overwritten.
  if (truncated) {
    return `Showing the ${count} most recent decisions. There are older ones this screen is not`
      + " showing, and the oldest is the one the current reason has already replaced.";
  }
  return null;
}

export const statusEventLine = (e) => {
  if (!e || !e.fromStatus) return `First recorded decision: ${statusWord(e && e.toStatus)}`;
  // ⛔ A SAME-STATUS DECISION IS A RE-AFFIRMATION, NOT A TRANSITION. "do not use → do not use"
  // reads as a change nobody made. The backend refuses a reason-less no-op, so a row in this
  // shape is a brokerage re-stating a do-not-use with fresh justification — which is a real
  // act, and the reason beside it is the whole point of recording it.
  if (e.fromStatus === e.toStatus) return `Re-affirmed: ${statusWord(e.toStatus)}`;
  return `${statusWord(e.fromStatus)} → ${statusWord(e.toStatus)}`;
};

export const writeFailure = (carrier, target, err) => {
  const who = carrier?.name || "That carrier";
  const showing = `This screen still shows it as "${statusWord(carrier?.status)}"`;
  // ⛔ A STALE ROW IS A THIRD CASE. `not_found` and `no_brokerage` are 4xx — so nothing was
  // written, which the definite branch gets right — but they also mean the row is not where
  // this screen thinks it is, and that is the one 4xx where reloading IS the advice. Reported
  // as "nothing was written, and the screen may be out of date" rather than either extreme.
  if (STALE_WRITE_CODES.has(String(err?.body?.error || ""))) {
    return `${who} was not moved to ${statusWord(target)} — ${humanError(err)} Nothing was written.`
      + ` ${showing}, which may no longer be right — reload.`;
  }
  return definiteRefusal(err)
    ? `${who} was not moved to ${statusWord(target)} — ${humanError(err)} Nothing was written. ${showing}.`
    : `${who} may not have been moved to ${statusWord(target)} — ${humanError(err)} ${showing} — reload to see where the change landed.`;
};

const humanError = (err) => {
  // ⛔ THE SERVER'S OWN SENTENCE WINS. This read `body.error` first, so a refusal the backend
  // had already written in words — "That reason is 2140 characters; the most this will store is
  // 2000. Shorten it — it is not being saved truncated." — was thrown away and the operator got
  // the bare code `reason_too_long.` instead. Reachable: the reason box has no maxLength.
  const said = String(err?.body?.message || "").trim();
  if (said) return said.replace(/([^.!?])$/, "$1.");
  const raw = String(err?.body?.error || err?.message || "").trim();
  const map = {
    no_brokerage: "your brokerage setup is not finished yet, so there is no carrier file to read.",
    not_found: "that carrier is no longer in your file — somebody may have changed it.",
    bad_status: "that is not a status this screen can set.",
    // ⛔ A 403 REACHED THE OPERATOR AS THE WORD "Forbidden." — the server's own HTTP noun, as
    // the final word of a sentence about a fraud record. `auth.mjs` answers 403 when the
    // signed-in address is not on the allowlist or the token's scope is wrong, so this says
    // that without guessing which.
    Forbidden: "this sign-in is not allowed to change that.",
    Unauthorized: "this sign-in was not accepted.",
    "Failed to fetch": "the connection dropped before the server answered.",
  };
  if (map[raw]) return map[raw];
  if (/^HTTP 5\d\d$/.test(raw)) return "the server failed to answer (" + raw + ").";
  if (/^HTTP 4\d\d$/.test(raw)) return "the server refused the change (" + raw + ").";
  return raw ? raw.replace(/\.?$/, ".") : "the request failed.";
};

/** Two facts joined, with whichever are known. Never "null · null". */
/**
 * ⛔ WHAT THE BROKER CALLS A STATUS, NOT WHAT THE COLUMN CALLS IT.
 * `prospect` is the database default, so every real carrier's profile header carried a solid
 * black tag reading "prospect" one line above a control calling the same state "Not reviewed"
 * and a list row calling it "not reviewed". Three names for one fact, on the screen that
 * decides whether a stranger is real.
 */
/**
 * ⛔ THREE DIFFERENT REASONS A ROW ABOUT A CARRIER MIGHT NOT BE SHOWABLE, and they are not
 * interchangeable: the carrier is genuinely not in the book; the book has not been read yet;
 * the read failed. The first is a fact about the carrier, the other two are facts about us.
 * A filter that tests membership alone treats all three as the first, which silently dropped
 * SIX of seven handoff rows and every risk signal the moment the fetch was in flight — under
 * headings reading "Nothing waiting. Alfred is working the board on its own".
 *
 * `splitByCarrier` keeps the rows it can show and COUNTS the ones it cannot, so a screen can
 * say "4 hidden" instead of implying there were none.
 */
const splitByCarrier = (rows, carriers, known) => {
  if (!known) return { shown: (rows || []).filter((r) => !r.carrierId), hidden: (rows || []).filter((r) => r.carrierId).length, unread: true };
  const resolves = (r) => !r.carrierId || carriers.some((x) => String(x.id) === String(r.carrierId));
  return { shown: (rows || []).filter(resolves), hidden: (rows || []).filter((r) => !resolves(r)).length, unread: false };
};
/** The line that explains a hidden count, or null when there is nothing to explain. */
const hiddenNote = ({ hidden, unread }, what) => {
  if (!hidden) return null;
  return unread
    ? `${hidden} ${what} not shown until your carrier file loads — they name a carrier we cannot look up yet.`
    : `${hidden} ${what} not shown: ${hidden === 1 ? "it names" : "they name"} a carrier that is not in your file.`;
};

/**
 * ⛔ THE CALL ENGINE ON THIS BOARD IS A SCRIPT. THERE IS NO DIAL SEAM.
 *
 * `callStep` sets up a transcript from `scriptFor` and a timer walks through it; the whole
 * board makes four API calls and all four are carrier- or customer-file reads and writes.
 * Nothing dials. Yet what it wrote into a load's activity read as a record of a conversation:
 * "Carrier confirmed, details read back. Written to PowerBroker." — a claim that the brokerage's
 * TMS received the data — plus "No answer. Attempt logged; Alfred retries in twenty minutes."
 * for a retry nothing schedules, and "Wrong company answered the FMCSA number. Alfred ended
 * the call and flagged the carrier." as a fraud finding.
 *
 * That was tolerable while every load was a seed array nobody could mistake for their own. It
 * is not tolerable in front of a prospect, and it is the reason this tab cannot be pointed at
 * real loads until the marker exists: a fabricated record of a call that never happened, on a
 * real load, is the worst thing this screen could produce.
 *
 * Every line the engine writes goes through here, so no site can be added that forgets.
 */
export const SIMULATED = "Scripted example — no call was placed.";
const simulated = (body) => `${body} ${SIMULATED}`;
// ⛔ A PARTIAL MARKER IS WORSE THAN NONE — the lesson from the seed loads this file used to
// carry. One load's activity was marked and three were not, so a prospect who opened the
// second one correctly concluded it was real. Every write below goes through `simulated()`;
// none of them is marked by hand at the call site.

const STATUS_LABEL = { prospect: "not reviewed", flagged: "not reviewed", approved: "approved", blocked: "do not use" };
export const statusWord = (st) => STATUS_LABEL[st] || String(st || "unknown");

const joinKnown = (...parts) => parts.filter((x) => x !== null && x !== undefined && x !== "").join(" \u00b7 ") || NOT_CHECKED;

/* --------------------------------- carriers -------------------------------- */

// ⛔ SEED_CARRIERS IS GONE. Five invented carriers used to live here, each with a full
// insurance schedule, an out-of-service percentage and eight green vetting ticks — on the
// screen whose entire job is deciding whether a stranger bidding on a load is real. A
// fabricated "Satisfactory" safety rating beside a fabricated callback check is worse than an
// empty screen, because a broker would act on it. The list now comes from the brokerage's own
// book and is empty until somebody puts a carrier in it.

/* ------------------------- equipment-aware call plans ---------------------- */

const EQUIP_CAPTURES = {
  van: { load: ["BOL", "Seal", "Weight"], extra: null },
  reefer: { load: ["BOL", "Seal", "Weight", "Set point", "Continuous or cycle", "Pre-cool confirmed", "Pulp temp"], extra: "Reefer: confirm set point in Fahrenheit, continuous run, pre-cool before loading, and pulp temp at the door." },
  flatbed: { load: ["BOL", "Weight", "Straps and chains", "Tarps"], extra: "Flatbed: confirm securement count, tarping, and that the driver has re-checked straps after the first 50 miles." },
};

export function buildPlan(load) {
  // `freight` is jsonb and may be {} — a load created without equipment is legal.
  const eq = EQUIP_CAPTURES[(load.freight || {}).equipment] || EQUIP_CAPTURES.van;
  const p = [
    { id: "bids", party: "carrier", title: "Call back everyone who bid", detail: "Work the bid list, confirm equipment and availability, and take a commitment at or below the rep's ceiling." },
    { id: "vet", party: "carrier", title: "Verify the carrier", detail: "Call the number on the FMCSA record and confirm the company, the MC, and that they know about this load." },
    { id: "dispatch", party: "carrier", title: "Dispatch confirmation", detail: "Truck number, trailer, driver name and cell, and read back both appointment windows.", captures: ["Truck", "Driver", "Driver cell"] },
    { id: "custbook", party: "customer", title: "Tell the customer it's covered", detail: "Confirm coverage and the pickup appointment. No carrier rate, ever." },
  ];
  // ⛔ A STEP TITLE IS A LINE ALFRED READS OUT ON A CALL. `stops` is jsonb and a facility name
  // is optional — the create sheet writes city without one — so `at ${s.name}` produced
  // "Set the pickup appointment at undefined", which the AI would have said to a shipper.
  // The city is the fallback, and "the pickup" is the fallback for that.
  const where = (st, drop) => st.name || st.city || (drop ? "the delivery" : "the pickup");
  (load.stops || []).forEach((s) => {
    const drop = s.type === "drop";
    const at = where(s, drop);
    p.push({ id: `appt${s.seq}`, stopSeq: s.seq, party: "customer", title: `Set the ${drop ? "delivery" : "pickup"} appointment at ${at}`,
      detail: `Confirm the window and check-in requirements at ${at}.${drop ? " Delivery appointments are the hard ones — Alfred calls the facility directly if the customer doesn't hold the slot." : ""}`, captures: ["Appointment", "Check-in"] });
    p.push({ id: `arrive${s.seq}`, stopSeq: s.seq, party: "carrier", title: `Arrived at ${at}`, detail: "Confirm gate-in and start the detention clock.", captures: ["Gate-in"] });
    if (!drop) {
      p.push({ id: `dbcheck${s.seq}`, stopSeq: s.seq, party: "carrier", title: "Who dispatched you?",
        detail: "Ask the driver at the dock which company dispatched them and what they were told they're hauling. A mismatch means the load was re-brokered.", captures: ["Dispatching company", "Driver's understanding"] });
      p.push({ id: `loaded${s.seq}`, stopSeq: s.seq, party: "carrier", title: `Loaded at ${at}`, detail: `Paperwork numbers and ETA to the next stop.${eq.extra ? " " + eq.extra : ""}`, captures: eq.load });
    } else {
      p.push({ id: `pod${s.seq}`, stopSeq: s.seq, party: "carrier", title: `Delivered at ${at}`, detail: "Who signed, at what time, and whether anything was noted on the POD.", captures: ["POD signer", "POD time", "Exceptions"] });
    }
  });
  p.splice(-2, 0, { id: "track", party: "carrier", title: "Tracking check call", detail: "Position and ETA. Skipped entirely when the tracking ping is fresh." });
  p.push({ id: "custclose", party: "customer", title: "Close it out with the customer", detail: "Delivery confirmation, POD details, and any accessorial to expect on the invoice." });
  return p.map((s) => ({ ...s, at: null, state: "scheduled", notes: "", captures: s.captures || [] }));
}

/* ---------------------------------- loads ---------------------------------- */

/* --------------------------------- handoffs -------------------------------- */

const KIND = {
  fraud: "Verification — person now", claim: "Claim or incident — person now", falloff: "Carrier fell off",
  rate: "Above the ceiling", money: "Money", appointment: "Appointment change", tracking: "Tracking gone quiet", unreachable: "Repeat callbacks",
};
const MONEY_KIND = { quickpay: "Quick pay", detention: "Detention", lumper: "Lumper", tonu: "TONU", layover: "Layover" };
const NEEDS = { fraud: "vetting", claim: "claims", falloff: "cover", rate: "cover", money: "money", appointment: "cover", tracking: "cover", unreachable: "cover" };

/**
 * ⛔ WRITTEN EXAMPLES, MARKED AT THE SOURCE — the same discipline the seed loads had before they
 * were deleted, and for the reason this file already states one tab over: a PARTIAL MARKER IS
 * WORSE THAN NONE.
 *
 * Nothing in this app produces a handoff. These are scripted, and on a real board exactly ONE
 * of them survives `splitByCarrier` (the others name seed carriers that no longer resolve) — so
 * a prospect or a rep sees a single, specific, present-tense row: "Carrier fell off before
 * dispatch … Alfred reposted and is working the remaining bids", with a dollar amount, a quote
 * attributed to a phone call, and a Take-it button. An independent review found it reading as
 * genuine, and found that the sentence explaining its missing load — "GB-88231 is not on your
 * board" — made it read as MORE genuine, because it blamed the board rather than admitting the
 * handoff is a script.
 *
 * `seeded: true` is applied here rather than typed on each row, so a new row cannot be added
 * without it and `QueueItem` has one thing to check.
 */
const SEED_QUEUE = [
  { id: "q0", kind: "fraud", loadRef: "GB-88214", carrierId: "car2", waiting: 8, jumped: true, assignedTo: null,
    title: "Northline Freight — every hijack signal at once",
    quote: "You can just send the ratecon to the dispatch email, we're good to go. No need to call the office line.",
    ask: "Alfred stopped and will not advance this carrier. Compliance decides.",
    intake: [["Bid came from", "(469) 555-0142 — VOIP"], ["FMCSA record", "(972) 555-0188"], ["Callback", "No answer, twice"], ["Record changed", "Contact and address, 9 days ago"], ["Authority age", "21 days"], ["Address", "Shared with 3 other MCs"]] },
  { id: "q6", kind: "claim", loadRef: "GB-88190", carrierId: "car3", waiting: 4, jumped: true, assignedTo: null,
    title: "Reefer alarm — possible temperature claim",
    quote: "Unit threw a code somewhere past Wichita. I got it running again but I don't know how long it was down.",
    ask: "Alfred asked about safety and the unit, then stopped. Nothing else — no promises, no fault, no instructions.",
    intake: [["Load", "Fresh produce, 34°F continuous"], ["Set point on file", "34°F, pre-cooled, continuous"], ["Alarm duration", "Unknown — driver unsure"], ["Injuries", "None"], ["Location", "I-35 near Emporia, KS"], ["Breakdown endorsement", "Yes — $250,000 on file"]] },
  { id: "q7", kind: "falloff", loadRef: "GB-88231", carrierId: null, waiting: 22, assignedTo: null,
    title: "Carrier fell off before dispatch",
    quote: "We had a truck but it broke down in Lake Charles. We can't cover Orange tomorrow.",
    ask: "Load is back on the board with a 7:00 AM appointment. Alfred reposted and is working the remaining bids.",
    intake: [["Pickup", "Tomorrow 07:00–11:00 CT"], ["Notice", "18 hours"], ["Bids left on file", "2"], ["Customer told", "Not yet"]] },
  { id: "q1", kind: "money", money: "lumper", loadRef: "GB-88190", carrierId: "car3", waiting: 14, amount: "$175", assignedTo: null,
    title: "Lumper at Midwest Grocers", quote: "They want a hundred seventy-five to unload and my card won't cover it.",
    ask: "Customer terms allow detention only, at contract rate. A lumper needs written approval." },
  { id: "q2", kind: "rate", loadRef: "GB-88214", carrierId: "car5", waiting: 12, amount: "$3,050 asked · $2,800 ceiling", assignedTo: null,
    title: "Sabine River is $250 over the ceiling", quote: "We can do it, but not at that number. Thirty fifty and the truck is yours.",
    ask: "Alfred confirmed everything but the rate and stopped. It doesn't counter. Raise the ceiling or Marisol takes the call." },
  { id: "q3", kind: "tracking", loadRef: "GB-88190", carrierId: "car3", waiting: 41, assignedTo: "u2",
    title: "No ping in 2h 40m on a reefer", quote: "Dispatch rings out. Driver's cell goes to voicemail.",
    ask: "Produce due in Kansas City at 4am. Next rung is the carrier's after-hours line." },
  { id: "q4", kind: "money", money: "quickpay", loadRef: "GB-88201", carrierId: "car1", waiting: 64, amount: "$2,750 at 2%", assignedTo: null,
    title: "Redland asking for quick pay", quote: "Can you run this one on quick pay once the POD's in?",
    ask: "Alfred confirms the ask and the load. It doesn't commit to terms." },
].map((q) => ({ ...q, seeded: true }));

const RISK = [
  { id: "r1", carrierId: "car2", at: "9:20 AM", loadRef: "GB-88214", signal: "FMCSA record changed", detail: "Contact name and physical address on the FMCSA record both changed 9 days ago. This is the hijack signature, not the authority age.", severity: "stop" },
  { id: "r2", carrierId: "car2", at: "9:21 AM", loadRef: "GB-88214", signal: "Phone and address clustering", detail: "Bid number is VOIP. The address on file is shared with three other active MC numbers.", severity: "stop" },
  { id: "r3", carrierId: "car2", at: "9:22 AM", loadRef: "GB-88214", signal: "Callback failed", detail: "Two calls to the FMCSA-registered line went unanswered. The person bidding asked us to skip it.", severity: "stop" },
  { id: "r4", carrierId: "car5", at: "Yesterday", loadRef: "GB-88222", signal: "Insurance window", detail: "Cargo policy expires in 11 days, inside your 14-day flag. Cancellation notice is 30 days.", severity: "watch" },
  { id: "r5", carrierId: "car4", at: "Aug 6", loadRef: "GB-88155", signal: "Out of service rate", detail: "22% OOS across 18 inspections, plus a Conditional rating. Blocked by rule, not by judgment.", severity: "watch", handled: true },
];

/* -------------------------------- inbound ---------------------------------- */

// ⛔ FOUR INVENTED NIGHT CALLS, with "closed by Alfred" badges and past-tense summaries
// ("Alfred confirmed the appointment was 4:00 AM… No one woke up."). Nothing on this board
// answers a line. The panel that renders them says so, and the metric counting them is an
// em dash rather than a number — a count of a fiction is still a claim.
const INBOUND = [
  { id: "in1", at: "2:14 AM", night: true, from: "Cypress Haul dispatch", about: "GB-88190", topic: "Driver sitting at a closed gate",
    outcome: "resolved", summary: "Alfred confirmed the appointment was 4:00 AM, gave the check-in instructions from the load file, and told the driver where to stage. No one woke up." },
  { id: "in2", at: "11:48 PM", night: true, from: "Redland Transport", about: "GB-88201", topic: "Where is my check",
    outcome: "resolved", summary: "POD is in, invoice went out Tuesday, Net 30 means the 14th. Alfred read the terms off the file and didn't promise anything faster." },
  { id: "in3", at: "3:31 AM", night: true, from: "Unknown carrier — Laredo", about: null, topic: "Empty truck offering capacity",
    outcome: "captured", summary: "Van empty in Laredo at 6 AM, wants a Houston reload. Alfred took MC, equipment, and availability, and left it on Marisol's desk for 7 AM." },
  { id: "in4", at: "1:05 AM", night: true, from: "Sabine River Carriers", about: "GB-88222", topic: "Detention starting",
    outcome: "escalated", summary: "Driver held past free time at Gulf Steel. Alfred logged the gate-in, started the clock, and put it in Paul's queue for the morning." },
  { id: "in5", at: "9:22 AM", night: false, from: "Vale Logistics", about: null, topic: "Asking why they can't bid",
    outcome: "declined", summary: "Alfred said it can't discuss a status decision and gave them compliance's direct line. It didn't explain the OOS rate." },
  { id: "in6", at: "8:04 AM", night: false, from: "Midwest Grocers", about: "GB-88190", topic: "Checking the reefer load",
    outcome: "resolved", summary: "Read back position, set point, and ETA from the file. Customer takes EDI, so this was their call to us, not ours to them." },
];

/* -------------------------------- call log --------------------------------- */

const CALL_LOG = [
  { id: "log1", day: "today", at: "9:20 AM", dir: "out", loadRef: "GB-88214", carrierId: "car2", party: "carrier", step: "Verify the carrier", dur: 112, outcome: "Stopped — verification",
    captured: ["Record changed 9 days ago", "VOIP number", "Callback unanswered"],
    transcript: [{ who: "alfred", text: "This is Alfred with Gulf Bridge, an AI assistant, calling about load 88214. This call is recorded. Before we go further I need to reach Northline on the number in your FMCSA record." }, { who: "carrier", text: "You can just send the ratecon to the dispatch email, we're good to go." }, { who: "alfred", text: "I can't move a load forward without that callback. I'm passing this to our compliance team." }] },
  { id: "log2", day: "today", at: "9:31 AM", dir: "out", loadRef: "GB-88214", carrierId: "car5", party: "carrier", step: "Call back everyone who bid", dur: 78, outcome: "Above ceiling",
    captured: ["Truck available in Beaumont", "Asked $3,050", "Ceiling $2,800"],
    transcript: [{ who: "alfred", text: "You bid on 88214, Beaumont to Kingsport. Still have a truck?" }, { who: "carrier", text: "We do, but not at that number. Thirty fifty and it's yours." }, { who: "alfred", text: "I can confirm at or below what my rep set, and thirty fifty is above it. I'm not able to counter — Marisol will call you on the rate." }] },
  { id: "log3", day: "today", at: "8:26 AM", dir: "out", loadRef: "GB-88201", carrierId: "car1", party: "carrier", step: "Who dispatched you?", dur: 44, outcome: "Completed",
    captured: ["Driver says Redland dispatched", "Matches the booking", "No re-brokering"],
    transcript: [{ who: "alfred", text: "One quick thing while you're at the dock — which company dispatched you on this load?" }, { who: "driver", text: "Redland. Ruben sent me the sheet this morning." }, { who: "alfred", text: "That matches what I have. Thank you." }] },
  { id: "log4", day: "today", at: "10:12 AM", dir: "out", loadRef: "GB-88201", carrierId: "car1", party: "carrier", step: "Loaded at Terminal 6", dur: 94, outcome: "Completed",
    captured: ["BOL PH-55120", "Seal 22187", "42,100 lbs"],
    transcript: [{ who: "alfred", text: "Are you loaded and out of Terminal 6?" }, { who: "carrier", text: "Rolling. Seal two-two-one-eight-seven." }, { who: "alfred", text: "Two-two-one-eight-seven. BOL and weight?" }, { who: "carrier", text: "P-H five five one two zero, forty-two thousand one hundred." }, { who: "alfred", text: "Reading back: BOL P-H-5-5-1-2-0, 42,100 pounds." }] },
  { id: "log5", day: "today", at: "2:14 AM", dir: "in", loadRef: "GB-88190", carrierId: "car3", party: "carrier", step: "Inbound — closed gate", dur: 132, outcome: "Resolved without a person",
    captured: ["Appointment 4:00 AM", "Staging instructions given", "No escalation"],
    transcript: [{ who: "carrier", text: "I'm at Midwest Grocers and the gate's locked. Nobody's answering." }, { who: "alfred", text: "Your appointment is 4:00 AM, about ninety minutes out. The file says stage on the north side and check in at the guard shack when the light comes on." }, { who: "carrier", text: "Alright, that's all I needed." }] },
  { id: "log6", day: "today", at: "1:15 PM", dir: "out", loadRef: "GB-88190", carrierId: "car3", party: "carrier", step: "Tracking check call", dur: 52, outcome: "No answer",
    captured: ["Attempt 2 of 3", "No ping 2h 40m"],
    transcript: [{ who: "alfred", text: "Voicemail: Alfred with Gulf Bridge on 88190. Tracking has been quiet over two hours. Please call 713-555-0288." }] },
  { id: "log7", day: "yesterday", at: "4:20 PM", dir: "out", loadRef: "GB-88155", carrierId: "car4", party: "carrier", step: "Call back everyone who bid", dur: 39, outcome: "Declined — do not use",
    captured: ["Carrier blocked", "Bid not advanced"],
    transcript: [{ who: "alfred", text: "I'm not able to move a bid forward for your company. Our compliance team can tell you more — 713-555-0259." }] },
];

// ⛔ A SHAPE, NOT A MEASUREMENT. These twelve numbers sum to 274 and are rendered under
// "Call volume · Today, 6am to 6pm" with "Outbound today 274" beneath. Nothing counts calls on
// this board — nothing places them. The panel says so where the chart is drawn.
const HOURLY = [6, 14, 23, 31, 26, 34, 39, 28, 22, 25, 17, 9];
const HOURLY_LABELS = ["6a", "7a", "8a", "9a", "10a", "11a", "12p", "1p", "2p", "3p", "4p", "5p"];

// ⛔ THIS MAP MUST COVER WHAT THE DATABASE STORES, AND IT DID NOT.
//
// `BROKER_LOAD_STATUSES` in backend/src/lib.mjs is ['quoting','covering','tracking','delivered',
// 'cancelled'] and the column defaults to 'quoting'; this map had 'needs_carrier' and 'tonu'
// instead, and `Chip` did a bare `STATUS[status]` then read `.fill` off it. So a load in its
// DEFAULT state crashed the chip. The carrier board had this identical defect — a STATUS map
// missing the two values its own schema documents — and it was a white screen there too.
const STATUS = {
  // what the database stores
  quoting: { label: "Quoting", muted: true },
  covering: { label: "Covering", fill: true },
  tracking: { label: "Tracking" },
  delivered: { label: "Delivered", muted: true },
  cancelled: { label: "Cancelled", muted: true },
  // UI-only states the screen sets itself
  calling: { label: "On the line", fill: true, live: true },
  attention: { label: "Needs attention", fill: true },
  // ⛔ NEITHER IS A STATUS THIS SCHEMA STORES. They are kept only so that a legacy row written
  // before `broker_loads` existed reads as "Needs a carrier" rather than "needs_carrier" —
  // `statusMeta` already falls back to the raw value for anything unknown, so that is all they
  // buy. (The first version of this note claimed they were what stopped `statusMeta` returning
  // undefined; the fallback below does that, and the claim was simply false.)
  needs_carrier: { label: "Needs a carrier", muted: true },
  tonu: { label: "TONU", muted: true },
};
/** Never undefined — an unknown status is shown AS unknown rather than crashing the chip. */
export const statusMeta = (status) => STATUS[status] || { label: status ? String(status) : "No status", muted: true };

const LIMITS = [
  { t: "Counter, negotiate, or invent a rate", d: "Alfred may confirm at or below a ceiling a rep set on the load, and nothing else. It never counters, never splits the difference, never hints at the number." },
  { t: "Advance a carrier that failed verification", d: "Failed callback, changed FMCSA record, blocked authority, or a do-not-use listing ends it. No override from the phone." },
  { t: "Commit to an appointment nobody confirmed", d: "Alfred books what a facility agrees to and reports it. It doesn't promise a window." },
  { t: "Authorize money", d: "Quick pay, advances, detention, lumper, TONU — Alfred captures the ask against the customer's terms, then stops." },
  { t: "Speak for the brokerage on a claim", d: "Cargo damage, a temperature excursion, injury, or a DOT stop ends the call plan. Alfred asks about safety and the facts, admits nothing, and puts a person on." },
  { t: "Share what the other side pays", d: "Carriers never hear the customer rate. Customers never hear the carrier rate. Neither hears the margin." },
  { t: "Call anyone who asked not to be called", d: "A person saying stop is honored at the person level, permanently, even when their carrier is approved." },
  { t: "Hide that it's an AI", d: "Disclosed on every call, inbound and outbound, and repeated whenever asked." },
];

const CONSENT = [
  ["Two-party states", "Alabama, California, Delaware, Florida, Illinois, Maryland, Massachusetts, Michigan, Montana, Nevada, New Hampshire, Oregon, Pennsylvania, Washington. Alfred plays the full consent line and waits for an answer before recording."],
  ["One-party states", "Notice only — Alfred still discloses that it's an AI and that the call is recorded."],
  ["If someone declines", "Recording stops, the call continues unrecorded, and a note goes on the file. Alfred doesn't argue and doesn't re-ask."],
  ["Do-not-call", "Person-level and permanent. Survives their carrier being re-approved."],
  ["Frequency cap", "No more than 3 attempts to a person per load per day, 20 minutes apart."],
  ["Retention", "Recordings 90 days, transcripts 2 years. Driver cell numbers redacted in stored transcripts."],
];

const TOGGLEABLE = [
  { id: "ceiling", label: "Alfred may confirm at or below the rep's ceiling", on: true, note: "Turns a bid callback into a covering call. Without it Alfred can only screen and hand off." },
  { id: "record", label: "Record calls", on: true, note: "Two-party states get the full consent line, not just notice." },
  { id: "fmcsa", label: "Require the FMCSA callback before a first load", on: true, note: "The strongest single control against double-brokering." },
  { id: "dbcheck", label: "Ask the driver at pickup who dispatched them", on: true, note: "Catches re-brokering after the booking looked clean." },
  { id: "pingfirst", label: "Check tracking before calling", on: true, note: "Fresh ping means no check call at all." },
  { id: "inbound", label: "Answer the carrier line after hours", on: true, note: "6pm to 6am. Alfred resolves what it can from the file and queues the rest." },
];

const INTEGRATIONS = [
  { id: "powerbroker", name: "McLeod PowerBroker", kind: "TMS", state: "connected", keyMask: "••••1a7d", lastSync: "2 min ago", pulls: "Loads, ratecons, carrier file, customer accounts, agent structure", keyHint: "pb_live_…", where: "Utilities → API Access." },
  { id: "dat", name: "DAT", kind: "Load board", state: "connected", keyMask: "••••55c1", lastSync: "just now", pulls: "Posts, bids, carrier callbacks", keyHint: "dat_…", where: "DAT Power → account settings → API." },
  { id: "truckstop", name: "Truckstop", kind: "Load board", state: "off", pulls: "Posts, bids, carrier callbacks", keyHint: "ts_…", where: "Request an API credential." },
  { id: "highway", name: "Highway", kind: "Vetting", state: "connected", keyMask: "••••7f30", lastSync: "9 min ago", pulls: "Identity, record changes, VOIP and address clustering, OOS status", keyHint: "hw_…", where: "Highway → Settings → API keys." },
  { id: "rmis", name: "RMIS", kind: "Vetting", state: "off", pulls: "Packets, certificates, insurance monitoring", keyHint: "rmis_…", where: "Ask your RMIS rep." },
  { id: "macropoint", name: "MacroPoint", kind: "Visibility", state: "connected", keyMask: "••••90e3", lastSync: "6 min ago", pulls: "Location pings, so Alfred only calls when tracking is quiet", keyHint: "mp_…", where: "Carrier API credential." },
  { id: "sps", name: "SPS Commerce (EDI)", kind: "Customer", state: "connected", keyMask: "••••2c88", lastSync: "18 min ago", pulls: "EDI 214 milestones for customers who mandate them", keyHint: "sps_…", where: "SPS → integrations." },
];

/* -------------------------------- helpers ---------------------------------- */

// ⛔ THIS LIST MUST COVER WHAT THE DATABASE STORES, and it did not — the same defect the
// STATUS map above carries a notice about, fifty lines up, for the same reason.
// `BROKER_LOAD_STATUSES` is quoting | covering | tracking | delivered | cancelled. `cancelled`
// was missing and `tonu` is not a status this schema has, so a cancelled load counted as
// freight that still needs a truck: "Loads to cover", "Open loads", the per-customer count, the
// tracking-alert count, the carrier's "Loads on the board" and the Loads tab's own Active
// filter all included it. A load somebody cancelled appearing on a coverage board is how a rep
// spends a morning calling carriers for a load that no longer exists.
const CLOSED = ["delivered", "cancelled"];
// ⛔ `stops` IS jsonb WITH NO SHAPE CONSTRAINT, so [] and a stop with no city are both legal
// rows. These returned undefined and `laneLabel` then did `.city.split(",")` on it — the exact
// white screen the carrier board had, in the same helper. Found here by rendering the views
// against database rows BEFORE the fetch was wired, which is the whole point of doing it first.
const firstPickup = (l) => ((l && l.stops) || []).find((s) => s.type === "pickup") || ((l && l.stops) || [])[0] || null;
const lastDrop = (l) => {
  const ss = (l && l.stops) || [];
  return [...ss].reverse().find((s) => s.type === "drop") || ss[ss.length - 1] || null;
};
const cityOf = (s) => String((s && s.city) || "").split(",")[0].trim();
export const laneLabel = (l) => {
  const a = cityOf(firstPickup(l));
  const b = cityOf(lastDrop(l));
  const extra = ((l && l.stops) || []).length > 2 ? ` +${l.stops.length - 2}` : "";
  if (!a && !b) return "No stops yet";
  if (!b || a === b) return `${a || b}${extra}`;
  return `${a} → ${b}${extra}`;
};
// ⛔ MARGIN COMES FROM THE SERVER, IN CENTS, AND IS NULL UNLESS BOTH SIDES ARE KNOWN.
// This was `l.sell - l.buy` over the screen's own dollar numbers — right for the seeds, wrong
// for a real row: an uncovered load has no buy rate, and treating that as 0 reports the whole
// sell price as profit on exactly the loads nobody has covered yet. backend/src/lib.mjs already
// computes marginCents and already returns null when either side is missing, so defer to it.
export const marginOf = (l) => {
  if (!l) return null;
  // ⛔ THE SERVER'S ANSWER WINS, INCLUDING ITS null. lib.mjs returns marginCents null exactly
  // when one side is unknown, so a row that HAS the cents fields and a null margin means
  // "not known" — not "compute it yourself". The dollar fallback is only for the seed loads,
  // which carry no cents at all; without that distinction a null margin would be silently
  // recomputed and an uncovered load would report its whole sell price as profit.
  if ('marginCents' in l || 'sellCents' in l) return l.marginCents == null ? null : Math.round(l.marginCents / 100);
  // ⛔ `buy > 0`, NOT `buy != null`. Tightening the cents branch loosened this one: `buy: 0` is
  // "no carrier rate", and `sell - 0` reports the whole sell price as profit — the exact thing
  // the note above forbids, on the branch I was not looking at.
  return l.sell != null && l.buy != null && l.buy > 0 ? l.sell - l.buy : null;
};
const marginPct = (l) => {
  const m = marginOf(l);
  const sell = l && l.sellCents != null ? l.sellCents / 100 : (l && l.sell);
  return m === null || !sell ? null : Math.round((m / sell) * 100);
};

function lateRisk(load) {
  // `stops` is jsonb with no shape constraint — [] is a legal row, and the callers read
  // `r.stop.name` off whatever comes back.
  for (const s of (load && load.stops) || []) {
    if (s.state === "complete" || !s.eta || !s.apptEnd) continue;
    if (s.eta > s.apptEnd) return { stop: s, minutes: mins(s.eta - s.apptEnd) };
  }
  return null;
}
const openDetention = (l) => ((l && l.stops) || []).find((s) => s.state === "arrived" && s.arrivedAt);
function routeRep(load) {
  const p = byId(load.rep), s = byId(load.second);
  if (p && p.onHours) return { person: p, fallback: false };
  if (s) return { person: s, fallback: true, primary: p };
  return { person: p, fallback: false };
}

const scriptFor = (party, step, carrier, load, customer) =>
  party === "customer"
    ? [{ who: "alfred", text: `Hi, this is Alfred calling from Gulf Bridge about load ${load.ref}. I'm an AI assistant and this call is recorded.` },
       { who: "customer", text: "Go ahead." },
       { who: "alfred", text: step.detail },
       { who: "customer", text: "That works on our end." },
       { who: "alfred", text: "Thank you. I'll write it back to the order." }]
    : [{ who: "alfred", text: `Hi, this is Alfred with Gulf Bridge Logistics, an AI assistant, on load ${load.ref}. This call is recorded — is that alright?` },
       { who: "carrier", text: "Go ahead." },
       { who: "alfred", text: step.detail },
       { who: "carrier", text: "Alright." },
       ...(step.captures.length ? [{ who: "alfred", text: `I need ${step.captures.join(", ").toLowerCase()} — take your time, I'll read it back.` }, { who: "carrier", text: "One second… okay." }] : []),
       { who: "alfred", text: "Anything you need from us?" },
       { who: "carrier", text: "No, we're set." },
       { who: "alfred", text: "Thank you. I'll update the order and the customer." }];

/* ------------------------------- primitives -------------------------------- */

const Micro = ({ children, style }) => <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: 0.9, textTransform: "uppercase", color: T.ink3, ...style }}>{children}</div>;
const Tag = ({ children, solid }) => <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", border: `1px solid ${T.ink}`, background: solid ? T.ink : "transparent", color: solid ? "#fff" : T.ink, borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>{children}</span>;
const Soft = ({ children }) => <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", border: `1px solid ${T.hair}`, color: T.ink2, borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>{children}</span>;

function Chip({ status }) {
  const s = statusMeta(status);
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 500, padding: "3px 9px", borderRadius: 4, whiteSpace: "nowrap", background: s.fill ? T.ink : "transparent", color: s.fill ? "#fff" : s.muted ? T.ink3 : T.ink, border: `1px solid ${s.fill ? T.ink : s.muted ? T.hair : T.ink}` }}>{s.live && <span className="alf-dot" />}{s.label}</span>;
}
function Button({ children, onClick, kind = "primary", disabled, size = "md", title }) {
  const k = { primary: { background: T.ink, color: "#fff", border: `1px solid ${T.ink}` }, quiet: { background: "#fff", color: T.ink, border: `1px solid ${T.hair}` } }[kind];
  return <button onClick={onClick} disabled={disabled} title={title} className="alf-btn" style={{ ...k, font: "inherit", fontSize: size === "sm" ? 12.5 : 13.5, fontWeight: 500, letterSpacing: -0.15, padding: size === "sm" ? "6px 11px" : "8px 15px", borderRadius: 7, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.35 : 1 }}>{children}</button>;
}
function Toggle({ on, onChange }) {
  return <button onClick={() => onChange(!on)} aria-pressed={on} style={{ width: 42, height: 24, borderRadius: 999, padding: 2, cursor: "pointer", flexShrink: 0, background: on ? T.ink : "#fff", border: `1px solid ${on ? T.ink : T.hair}`, display: "flex", justifyContent: on ? "flex-end" : "flex-start", transition: "background .18s" }}><span style={{ width: 18, height: 18, borderRadius: 999, background: on ? "#fff" : T.ink4, transition: "all .18s" }} /></button>;
}
function Disclosure({ title, meta, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: `1px solid ${T.hairSoft}` }}>
      <button onClick={() => setOpen((o) => !o)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", padding: "14px 20px", cursor: "pointer", textAlign: "left", font: "inherit" }}>
        <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{title}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {meta && <span style={{ fontSize: 12.5, color: T.ink3 }}>{meta}</span>}
          <span style={{ color: T.ink3, fontSize: 13, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
        </span>
      </button>
      {open && <div style={{ padding: "0 20px 18px" }}>{children}</div>}
    </div>
  );
}
const Field = ({ label, value, isMono }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "6px 0" }}>
    <span style={{ fontSize: 13, color: T.ink3 }}>{label}</span>
    <span style={{ fontSize: 13, textAlign: "right", fontFamily: isMono ? mono : sans, fontVariantNumeric: "tabular-nums" }}>{value}</span>
  </div>
);
const Waveform = ({ active, invert }) => (
  <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 2.5, height: 14 }}>
    {[0, 1, 2, 3, 4].map((i) => <span key={i} className={active ? "alf-bar" : ""} style={{ width: 2, borderRadius: 1, background: invert ? "#fff" : T.ink, height: active ? 14 : 4, animationDelay: `${i * 0.11}s`, opacity: active ? 1 : 0.3 }} />)}
  </span>
);
const Panel = ({ children }) => <div style={{ background: T.surface, border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>{children}</div>;
const PanelHead = ({ title, note, right }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "16px 20px 14px", flexWrap: "wrap" }}>
    <div style={{ flex: 1, minWidth: 180 }}>
      <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.35 }}>{title}</div>
      {note && <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 3, lineHeight: 1.45 }}>{note}</div>}
    </div>
    {right}
  </div>
);
const Segmented = ({ value, onChange, options, disabled = false }) => (
  <div style={{ display: "flex", border: `1px solid ${T.hair}`, borderRadius: 7, overflow: "hidden", flexWrap: "wrap", opacity: disabled ? 0.5 : 1 }}>
    {/* ⛔ AND IT DOES NOT FIRE FOR THE VALUE ALREADY SELECTED. Clicking the active segment
        posted a write that changed nothing and re-stamped the stored reason, author and date —
        so the original block decision could be overwritten by a click with no effect. */}
    {options.map((o) => <button key={o.v} disabled={disabled || value === o.v} onClick={() => { if (!disabled && value !== o.v) onChange(o.v); }} style={{ font: "inherit", fontSize: 12.5, fontWeight: 500, padding: "6px 11px", border: "none", cursor: disabled ? "default" : "pointer", background: value === o.v ? T.ink : "#fff", color: value === o.v ? "#fff" : T.ink2 }}>{o.label}</button>)}
  </div>
);
const selectStyle = { font: "inherit", fontSize: 13, background: "#fff", color: T.ink, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "6px 10px", outline: "none", appearance: "none", cursor: "pointer" };
const Metric = ({ label, value, sub, emphasis }) => (
  <div style={{ flex: "1 1 165px", padding: "18px 20px", borderRight: `1px solid ${T.hairSoft}` }}>
    <Micro>{label}</Micro>
    <div style={{ fontFamily: mono, fontVariantNumeric: "tabular-nums", fontSize: 32, letterSpacing: -1.5, marginTop: 8, lineHeight: 1, fontWeight: emphasis ? 600 : 400 }}>{value}</div>
    {sub && <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 7 }}>{sub}</div>}
  </div>
);
// An absent measurement draws an EMPTY bar, not a full one and not a zero-width lie about zero.
const Bar = ({ pct }) => <span style={{ display: "flex", width: "100%", height: 6, borderRadius: 3, overflow: "hidden", background: T.hairSoft }}><span style={{ width: `${pct == null || Number.isNaN(Number(pct)) ? 0 : Math.round(Number(pct) * 100)}%`, background: T.ink }} /></span>;
// ⛔ AN UNRECORDED CHECK MUST NOT RENDER AS A TICK, AND THIS ONE DID.
//
// The fallback was `map[state] || map.pass` — so any state outside the three it knew, INCLUDING
// a check nobody has run, drew a ✓. That is the worst possible default on this screen: a broker
// reads a row of ticks to decide whether a stranger claiming to be a carrier is real, and
// double-brokering is the loss this product exists to prevent. There is now a fourth state for
// "not checked", it looks nothing like a pass, and the fallback IS that state.
function CheckMark({ state }) {
  const map = {
    pass: ["✓", T.ink, "#fff"],
    warn: ["!", "#fff", T.ink4],
    flag: ["✕", "#fff", T.ink],
    none: ["?", T.ink3, "#fff"],
  };
  const [ch, fg, bg] = map[state] || map.none;
  const unchecked = !map[state] || state === "none";
  // ⛔ LONGHANDS, NOT A SHORTHAND PLUS AN OVERRIDE. This was `border: '1px dashed …'` followed by
  // `borderStyle`, which only works because object keys keep their insertion order — perturb that
  // and every PASS tick renders dashed, i.e. every verified check starts reading as "not checked"
  // on the screen where that distinction is the whole product. It also silently turned the six
  // `warn` marks' hairline grey border solid black; `warn` keeps its own colour now.
  const borderColor = unchecked ? T.ink4 : state === "warn" ? T.hair : T.ink;
  return <span style={{ width: 15, height: 15, borderRadius: 3, background: state === "pass" || unchecked ? "#fff" : bg, borderWidth: 1, borderStyle: unchecked ? "dashed" : "solid", borderColor, color: state === "pass" ? T.ink : fg, fontSize: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{ch}</span>;
}
// ⛔ `checks` IS NOT IN THE DATABASE. The seeds carried an eight-item checklist of pass/warn/flag
// per carrier; the row holds a `vetting` jsonb with whatever was actually recorded. The adapter
// derives the list (see brokerCarrierFromApi) and marks everything unrecorded as "none" — this
// mapped `carrier.checks` straight off the object and threw on every real row.
const VetChecks = ({ carrier }) => {
  const checks = Array.isArray(carrier.checks) ? carrier.checks : [];
  if (!checks.length) {
    return (
      <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>
        Nothing has been checked on this carrier yet. The status below is somebody&rsquo;s decision,
        not a verification.
      </div>
    );
  }
  const done = checks.filter(([, v]) => v !== "none").length;
  // ⛔ EVERY RECORDED CHECK TODAY IS A PERSON SAYING SO. Nothing in this platform verifies any
  // of the eight — /api/fmcsa/lookup returns a result to the client and writes nothing — so an
  // entry's `source` is 'operator' for all of them. Drawing a broker's own tick the same as a
  // platform check is the defect this whole column exists to prevent, so the footer names it.
  const attested = checks.filter(([, v, src]) => v !== "none" && src === "operator").length;
  const verified = checks.filter(([, , src]) => src === "platform").length;
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px" }}>
        {checks.map(([k, v, src]) => (
          <span key={k} title={v === "none" ? "Nobody has run this check" : src === "platform" ? "Checked by the platform" : "Recorded by a person — not a verification"}
            style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: v === "pass" ? T.ink2 : v === "none" ? T.ink3 : T.ink, minWidth: 150 }}>
            <CheckMark state={v} />{k}
            {/* A person's claim is marked, so a tick alone never reads as a verification. */}
            {v !== "none" && src !== "platform" && (
              <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: 0.5, color: T.ink3, border: `1px solid ${T.hair}`, borderRadius: 3, padding: "0 3px" }}>said</span>
            )}
          </span>
        ))}
      </div>
      {/*
        ⛔ THE EXPLANATION MUST NOT VANISH WHEN BOTH KINDS COEXIST. The condition was
        `attested > 0 && verified === 0`, so on a row with one platform check and two operator
        claims the footer disappeared while the two "said" chips stayed on screen with nothing
        explaining them — the explanation went missing exactly when the distinction first starts
        to matter. Unreachable today (nothing writes a platform check) and live the moment one
        lands. Found by review.
      */}
      {(done < checks.length || attested > 0) && (
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 8, lineHeight: 1.5 }}>
          {done < checks.length && <>{done} of {checks.length} checked. A dashed ? is a check nobody has run — not a pass. </>}
          {attested > 0 && (verified === 0
            ? <>Every recorded check here was <b>entered by a person</b>, not verified by the platform —
              nothing in Alfred checks these yet. Marked “said”.</>
            : <>{attested} of these {attested === 1 ? "was" : "were"} <b>entered by a person</b> rather than
              verified by the platform — the ones marked “said”.</>)}
        </div>
      )}
    </div>
  );
};

// ⛔ NO FREE-TIME TERM MEANS NO CLOCK, NOT A CLOCK AGAINST ZERO.
//
// `freeMins` comes from the customer's contract and the database does not store one. With
// `freeMins = null`, `m >= null` is `m >= 0` — so this asserted BILLABLE detention from the
// moment a truck arrived, against a contract nobody had entered, and then threw on
// `customer.accessorial.toLowerCase()`. Making `custById` return a stub instead of null turned
// a crash into a confident false claim, which is the worse of the two.
//
// Unreachable today — it needs `stop.state === "arrived"` and an `arrivedAt`, which no adapter
// stop carries — but it is the one place the stub made things worse rather than better.
export function DetentionClock({ stop, freeMins, customer }) {
  const [, tick] = useState(0);
  useEffect(() => { const id = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(id); }, []);
  const arrived = stop && stop.arrivedAt;
  if (!arrived) return null;
  const m = mins(Date.now() - arrived);
  // ⛔ `>= 0`, NOT `> 0`. A recorded 0 means detention is billable from minute ONE — the
  // strictest account on the file — and `> 0` rendered it as "no free-time term is on file,
  // so nothing here is billable yet", which is the opposite, in the direction that costs the
  // brokerage money. The `> 0` was there to guard null (`m >= null` is `m >= 0`); an integer
  // check does that without erasing the answer. The intake form promises "0 is a real answer".
  const known = Number.isInteger(freeMins) && freeMins >= 0;
  const over = known && m >= freeMins;
  const who = (customer && customer.name) || "this customer";
  return (
    <div style={{ border: `1px solid ${over ? T.ink : T.hair}`, borderRadius: 8, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
        {/* ⛔ THE TAG NAMES THE BILLABLE MINUTES; THE HEADLINE IS LABELLED "on site".
            A solid `billable` tag beside a headline that is TOTAL time on site reads as a money
            figure it is not: measured on the seeded load, "billable · 2h 34m … Free time is 90
            minutes … 64 minutes past" — so is the invoice 154 minutes or 64? The body text was
            always right; the header contradicted it. This panel needs `state: "arrived"` and an
            `arrivedAt`, which no adapter stop carried until the demo brokerage seed, so it goes
            on screen for the first time with the narration pointing at it. */}
        <Micro>Detention clock</Micro>{over && <Tag solid>{m - freeMins}m billable</Tag>}
        <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 18, fontVariantNumeric: "tabular-nums" }}>{Math.floor(m / 60)}h {String(m % 60).padStart(2, "0")}m{" "}
          <span style={{ fontFamily: sans, fontSize: 11.5, color: T.ink3, fontWeight: 400 }}>on site</span></span>
      </div>
      <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 7, lineHeight: 1.5 }}>
        On site at {(stop && stop.name) || "the stop"}.{" "}
        {!known
          ? `No free-time term is on file for ${who}, so nothing here is billable yet — check the contract before you invoice detention.`
          : `Free time is ${freeMins} minutes under ${who}'s contract${freeMins !== 120 ? ", not the default two hours" : ""}. ${over ? `${m - freeMins} minutes past${customer && customer.accessorial ? ` — ${customer.accessorial.toLowerCase()}` : ""}.` : "Alfred flags accounting when it passes."}`}
      </div>
    </div>
  );
}

/* ------------------------------- recording --------------------------------- */

const barsFor = (seed, n = 44) => { const out = []; let x = seed * 9301; for (let i = 0; i < n; i++) { x = (x * 9301 + 49297) % 233280; out.push(0.25 + (x / 233280) * 0.75); } return out; };

/**
 * ⛔ A WORKING PLAYER FOR A RECORDING THAT DOES NOT EXIST.
 *
 * Play button, a 44-bar waveform seeded off the row, and a counter that advances every 90ms.
 * There is no audio: nothing on this board places a call, and nothing fetches a recording. A
 * prospect clicks play and watches a call recording play back, which is more convincing than
 * any sentence on the screen — so it says what it is, next to the control.
 */
export function Recording({ seed, dur }) {
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const shape = useMemo(() => barsFor(seed), [seed]);
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setPos((p) => p + 1), 90);
    return () => clearInterval(id);
  }, [playing]);
  useEffect(() => { if (pos >= dur) { setPlaying(false); setPos(0); } }, [pos, dur]);
  const pct = Math.min(pos / dur, 1);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: `1px solid ${T.hair}`, borderRadius: 8 }}>
      <button onClick={() => setPlaying((p) => !p)} aria-label={playing ? "Pause" : "Play"} style={{ width: 30, height: 30, borderRadius: 999, background: T.ink, color: "#fff", border: "none", cursor: "pointer", fontSize: 11, flexShrink: 0 }}>{playing ? "❚❚" : "▶"}</button>
      <span style={{ flex: 1, display: "flex", alignItems: "center", gap: 1.5, height: 26 }}>
        {shape.map((h, i) => <span key={i} style={{ flex: 1, height: `${h * 100}%`, borderRadius: 1, background: i / shape.length <= pct ? T.ink : "#D8D8D8" }} />)}
      </span>
      <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink2, minWidth: 74, textAlign: "right" }}>{durLabel(Math.min(pos, dur))} / {durLabel(dur)}</span>
      </div>
      {/* ⛔ NEXT TO THE CONTROL, not in a banner that scrolls away. The player works; the
          audio does not exist. */}
      <div style={{ fontSize: 12, color: T.ink2, marginTop: 6, lineHeight: 1.4 }}>
        {SIMULATED} There is no audio — this is what the player will look like.
      </div>
    </div>
  );
}

/**
 * ⛔ "NO CARRIER ROW" IS NOT "THIS WAS A CALL WITH THE CUSTOMER".
 *
 * This read `carrier ? carrier.name : "Customer"`. The call log is seeded with carrier ids
 * that no longer resolve, so every outbound carrier verification call was relabelled as a
 * call with the shipper — while the tag two lines above it still said `carrier`. A row that
 * contradicts itself about who was on the phone is worse than one that admits it does not
 * know, and on this screen the party is the whole point of the row.
 */
const partyLabel = (c, carrier, known = true) => {
  if (carrier) return carrier.name;
  // ⛔ AND IT DOES NOT ACCUSE THE FILE OF A GAP IT HAS NOT READ. Before the fetch lands, and
  // after it fails, every row claimed the broker's file was missing a carrier.
  if (c.carrierId) return known ? "Carrier not in your file" : "Carrier — file not loaded";
  return "Customer";
};

function CallRow({ c, carriers, carriersKnown = true, open, onToggle, showParty = true }) {
  const carrier = c.carrierId ? carriers.find((x) => String(x.id) === String(c.carrierId)) : null;
  return (
    <div style={{ borderTop: `1px solid ${T.hairSoft}` }}>
      <button onClick={onToggle} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", background: "none", border: "none", cursor: "pointer", textAlign: "left", font: "inherit" }}>
        <span style={{ fontFamily: mono, fontSize: 12, color: T.ink3, minWidth: 62 }}>{c.at}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{c.step}</span>
            <Soft>{c.dir === "in" ? "inbound" : c.party}</Soft>
            {c.outcome !== "Completed" && <Tag solid>{c.outcome}</Tag>}
          </span>
          <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3 }}>{c.loadRef}{showParty ? ` \u00b7 ${partyLabel(c, carrier, carriersKnown)}` : ""}</span>
        </span>
        <span style={{ fontFamily: mono, fontSize: 12, color: T.ink3 }}>{durLabel(c.dur)}</span>
        <span style={{ color: T.ink4, fontSize: 13, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
      </button>
      {open && (
        <div className="alf-fade" style={{ padding: "0 20px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          <Recording seed={c.id.length + c.dur} dur={c.dur} />
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {c.captured.map((x, i) => <span key={i} style={{ fontSize: 12, border: `1px solid ${T.hair}`, borderRadius: 4, padding: "3px 8px", color: T.ink2 }}>{x}</span>)}
          </div>
          <div>
            <Micro style={{ marginBottom: 8 }}>Transcript · driver cells redacted</Micro>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {c.transcript.map((l, i) => (
                <div key={i} style={{ display: "flex", gap: 10 }}>
                  <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", color: l.who === "alfred" ? T.ink : T.ink4, minWidth: 52, paddingTop: 3 }}>{l.who}</span>
                  <span style={{ fontSize: 13.5, lineHeight: 1.45, color: l.who === "alfred" ? T.ink : T.ink2 }}>{l.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// `loads` is not read here — the call log's rows carry their own `loadRef` text.
export function History({ carriers, carriersKnown = true, audit }) {
  const [tab, setTab] = useState("calls");
  const [day, setDay] = useState("today");
  const [dir, setDir] = useState("all");
  const [who, setWho] = useState("all");
  const [ref, setRef] = useState("all");
  const [outcome, setOutcome] = useState("all");
  const [text, setText] = useState("");
  const [openId, setOpenId] = useState(null);

  const rows = CALL_LOG.filter((c) =>
    (day === "all" || c.day === day) && (dir === "all" || c.dir === dir) &&
    (who === "all" || String(c.carrierId) === String(who)) && (ref === "all" || c.loadRef === ref) &&
    (outcome === "all" || (outcome === "clean" ? c.outcome === "Completed" : c.outcome !== "Completed")) &&
    (!text.trim() || (c.step + " " + c.captured.join(" ") + " " + c.transcript.map((t) => t.text).join(" ")).toLowerCase().includes(text.toLowerCase())));
  const refs = Array.from(new Set(CALL_LOG.map((c) => c.loadRef)));

  /**
   * ⛔ THE ONE PLACE THE DATA LEAVES THE SCREEN, SO THE MARKER HAS TO TRAVEL WITH IT.
   *
   * Everything in these two tabs is a written example — the call log is an array in this file
   * and the audit list lives in the browser. A colleague who is forwarded the file never saw
   * the banner, the panel note or the "scripted example" line, so the file says it itself, in
   * a first column that cannot be mistaken for data.
   */
  function exportCsv(kind) {
    const rowsOut = kind === "calls"
      ? [["note", "time", "direction", "load", "party", "step", "seconds", "outcome", "captured"],
         ...rows.map((c) => [SIMULATED, c.at, c.dir, c.loadRef, c.party, c.step, c.dur, c.outcome, c.captured.join("; ")])]
      : [["note", "time", "actor", "action", "ref"],
         ...audit.map((a) => [SIMULATED, a.at, a.actor, a.what, a.ref || ""])];
    const csv = rowsOut.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `alfred-${kind}-scripted-example.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <Segmented value={tab} onChange={setTab} options={[{ v: "calls", label: "Calls" }, { v: "audit", label: `Audit trail (${audit.length})` }]} />
        <Button size="sm" kind="quiet" onClick={() => exportCsv(tab)}>Export CSV</Button>
      </div>

      {tab === "calls" ? (
        <Panel>
          {/* ⛔ THIS FRAMED SEVEN WRITTEN TRANSCRIPTS AS THE CALL ARCHIVE. There is no call
              archive: nothing on this board dials, and CALL_LOG is an array in this file. */}
          <PanelHead title="Call history" note={`Written examples of what Alfred captures on a call, with the transcript and the fields it read back. ${SIMULATED} Nothing here is a recording of anything.`} />
          <div style={{ display: "flex", gap: 8, padding: "0 20px 14px", flexWrap: "wrap" }}>
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search transcripts"
              style={{ flex: 1, minWidth: 190, font: "inherit", fontSize: 13, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "7px 10px", outline: "none" }} />
            <select value={who} onChange={(e) => setWho(e.target.value)} style={selectStyle}>
              <option value="all">All carriers</option>
              {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={ref} onChange={(e) => setRef(e.target.value)} style={selectStyle}>
              <option value="all">All loads</option>
              {refs.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)} style={selectStyle}>
              <option value="all">Any outcome</option><option value="clean">Completed</option><option value="exception">Exceptions only</option>
            </select>
            <Segmented value={dir} onChange={setDir} options={[{ v: "all", label: "All" }, { v: "out", label: "Out" }, { v: "in", label: "In" }]} />
            <Segmented value={day} onChange={setDay} options={[{ v: "today", label: "Today" }, { v: "yesterday", label: "Yesterday" }, { v: "all", label: "Any day" }]} />
          </div>
          {rows.length === 0 ? <Empty>No calls match those filters. Clear the search or widen the day range.</Empty>
            : rows.map((c) => <CallRow key={c.id} c={c} carriers={carriers} carriersKnown={carriersKnown} open={openId === c.id} onToggle={() => setOpenId(openId === c.id ? null : c.id)} />)}
        </Panel>
      ) : (
        <Panel>
          <PanelHead title="Audit trail" note={`Only human actions land here. Anything Alfred did is in the call log. ⚠️ This list lives in the browser and is gone on refresh — it is not a kept record, and the calls it refers to are scripted examples.`} />
          {audit.length === 0 ? <Empty>Nothing yet this session. Take a handoff, change a ceiling, or move a carrier's status and it lands here with your name on it.</Empty>
            : audit.map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "12px 20px", borderTop: `1px solid ${T.hairSoft}`, flexWrap: "wrap" }}>
              <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3, minWidth: 62 }}>{a.at}</span>
              <Soft>human</Soft>
              <span style={{ fontSize: 13, fontWeight: 500, minWidth: 130 }}>{a.actor}</span>
              <span style={{ fontSize: 13, color: T.ink2, flex: 1, minWidth: 200 }}>{a.what}</span>
              {a.ref && <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{a.ref}</span>}
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}

/* -------------------------------- overview --------------------------------- */

function LadderStrip({ waiting, load, jumped }) {
  const rungs = ladderFor(load, CFG);
  const now = jumped ? rungs[rungs.length - 1] : currentRung(load, CFG, waiting);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 11, paddingTop: 11, borderTop: `1px dashed ${T.hair}`, flexWrap: "wrap" }}>
      <Micro style={{ marginRight: 2 }}>{jumped ? "Ladder skipped" : "Escalation"}</Micro>
      {rungs.map((r, i) => {
        const active = now && r.person && now.person && r.person.id === now.person.id;
        return (
          <React.Fragment key={i}>
            {i > 0 && <span style={{ width: 12, height: 1, background: T.hair }} />}
            <span style={{ fontSize: 11.5, padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap", textDecoration: r.skipped ? "line-through" : "none",
              border: `1px solid ${active ? T.ink : T.hair}`, background: active ? T.ink : "#fff", color: active ? "#fff" : r.skipped ? T.ink4 : T.ink3 }}>
              {r.person ? r.person.name : "unset"}{r.at > 0 && !jumped ? ` +${r.at}m` : ""}
            </span>
          </React.Fragment>
        );
      })}
      {rungs.some((r) => r.skipped) && <span style={{ fontSize: 11.5, color: T.ink3 }}>out of office, skipped</span>}
    </div>
  );
}

export function QueueItem({ q, loads, carriers, carriersKnown = true, seat, onOpenLoad, onResolve, onClaim, onRelease }) {
  // ⛔ A SCRIPTED HANDOFF SAYS SO, ON THE ROW. Not in a panel note above it — a reader who scrolls
  // to one row needs it on that row, which is the whole lesson of the partial marker.
  const scriptedRow = !!q.seeded;
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const load = loads.find((l) => l.ref === q.loadRef);
  // ⛔ A BUTTON THAT DOES NOTHING IS WORSE THAN NO BUTTON. `onOpenLoad` is `openLoadByRef`,
  // which is `if (l) { … }` — so on a handoff naming a load that is not on the board, all three
  // controls below were silent no-ops, and a silent no-op is an invitation to click again. The
  // handoff queue is still seeded while the loads are real, so this is the common case, not an
  // edge: these rows name refs no board has. See `loadsKnown` for the other half — a load we
  // could not READ is not a load that is not there.
  const openable = !!load;
  const carrier = q.carrierId ? carriers.find((c) => String(c.id) === String(q.carrierId)) : null;
  const need = NEEDS[q.kind];
  const me = byId(seat);
  const allowed = me && me.can.includes(need);
  const mine = q.assignedTo === seat;
  const takenBy = q.assignedTo && q.assignedTo !== seat ? byId(q.assignedTo) : null;
  const urgent = q.kind === "fraud" || q.kind === "claim" || q.waiting > 30;
  const lastRung = CFG.ladder.length ? CFG.ladder[CFG.ladder.length - 1].at : 0;
  const overdue = !q.assignedTo && q.waiting > lastRung;
  const nobody = !canApprove(CFG, need);
  const outcomes = RESOLVE_OUTCOMES[q.kind] || ["Handled"];

  return (
    <div style={{ display: "flex", gap: 14, padding: "16px 20px", borderTop: `1px solid ${T.hairSoft}`, position: "relative", opacity: takenBy ? 0.7 : 1 }}>
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: urgent ? T.ink : T.hair }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
          <Tag solid={urgent}>{KIND[q.kind]}</Tag>
          {scriptedRow && <Soft>scripted example</Soft>}
          {q.kind === "money" && <Soft>{MONEY_KIND[q.money]}</Soft>}
          {q.amount && <span style={{ fontFamily: mono, fontSize: 12.5 }}>{q.amount}</span>}
          {openable
            ? <button onClick={() => onOpenLoad(q.loadRef)} style={{ font: "inherit", fontFamily: mono, fontSize: 12, color: T.ink2, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}>{q.loadRef}</button>
            : <span style={{ fontFamily: mono, fontSize: 12, color: T.ink3 }} title="This load is not on your board.">{q.loadRef}</span>}
          {carrier && <span style={{ fontSize: 12.5, color: T.ink3 }}>{carrier.name}</span>}
          <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 12, color: urgent ? T.ink : T.ink3 }}>waiting {waitLabel(q.waiting)}</span>
        </div>
        <div style={{ fontSize: 15, fontWeight: 500, letterSpacing: -0.25, marginTop: 9 }}>{q.title}</div>
        <div style={{ borderLeft: `2px solid ${T.hair}`, paddingLeft: 12, marginTop: 10 }}>
          <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{q.quote}</div>
          <Micro style={{ marginTop: 5 }}>On the call</Micro>
        </div>
        {q.tried && <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 9, lineHeight: 1.5 }}><Soft>already tried</Soft> <span style={{ marginLeft: 6 }}>{q.tried}</span></div>}
        {q.intake && (
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 8, padding: "12px 14px", marginTop: 12 }}>
            <Micro style={{ marginBottom: 8 }}>What Alfred got before it stopped</Micro>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 26px" }}>
              {q.intake.map(([k, v]) => <span key={k} style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 160 }}><span style={{ fontSize: 11.5, color: T.ink3 }}>{k}</span><span style={{ fontSize: 13 }}>{v}</span></span>)}
            </div>
          </div>
        )}
        <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5, marginTop: 10 }}>{q.ask}</div>
        {load ? <LadderStrip waiting={q.waiting} load={load} jumped={q.jumped} />
          : <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 9, lineHeight: 1.5 }}>
            {/* ⛔ DO NOT BLAME THE BOARD FOR A LOAD THAT NEVER EXISTED. The first version said
                "<ref> is not on your board", which reads as a real handoff about a load the
                board has lost — and that is a more convincing lie than the dead button it
                replaced. For a scripted row the honest fact is that the load is invented. */}
            {scriptedRow
              ? `${SIMULATED} ${q.loadRef} is not a real load, so there is no call plan or escalation ladder behind this.`
              : `${q.loadRef} is not on your board, so this handoff cannot show who is next on it or open it.`}
          </div>}

        {overdue && !nobody && (
          <div style={{ fontSize: 12.5, color: T.ink, marginTop: 9, lineHeight: 1.5 }}>
            Past the last rung at {lastRung} minutes. Alfred has stopped climbing — nobody else is going to be called about this one.
          </div>
        )}
        {nobody && (
          <div style={{ fontSize: 12.5, color: T.ink, marginTop: 9, lineHeight: 1.5, border: `1px solid ${T.ink}`, borderRadius: 8, padding: "10px 12px" }}>
            Nobody on the roster can approve {need} right now — the only person who could is out of office. This will sit here until someone with that permission is back.
          </div>
        )}

        {takenBy ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 13, flexWrap: "wrap" }}>
            <Soft>locked</Soft>
            <span style={{ fontSize: 12.5, color: T.ink2 }}>{takenBy.name} took this at {q.takenAt}.</span>
            {me && me.role === "Branch manager" && <Button size="sm" kind="quiet" onClick={() => onRelease(q.id)}>Release it</Button>}
          </div>
        ) : mine ? (
          <div style={{ marginTop: 13, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <select value={outcome} onChange={(e) => setOutcome(e.target.value)} style={{ ...selectStyle, flex: 1, minWidth: 240 }}>
                <option value="">What happened?</option>
                {outcomes.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <Button size="sm" disabled={!outcome} onClick={() => onResolve(q.id, outcome, note)}>Close it out</Button>
            </div>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything else worth recording (optional)"
              style={{ font: "inherit", fontSize: 12.5, border: `1px solid ${T.hair}`, borderRadius: 6, padding: "7px 10px", outline: "none" }} />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button size="sm" kind="quiet" onClick={() => onRelease(q.id)}>Put it back</Button>
              {openable && <Button size="sm" kind="quiet" onClick={() => onOpenLoad(q.loadRef)}>Open load</Button>}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, marginTop: 13, alignItems: "center", flexWrap: "wrap" }}>
            <Button size="sm" disabled={!allowed} onClick={() => allowed && onClaim(q.id)}>Take it</Button>
            {openable && <Button size="sm" kind="quiet" onClick={() => onOpenLoad(q.loadRef)}>Open load</Button>}
            {!allowed && <span style={{ fontSize: 12, color: T.ink3 }}>Only {need} can clear this one.</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityStrip({ loads, loadsKnown = true, queue, call, seat }) {
  const me = byId(seat);
  const inflight = call ? 1 : 0;
  const aging = queue.filter((q) => !q.assignedTo && q.waiting > 30).length;
  // ⛔ NOT A COUNT OF ANYTHING MEASURED. `trackingOk` is hard-set false for every row by the
  // adapter — nothing in this stack talks to a visibility provider — so this counted every
  // covered load as a tracking alert and put the figure beside real ones. An em dash until a
  // provider is connected; the adapter's own note says a dispatcher reading a fresh ping would
  // be reading a developer's placeholder, and a fresh ALARM is the same lie pointing the other
  // way. `stale` stays for the day a load carries a real ping.
  // ⛔ GATED ON A MEASURED PING, NOT ON A PROVIDER BEING NAMED. The first version tested
  // `l.tracking !== null`, which is the shape the adapter writes TODAY for an unconnected
  // provider — so the day somebody stores a provider name without a ping, the figure would flip
  // straight back to "every covered load is an alert", which is the fabricated alarm this
  // removed. `lastPing` is the thing that was actually observed.
  const tracked = loads.filter((l) => l.lastPing != null && !CLOSED.includes(l.status) && sees(me, l.branch));
  const stale = tracked.filter((l) => l.carrierId && !l.trackingOk).length;
  const locked = queue.filter((q) => q.assignedTo).length;
  const m = mode();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 18, padding: "11px 20px", background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 10, flexWrap: "wrap" }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {inflight ? <Waveform active /> : <Waveform active={false} />}
        <span style={{ fontSize: 12.5, color: T.ink2 }}>{inflight ? "1 scripted call" : CFG.live ? "No calls in flight" : "Alfred is dark"}</span>
      </span>
      <span style={{ fontSize: 12.5, color: T.ink2 }}><span style={{ fontFamily: mono, fontWeight: 600 }}>{aging}</span> handoffs aging</span>
      <span style={{ fontSize: 12.5, color: T.ink2 }}><span style={{ fontFamily: mono, fontWeight: 600 }}>{locked}</span> taken by someone</span>
      {/* An em dash, not 0: "no tracking alerts" is the reassuring direction and it must be
          earned — by a read that answered AND by a load that carries a real ping. */}
      <span style={{ fontSize: 12.5, color: T.ink2 }}><span style={{ fontFamily: mono, fontWeight: 600 }}>{loadsKnown && tracked.length ? stale : "\u2014"}</span> tracking alerts</span>
      <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
        <Soft>{m.label}</Soft>
        {!m.canWrite && <span style={{ fontSize: 12, color: T.ink3 }}>writes disabled</span>}
      </span>
    </div>
  );
}

function Empty({ children }) {
  return <div style={{ padding: "26px 20px 30px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13, color: T.ink2, lineHeight: 1.6, maxWidth: 520 }}>{children}</div>;
}

export function Overview({ queue, loads, carriers, carriersKnown = true, loadsKnown = true, loadsLoading = false, seat, call, onOpenLoad, onResolve, onClaim, onRelease, onOpenCarrier }) {
  const [filter, setFilter] = useState("all");
  const me = byId(seat);
  const visible = loads.filter((l) => sees(me, l.branch));
  const scoped = visible.filter((l) => inScope(l, CFG, CFG.scope.myLoadsOnly ? seat : null).ok);
  const outside = visible.filter((l) => !inScope(l, CFG, CFG.scope.myLoadsOnly ? seat : null).ok);
  // ⛔ A HANDOFF ROW ABOUT A CARRIER NOBODY CAN NAME. SEED_QUEUE's first item is a complete
  // fabricated hijack dossier — a VOIP bid number, an FMCSA record number, "Callback: no
  // answer, twice", "Address shared with 3 other MCs" — attributed to "Northline Freight",
  // which was one of the deleted seed carriers. It is the same invention the note above
  // SEED_CARRIERS condemns, and now orphaned: a broker who tries to act on it finds no such
  // carrier in their file. Same rule as the risk panel and the bid list — a row that names a
  // carrier only renders if that carrier resolves. A row with no carrierId makes no claim
  // about anybody and stays.
  const byCarrier = splitByCarrier(filter === "all" ? queue : queue.filter((q) => q.kind === filter), carriers, carriersKnown);
  const queueHidden = hiddenNote(byCarrier, byCarrier.hidden === 1 ? "handoff" : "handoffs");
  const shown = byCarrier.shown
    .filter((q) => { const l = loads.find((x) => x.ref === q.loadRef); return !l || sees(me, l.branch); });
  const active = scoped.filter((l) => !CLOSED.includes(l.status));
  const uncovered = active.filter((l) => !l.carrierId);
  const late = active.map((l) => ({ l, r: lateRisk(l) })).filter((x) => x.r);
  // ⛔ FILTERED THE SAME WAY THE ROWS ARE. This counter fed "N open" above a panel whose rows
  // are filtered to carriers that resolve — so with real data it read "2 open" over an empty
  // list. Filtering a list and leaving its count is how a screen disagrees with itself.
  const openRisk = RISK.filter((r) => !r.handled && carriers.some((x) => String(x.id) === String(r.carrierId)));
  const night = CFG.scope.inbound ? INBOUND.filter((i) => i.night) : [];
  const resolvedNight = night.filter((i) => i.outcome === "resolved").length;
  const callsToday = HOURLY.reduce((a, b) => a + b, 0);
  const maxHour = Math.max(...HOURLY);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <ActivityStrip loads={loads} loadsKnown={loadsKnown} queue={queue} call={call} seat={seat} />

      <Panel>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {/* ⛔ NOT ZERO FOR A READ THAT FAILED. This is the first number on the board and it
              said "Loads to cover 0 · 0 in scope" whenever the loads read errored or had not
              answered — a rep reads that as a quiet morning. Same rule as "Carriers on file"
              on the Carriers tab. */}
          <Metric label="Loads to cover" value={loadsKnown ? String(uncovered.length) : "\u2014"}
            sub={loadsKnown ? `${active.length} in scope${outside.length ? ` · ${outside.length} outside` : ""}`
              : boardStateNote({ loading: loadsLoading, failed: !loadsLoading })} />
          <Metric label="Avg. time to cover" value={"\u2014"} sub="Not measured — nothing here times a coverage cycle" />
          <Metric label="Answered after hours" value={"\u2014"} sub="Nothing on this board answers a line" />
          {/* ⛔ NOT "Alfred handed these off" — nothing in this app produces a handoff, and on a
              real board the survivors of `splitByCarrier` are all scripted. Counting them as
              work waiting on a person, under a caption claiming Alfred produced them, is the
              figure a dispatcher reads first. */}
          <Metric label="Waiting on a person" value={String(shown.length)}
            sub={shown.length && shown.every((q) => q.seeded) ? "Written examples \u2014 Alfred has handed off nothing"
              : shown.some((q) => q.kind === "claim" || q.kind === "fraud") ? "Two skipped the ladder" : "Alfred handed these off"} emphasis />
        </div>
      </Panel>

      {CFG.scope.inbound ? (
        <Panel>
          <PanelHead title="Last night, 6pm to 6am" note={`The shift nobody staffs. Alfred answers the carrier line, resolves what the load file already knows, and queues the rest. ${SIMULATED} The four calls below are written examples.`}
            right={<Soft>{resolvedNight} of {night.length} closed</Soft>} />
          {night.map((i) => (
            <div key={i.id} style={{ padding: "13px 20px", borderTop: `1px solid ${T.hairSoft}`, display: "flex", gap: 14, flexWrap: "wrap" }}>
              <span style={{ fontFamily: mono, fontSize: 12, color: T.ink3, minWidth: 62 }}>{i.at}</span>
              <span style={{ flex: 1, minWidth: 240 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{i.topic}</span>
                  {i.outcome === "resolved" && <Soft>closed by Alfred</Soft>}
                  {i.outcome === "captured" && <Soft>left on a desk</Soft>}
                  {i.outcome === "escalated" && <Tag>queued</Tag>}
                </span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3 }}>{i.from}{i.about ? ` · ${i.about}` : " · no load"}</span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink2, marginTop: 6, lineHeight: 1.5 }}>{i.summary}</span>
              </span>
            </div>
          ))}
        </Panel>
      ) : (
        <Panel>
          <PanelHead title="After hours" note="Inbound is switched off in your configuration." />
          <Empty>Four carriers called between 6pm and 6am last night. They got voicemail. Turning inbound on in Settings is the cheapest thing on this screen — the file already has the answers to three of the four.</Empty>
        </Panel>
      )}

      <Panel>
        <PanelHead title="Carrier risk" note="Verification signals from Alfred's calls. Nothing advances until someone with vetting permission clears it."
          right={<span style={{ fontFamily: mono, fontSize: 12.5, color: T.ink3 }}>{openRisk.length} open</span>} />
        {/* ⛔ RISK IS A SEED ARRAY KEYED TO SEED CARRIER IDS, and nothing in the backend
            produces a risk signal — there is no table for one. Against real carriers
            `carriers.find(...)` returns undefined and `c.name` threw, taking the Overview tab
            down. Filtered to entries whose carrier actually resolves, which with real data is
            none — showing a risk row for a carrier nobody can name would be worse than showing
            nothing, and on this screen a risk signal is what a broker acts on. */}
        {/* ⛔ AND AN EMPTY BODY IS A CLAIM — a zero-risk statement about a book this tab may
            not have been able to read. Three different emptinesses, said differently. */}
        {!openRisk.length && <Empty>{!carriersKnown
          ? "Your carrier file has not loaded, so nothing here can be matched to a carrier. This is not a statement that there are no signals."
          : carriers.length
            ? "No verification signals. Nothing in this stack produces one yet — there is no risk table — so this stays empty even for a carrier that should worry you."
            : "No carriers in your file, so there is nothing to raise a signal about. This is not a statement that your carriers are clean."}</Empty>}
        {RISK.filter((r) => carriers.some((x) => String(x.id) === String(r.carrierId))).map((r) => {
          const c = carriers.find((x) => String(x.id) === String(r.carrierId));
          return (
            <div key={r.id} style={{ padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}`, position: "relative", opacity: r.handled ? 0.55 : 1 }}>
              <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: r.handled ? "transparent" : r.severity === "stop" ? T.ink : T.hair }} />
              <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                {r.severity === "stop" ? <Tag solid>stop</Tag> : <Soft>watch</Soft>}
                <button onClick={() => onOpenCarrier(r.carrierId)} style={{ font: "inherit", fontSize: 14, fontWeight: 500, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}>{c.name}</button>
                <Soft>{r.signal}</Soft>
                <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{r.at}</span>
              </div>
              <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 7 }}>{r.detail}</div>
            </div>
          );
        })}
      </Panel>

      <Panel>
        <PanelHead title="Coverage board" note="Loads without a committed carrier, inside Alfred's scope. The ceiling is what Alfred may confirm at — set by the rep." />
        {/* ⛔ "NOTHING UNCOVERED" IS A CLAIM, and it sat directly under a metric reading
            "Loads to cover — Could not read your board". Two panels on one screen disagreeing
            about the same fact, and the one phrased as a fact is the one a rep acts on. */}
        {!loadsKnown ? <Empty>{loadsLoading
          ? "Still reading your board \u2014 this does not yet say whether anything needs covering."
          : "We could not read your board, so this does not say whether anything needs covering."}</Empty>
          : uncovered.length === 0 ? <Empty>Nothing uncovered in scope right now. {outside.length > 0 && `${outside.length} load${outside.length === 1 ? "" : "s"} sit outside what Alfred may touch — your reps are working those as usual.`}</Empty>
          : uncovered.map((l) => (
          <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}`, flexWrap: "wrap" }}>
            <span style={{ flex: 1, minWidth: 210 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{l.ref}</span>
                <Chip status={l.status} /><Soft>{l.freight.equipment}</Soft>
                {/* ⛔ A load nobody has priced has no ceiling. `.toLocaleString()` on null throws. */}
                <span style={{ fontFamily: mono, fontSize: 12 }}>{l.ceiling == null ? "no ceiling set" : `ceiling $${l.ceiling.toLocaleString()}`}</span>
              </span>
              <span style={{ display: "block", fontSize: 14.5, fontWeight: 500, letterSpacing: -0.25, marginTop: 5 }}>{laneLabel(l)}</span>
              <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3 }}>{custById(l.customerId).name} · {(l.bids || []).length} bid{(l.bids || []).length === 1 ? "" : "s"}</span>
            </span>
            <Button size="sm" kind="quiet" onClick={() => onOpenLoad(l.ref)}>Open</Button>
          </div>
        ))}
      </Panel>

      {outside.length > 0 && (
        <Panel>
          <PanelHead title="Outside Alfred's scope" note="Visible so you can see what it's leaving alone. Alfred won't call, post, or write on any of these." />
          {outside.map((l) => {
            const why = inScope(l, CFG, CFG.scope.myLoadsOnly ? seat : null).why;
            return (
              <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "13px 20px", borderTop: `1px solid ${T.hairSoft}`, flexWrap: "wrap", opacity: 0.6 }}>
                <span style={{ flex: 1, minWidth: 200 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{l.ref}</span>
                    <Soft>{l.branch}</Soft><Soft>{l.freight.equipment}</Soft>
                  </span>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 500, marginTop: 4 }}>{laneLabel(l)}</span>
                </span>
                <span style={{ fontSize: 12.5, color: T.ink3 }}>{why}</span>
              </div>
            );
          })}
        </Panel>
      )}

      {late.length > 0 && (
        <Panel>
          <PanelHead title="Late risk" note="ETA past the appointment close, compared in the facility's own time zone." />
          {late.map(({ l, r }) => (
            <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}`, flexWrap: "wrap", position: "relative" }}>
              <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: T.ink }} />
              <span style={{ flex: 1, minWidth: 200 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{l.ref}</span>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{r.stop.name}</span>
                  <Tag solid>{waitLabel(r.minutes)} late</Tag>
                </span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3 }}>ETA {fmt(r.stop.eta, r.stop.tz)} against a {fmt(r.stop.apptEnd, r.stop.tz)} close, {tzAbbr(r.stop.apptEnd, r.stop.tz)}</span>
              </span>
              <Button size="sm" kind="quiet" onClick={() => onOpenLoad(l.ref)}>Open load</Button>
            </div>
          ))}
        </Panel>
      )}

      <Panel>
        <PanelHead title="Waiting on a person"
          note={`Verification and claims skip the ladder. Taking one locks it to you until you close it or put it back.${shown.length && shown.every((q) => q.seeded) ? ` Every row below is a written example — ${SIMULATED}` : ""}`}
          right={<Segmented value={filter} onChange={setFilter} options={[{ v: "all", label: `All ${shown.length}` }, { v: "fraud", label: "Verification" }, { v: "claim", label: "Claims" }, { v: "rate", label: "Ceiling" }, { v: "money", label: "Money" }, { v: "falloff", label: "Fall-off" }]} />} />
        {/* ⛔ "NOTHING WAITING" IS A STRONG CLAIM ON THIS PANEL — it is the one a dispatcher
            reads to decide nobody is waiting on them. It must not be printed over rows that
            were hidden because a carrier could not be looked up. */}
        {queueHidden && <div style={{ padding: "13px 20px", fontSize: 12.5, color: T.ink2, borderTop: `1px solid ${T.hairSoft}`, lineHeight: 1.45 }}>{queueHidden}</div>}
        {shown.length === 0 && !queueHidden
          ? <Empty>Nothing waiting. Alfred is working the board on its own — this panel fills when it hits something it can&apos;t authorize.</Empty>
          : shown.map((q) => <QueueItem key={q.id} q={q} loads={loads} carriers={carriers} carriersKnown={carriersKnown} seat={seat} onOpenLoad={onOpenLoad} onResolve={onResolve} onClaim={onClaim} onRelease={onRelease} />)}
      </Panel>

      <Panel>
        <PanelHead title="Call volume" note="Today, 6am to 6pm." />
        <div style={{ padding: "18px 20px" }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 118 }}>
            {HOURLY.map((v, i) => <span key={i} style={{ flex: 1, height: `${(v / maxHour) * 100}%`, background: v === maxHour ? T.ink : "#D2D2D2", borderRadius: 2 }} />)}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            {HOURLY_LABELS.map((l, i) => <span key={i} style={{ flex: 1, textAlign: "center", fontFamily: mono, fontSize: 10, color: T.ink4 }}>{l}</span>)}
          </div>
          <div style={{ fontSize: 12, color: T.ink2, marginTop: 10, lineHeight: 1.4 }}>
            {SIMULATED} The shape and the totals below are an example of what this panel will show.
          </div>
          <div style={{ borderTop: `1px solid ${T.hairSoft}`, marginTop: 18, paddingTop: 16, display: "flex", flexWrap: "wrap", gap: 30 }}>
            {[["Outbound today", "\u2014"], ["Inbound answered", "\u2014"], ["Check calls skipped by tracking", "\u2014"], ["Covered at or below ceiling", "\u2014"]].map(([k, v]) => (
              <span key={k} style={{ display: "flex", flexDirection: "column", gap: 5 }}><Micro>{k}</Micro><span style={{ fontFamily: mono, fontSize: 15, fontVariantNumeric: "tabular-nums" }}>{v}</span></span>
            ))}
          </div>
        </div>
      </Panel>
    </div>
  );
}

/* ------------------------------- call panel -------------------------------- */

const OUTCOMES = {
  connect: { label: "Answers" },
  voicemail: { label: "Voicemail" },
  noanswer: { label: "No answer" },
  wrongco: { label: "Wrong company" },
  declined: { label: "Declines recording" },
  hangup: { label: "Hangs up" },
};

/**
 * ⛔ THIS PANEL IS A SCRIPT, AND IT SAYS SO. There is no dial seam on the brokerage board —
 * `scriptFor` writes the lines and a timer reveals them. A live-looking transcript with a
 * running clock and a waveform, in front of someone deciding whether to buy, must not be
 * mistaken for a call Alfred placed.
 */
export function CallPanel({ call, elapsed, onEnd, onTransfer, onStopRecording }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [call.lines.length]);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0"), ss = String(elapsed % 60).padStart(2, "0");
  const dead = call.phase === "voicemail" || call.phase === "noanswer" || call.phase === "hangup";
  return (
    <div style={{ margin: "0 20px 16px", border: `1px solid ${T.ink}`, borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", background: T.ink, color: "#fff", flexWrap: "wrap" }}>
        <Waveform active={call.phase === "connected"} invert />
        <span style={{ fontSize: 13, fontWeight: 500 }}>
          {call.phase === "dialing" ? `Dialing ${call.partyName}…`
            : call.phase === "noanswer" ? "No answer"
            : call.phase === "voicemail" ? "Leaving a voicemail"
            : call.phase === "hangup" ? "They hung up"
            : `On the line with ${call.partyName}`}
        </span>
        <span style={{ fontSize: 11, fontFamily: mono, opacity: 0.8, border: "1px solid rgba(255,255,255,.4)", borderRadius: 3, padding: "1px 5px" }}>
          {call.recording ? "REC" : "NOT RECORDING"}
        </span>
        <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 12, opacity: 0.75 }}>{mm}:{ss}</span>
        {!dead && <button onClick={onTransfer} style={{ font: "inherit", fontSize: 12, color: "#fff", background: "none", border: "1px solid rgba(255,255,255,.4)", borderRadius: 5, padding: "2px 8px", cursor: "pointer" }}>Take over</button>}
        <button onClick={onEnd} style={{ font: "inherit", fontSize: 12, color: "#fff", background: "none", border: "1px solid rgba(255,255,255,.4)", borderRadius: 5, padding: "2px 8px", cursor: "pointer" }}>End</button>
      </div>
      {/* ⛔ ALWAYS, while the panel is open — not only once lines have appeared. A live-looking
          transcript with a running clock and a waveform, in front of someone deciding whether
          to buy, must not be mistaken for a call Alfred placed. There is no dial seam here. */}
      <div style={{ padding: "6px 13px", background: T.wash, borderBottom: `1px solid ${T.hairSoft}`,
                    fontSize: 12, color: T.ink2, lineHeight: 1.4 }}>
        {SIMULATED} The words below are a written example of how Alfred works this step.
      </div>
      {call.consentPending && (
        <div style={{ padding: "11px 13px", borderBottom: `1px solid ${T.hairSoft}`, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12.5, color: T.ink2, flex: 1, minWidth: 240, lineHeight: 1.5 }}>
            They declined recording. Two-party state — Alfred has stopped the recorder and is waiting on you before it carries on.
          </span>
          <Button size="sm" onClick={onStopRecording}>Continue without recording</Button>
        </div>
      )}
      {call.lines.length > 0 && (
        <div ref={ref} style={{ maxHeight: 190, overflowY: "auto", padding: 13, display: "flex", flexDirection: "column", gap: 9 }}>
          {call.lines.map((l, i) => (
            <div key={i} className="alf-fade" style={{ display: "flex", gap: 10 }}>
              <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", color: l.who === "alfred" ? T.ink : T.ink4, minWidth: 52, paddingTop: 3 }}>{l.who}</span>
              <span style={{ fontSize: 13.5, lineHeight: 1.45, color: l.who === "alfred" ? T.ink : T.ink2 }}>{l.text}</span>
            </div>
          ))}
        </div>
      )}
      {dead && (
        <div style={{ padding: "11px 13px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>
          {call.phase === "noanswer" ? "In a live deployment Alfred would retry on a schedule; nothing here does."
            : call.phase === "voicemail" ? "In a live deployment Alfred would leave the load number and a callback line."
            : "Alfred logged the hang-up and did not call straight back."}
        </div>
      )}
    </div>
  );
}

/* -------------------------------- inspector -------------------------------- */

function StepMarker({ state }) {
  if (state === "done") return <span style={{ width: 15, height: 15, borderRadius: 999, background: T.ink, color: "#fff", fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✓</span>;
  if (state === "due") return <span style={{ width: 15, height: 15, borderRadius: 999, border: `2px solid ${T.ink}`, background: "#fff", flexShrink: 0, boxSizing: "border-box" }} />;
  return <span style={{ width: 15, height: 15, borderRadius: 999, border: `1px solid ${T.hair}`, background: "#fff", flexShrink: 0, boxSizing: "border-box" }} />;
}

export function Inspector({ load, carriers, carriersKnown = true, call, elapsed, seat, onCallStep, onEndCall, onTransfer, onStopRecording, onPatchStep, onSetCeiling, onClose, onOpenCarrier,
                            ceilingError = null, ceilingPending = false }) {
  const [openStep, setOpenStep] = useState(null);
  // `String(null)` is the four-character string "null" in the input box.
  const [ceil, setCeil] = useState(load && load.ceiling != null ? String(load.ceiling) : "");
  if (!load) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 10, padding: 32, textAlign: "center" }}>
        <Waveform active={false} />
        <div style={{ fontSize: 14, color: T.ink2, marginTop: 4 }}>Select a load</div>
        <div style={{ fontSize: 12.5, color: T.ink3, maxWidth: 220, lineHeight: 1.5 }}>Bids, ceiling, verification, and the call plan open here.</div>
      </div>
    );
  }
  const activeCall = call && call.loadId === load.id ? call : null;
  const carrier = load.carrierId ? carriers.find((c) => c.id === load.carrierId) : null;
  // Only bids whose carrier this brokerage's book can name — see the panel below.
  const bidSplit = splitByCarrier(load.bids, carriers, carriersKnown);
  const bids = bidSplit.shown;
  const bidsHidden = hiddenNote(bidSplit, bidSplit.hidden === 1 ? "bid" : "bids");
  const customer = custById(load.customerId);
  const nextDue = load.plan.find((s) => s.state === "due");
  const det = openDetention(load);
  const late = lateRisk(load);
  const me = byId(seat);
  const seeMargin = me && (me.manager || (sees(me, load.branch) && me.can.includes("cover")));
  const canSetCeiling = me && me.can.includes("cover") && sees(me, load.branch) && canWrite();
  const scope = inScope(load, CFG, CFG.scope.myLoadsOnly ? seat : null);
  // ⛔ THE WALKTHROUGH MAY NOT RUN ON A REAL LOAD. `onCallStep` is a SCRIPT: it sets a status,
  // ticks plan steps and appends activity to local state, nothing persists any of it, and no
  // call is placed — there is no dial seam on this board at all, only the four carrier and
  // customer reads. On the example loads this file used to carry that was a demo. On a load
  // read from the brokerage's own book it is a dispatcher watching real freight move to
  // "covered" on the strength of a call nobody made, until a reload silently puts it back —
  // and a badge on the activity line does not undo a status that moved.
  //
  // `fromApi` is stamped by the read, so a demo load still walks through and freight never can.
  const scripted = !load.fromApi;
  const dialable = canCall() && scope.ok && scripted;
  // Said out loud, not only in a `title`: a hover tooltip is invisible on a touch screen, and
  // a disabled button with no reason beside it reads as a broken button.
  const dialNote = !scripted
    ? "Alfred does not place calls from the broker board yet \u2014 the voice platform is wired on the carrier side only. The steps below are the plan, not a call Alfred will make."
    : !canCall() ? "Outbound calling is off for this brokerage."
      : !scope.ok ? `Alfred is not working this load \u2014 ${scope.why}.`
        : null;

  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <div style={{ padding: "18px 20px 15px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontFamily: mono, fontSize: 12, color: T.ink3, letterSpacing: 0.4 }}>{load.ref}</span>
          <button onClick={onClose} className="alf-close" style={{ font: "inherit", fontSize: 12.5, color: T.ink3, background: "none", border: "none", cursor: "pointer" }}>Close</button>
        </div>
        <div style={{ fontSize: 21, fontWeight: 600, letterSpacing: -0.6, marginTop: 6, lineHeight: 1.25 }}>{laneLabel(load)}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 10, flexWrap: "wrap" }}>
          <Chip status={activeCall ? "calling" : load.status} />
          <Soft>{load.branch}</Soft>
          <span style={{ fontSize: 12, color: T.ink3 }}>{customer.name}</span>
        </div>
        <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 8, lineHeight: 1.5 }}>
          {/* ⛔ JOINED, NOT SEPARATED BY HAND. `freight` is jsonb and a load can be created with
              none of these — React renders an absent child as nothing, so this printed a bare
              " · · ". Dangling punctuation is a screen claiming it had something here. */}
          {[load.freight.commodity, load.freight.weight, load.freight.equipment, load.freight.temp]
            .filter(Boolean).join(" · ") || "No freight details recorded"}
        </div>

        <div style={{ display: "flex", gap: 22, marginTop: 12, flexWrap: "wrap" }}>
          <span><Micro>Customer</Micro><div style={{ fontFamily: mono, fontSize: 14, marginTop: 3 }}>{!seeMargin ? "hidden" : load.sell == null ? "\u2014" : `$${load.sell.toLocaleString()}`}</div></span>
          <span><Micro>Carrier</Micro><div style={{ fontFamily: mono, fontSize: 14, marginTop: 3 }}>{load.buy ? `$${load.buy.toLocaleString()}` : "—"}</div></span>
          <span><Micro>Margin</Micro><div style={{ fontFamily: mono, fontSize: 14, marginTop: 3 }}>{seeMargin ? (marginOf(load) !== null ? `$${marginOf(load).toLocaleString()} · ${marginPct(load)}%` : "—") : "hidden"}</div></span>
        </div>
        {!seeMargin && <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 6 }}>Rates are scoped to the {load.branch} branch.</div>}

        <div style={{ marginTop: 14, padding: "11px 13px", border: `1px solid ${T.hair}`, borderRadius: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 170 }}>
              <Micro>Alfred's ceiling</Micro>
              <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.45 }}>
                Alfred may confirm at or below this. It never counters and never names the number first.
              </div>
            </div>
            {canSetCeiling ? (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input value={ceil} onChange={(e) => setCeil(e.target.value.replace(/[^0-9]/g, ""))}
                  style={{ font: "inherit", fontFamily: mono, fontSize: 13, border: `1px solid ${T.hair}`, borderRadius: 6, padding: "6px 9px", outline: "none", width: 90 }} />
                {/* ⛔ DISABLED WHILE IT IS IN FLIGHT. The ceiling is a real write now, and a
                    button that stays live during a slow POST invites a second click — two
                    ceilings for one decision, with whichever lands last winning. */}
                <Button size="sm" disabled={!ceil || ceilingPending || Number(ceil) === load.ceiling} onClick={() => onSetCeiling(load.id, Number(ceil))}>{ceilingPending ? "Saving…" : "Set"}</Button>
              </div>
            ) : <span style={{ fontFamily: mono, fontSize: 14 }}>{load.ceiling == null ? "Not set" : `$${load.ceiling.toLocaleString()}`}</span>}
          </div>
          {/* ⛔ A FAILED CEILING HAS TO SAY SO HERE. It is the number Alfred may commit the
              brokerage to; silence after a failed write reads as "set", and the figure beside
              it is the OLD one. The handler does not move the display unless the server
              confirmed, so this message and the figure above it always agree. */}
          {ceilingError && <div style={{ marginTop: 10, padding: "10px 12px", border: `1px solid ${T.ink}`, borderRadius: 8, fontSize: 12.5, lineHeight: 1.5 }}>{ceilingError}</div>}
        </div>

        {/* ⛔ "NO DIAL SEAM" IS THE FIRST REASON, NOT THE LAST. This box used to list only the
            two configuration reasons, so on a real load it either said nothing at all or
            blamed the brokerage's scope settings for a seam that does not exist — and the
            operator would go widen the scope and still get nothing. See `dialNote`. */}
        {!scripted && (
          <div style={{ marginTop: 12, padding: "11px 13px", border: `1px solid ${T.ink}`, borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Tag solid>not connected</Tag>
              <span style={{ fontSize: 13.5, fontWeight: 500 }}>Alfred does not call on broker loads yet</span>
            </div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 6, lineHeight: 1.5 }}>{dialNote}</div>
          </div>
        )}
        {scripted && (!scope.ok || !canCall()) && (
          <div style={{ marginTop: 12, padding: "11px 13px", border: `1px solid ${T.ink}`, borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Tag solid>{!scope.ok ? "outside scope" : !CFG.live ? "alfred is dark" : "watch mode"}</Tag>
              <span style={{ fontSize: 13.5, fontWeight: 500 }}>{!scope.ok ? scope.why : !CFG.live ? "Nothing dials until you turn Alfred on" : "Alfred shows what it would do and dials nothing"}</span>
            </div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 6, lineHeight: 1.5 }}>
              {!scope.ok ? "Your reps work this load exactly as they do now. Widen the scope in Settings if you want Alfred on it." : mode().note}
            </div>
          </div>
        )}

        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: T.ink2, flexWrap: "wrap" }}>
          {!load.tracking || load.tracking === "None" ? <><Soft>no tracking</Soft><span>Alfred calls for every position.</span></>
            : load.trackingOk ? <><Soft>tracking live</Soft><span>{load.tracking} · {load.lastPing} · check calls skipped</span></>
            : <><Tag solid>ping stale</Tag><span>{load.tracking} · last ping {load.lastPing}</span></>}
        </div>

        {late && (
          <div style={{ marginTop: 12, padding: "11px 13px", border: `1px solid ${T.ink}`, borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Tag solid>late risk</Tag><span style={{ fontSize: 13.5, fontWeight: 500 }}>{waitLabel(late.minutes)} past {late.stop.name}</span>
            </div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 6, lineHeight: 1.5 }}>
              ETA {fmt(late.stop.eta, late.stop.tz)} against a {fmt(late.stop.apptEnd, late.stop.tz)} close, {tzAbbr(late.stop.apptEnd, late.stop.tz)}. Alfred won't promise a new window.
            </div>
          </div>
        )}

        {!activeCall && nextDue && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 13, padding: "11px 13px", border: `1px solid ${T.ink}`, borderRadius: 8, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <Micro>Next step</Micro>
              <div style={{ fontSize: 13.5, fontWeight: 500, marginTop: 3 }}>{nextDue.title}</div>
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 1 }}>
                {nextDue.party === "customer"
                  ? (customer.doNotCall === true
                    // ⛔ "takes Not recorded — no call" IS WHAT THIS SAID. An account marked
                    // never-phone with no channel recorded is the one state where nobody can
                    // reach them at all, and it rendered as broken English rather than as the
                    // gap it is.
                    ? (customer.updateVia
                      ? `${customer.name} takes ${updateChannel(customer)} — no call`
                      : `${customer.name} — no call, and no channel on file`)
                    : customer.name)
                  : carrier ? carrier.name : "carrier not set"}
              </div>
              {/* ⛔ SAID WHERE THE DECISION IS MADE. For every real customer this is null —
                  nobody has ever asked them — and the button said "Call now" with no caveat,
                  while the only disclosure sat inside a collapsed panel and a metric on
                  another tab. The copy below calls phoning a portal-only customer a service
                  failure; this is the screen admitting it does not know which they are. */}
              {nextDue.party === "customer" && !mandatesNonVoice(customer) && customer.doNotCall !== false
                && <div style={{ fontSize: 12, color: T.ink2, marginTop: 4, lineHeight: 1.4 }}>
                  No contact preference on file for {customer.name} — nobody has recorded whether they take calls.
                </div>}
            </div>
            <Button size="sm" disabled={!dialable || (nextDue.party === "customer" && mandatesNonVoice(customer))} onClick={() => onCallStep(load.id, nextDue.id)}
              title={nextDue.party === "customer" && mandatesNonVoice(customer) ? "This customer mandates a non-voice channel" : dialNote || undefined}>
              {nextDue.party === "customer" && mandatesNonVoice(customer) ? "Push update" : "Call now"}
            </Button>
          </div>
        )}
      </div>

      {activeCall && <CallPanel call={activeCall} elapsed={elapsed} onEnd={onEndCall} onTransfer={onTransfer} onStopRecording={onStopRecording} />}
      {det && <div style={{ padding: "0 20px 16px" }}><DetentionClock stop={det} freeMins={customer.detentionFree} customer={customer} /></div>}

      {/* ⛔ A BID FROM A CARRIER THAT IS NOT IN THE BOOK IS A WHITE SCREEN, AND WAS ONE.
          `carriers.find(...)` returned undefined and `c.name` threw, unmounting the whole
          board — reachable in two clicks from the Overview tab, for every brokerage, in
          every state, because the seed load's bids are keyed to carrier ids that no longer
          exist. Same defect and same fix as the Overview risk panel above: resolve first,
          and if a bid's carrier cannot be found, the bid is not shown. Rendering the row
          with a blank where the carrier's name goes would be worse than not rendering it —
          this is the panel a broker reads to decide who gets the load. The count in the
          header counts what is SHOWN, so it cannot claim bids the panel is hiding. */}
      {(bids.length > 0 || bidsHidden) && (
        <Disclosure title="Bids" meta={bids.length ? `${bids.length} worked` : "none we can name"} defaultOpen={!load.carrierId}>
          {bidsHidden && <div style={{ padding: "11px 0", fontSize: 12.5, color: T.ink2, lineHeight: 1.45 }}>{bidsHidden}</div>}
          {bids.map((b) => {
            const c = carriers.find((x) => String(x.id) === String(b.carrierId));
            return (
              <div key={b.carrierId} style={{ padding: "11px 0", borderTop: `1px solid ${T.hairSoft}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => onOpenCarrier(c.id)} style={{ font: "inherit", fontSize: 13.5, fontWeight: 500, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}>{c.name}</button>
                  {c.status === "approved" && <Soft>approved</Soft>}
                  {(c.status === "prospect" || c.status === "flagged") && <Tag solid>not reviewed</Tag>}
                  {c.status === "blocked" && <Tag>do not use</Tag>}
                  <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{b.at}</span>
                </div>
                <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.45 }}>{b.note}</div>
              </div>
            );
          })}
        </Disclosure>
      )}

      {carrier && (
        <Disclosure title="Carrier" meta={carrier.name} defaultOpen>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <button onClick={() => onOpenCarrier(carrier.id)} style={{ font: "inherit", fontSize: 14, fontWeight: 500, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}>{carrier.name}</button>
            <span style={{ fontFamily: mono, fontSize: 12, color: T.ink3 }}>{carrier.mc}</span>
            {carrier.status === "approved" ? <Soft>approved</Soft> : <Tag solid>{statusWord(carrier.status)}</Tag>}
          </div>
          <VetChecks carrier={carrier} />
        </Disclosure>
      )}

      <Disclosure title="Customer" meta={customer.name}>
        {/* ⛔ joinKnown, like the carrier twin eleven lines down. Raw interpolation over two
            nullable columns rendered "null · null" for a customer that is only a name, and
            "Dana Whitfield · null" for the likelier partial case. */}
        <Field label="Contact" value={joinKnown(customer.contact, customer.phone)} />
        <Field label="Updates" value={joinKnown(updateChannel(customer), customer.portal)} />
        <Field label="Detention free time" value={freeTimeLabel(customer)} />
        <Field label="Accessorials" value={absent(customer.accessorial)} />
        <Field label="Terms" value={absent(customer.terms)} />
        {customer.doNotCall === true && <div style={{ marginTop: 10, padding: "10px 12px", border: `1px solid ${T.ink}`, borderRadius: 8, fontSize: 12.5, lineHeight: 1.5 }}>
          <Tag solid>no voice</Tag> <span style={{ marginLeft: 6 }}>{customer.name} mandates {updateChannel(customer)}. Calling them instead is a service failure — Alfred pushes the milestone and doesn't dial.</span>
        </div>}
      </Disclosure>

      <Disclosure title="Call plan" meta={`${load.plan.filter((s) => s.state === "done").length}/${load.plan.length}`} defaultOpen={!!load.carrierId}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 7, top: 8, bottom: 8, width: 1, background: T.hairSoft }} />
          {load.plan.map((s) => {
            const open = openStep === s.id;
            const skip = s.id === "track" && load.trackingOk;
            const noVoice = s.party === "customer" && mandatesNonVoice(customer);
            return (
              <div key={s.id} style={{ position: "relative", paddingLeft: 26, paddingBottom: 4 }}>
                <span style={{ position: "absolute", left: 0, top: 10 }}><StepMarker state={s.state} /></span>
                <button onClick={() => setOpenStep(open ? null : s.id)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", padding: "8px 6px 8px 0", cursor: "pointer", textAlign: "left", font: "inherit", borderRadius: 6 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13.5, fontWeight: s.state === "due" ? 600 : 500, color: s.state === "scheduled" ? T.ink2 : T.ink, letterSpacing: -0.2 }}>{s.title}</span>
                      <Soft>{s.party}</Soft>
                      {skip && s.state !== "done" && <Soft>tracking live</Soft>}
                      {noVoice && s.state !== "done" && <Soft>{updateChannel(customer)}</Soft>}
                    </span>
                  </span>
                  <span style={{ fontFamily: mono, fontSize: 11.5, color: s.state === "due" ? T.ink : T.ink3 }}>{s.at || "—"}</span>
                </button>
                {open && (
                  <div className="alf-fade" style={{ padding: "4px 0 14px" }}>
                    <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>{s.detail}</div>
                    {s.captures.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 9 }}>
                        {s.captures.map((c) => <span key={c} style={{ fontSize: 11.5, border: `1px solid ${T.hair}`, borderRadius: 4, padding: "2px 7px", color: T.ink2 }}>{c}</span>)}
                      </div>
                    )}
                    <div style={{ marginTop: 10 }}>
                      <Micro style={{ marginBottom: 5 }}>Notes for this call</Micro>
                      {/* ⛔ NOT ON A REAL LOAD. Nothing persists these notes and Alfred places
                          no call from this board, so the placeholder was a promise about a call
                          that will not happen, on a screen that says so a hundred lines up. */}
                      <textarea value={s.notes} disabled={!scripted}
                        placeholder={scripted ? "Carries into what Alfred says on this call." : "Alfred does not call on broker loads yet, and notes typed here are not saved."}
                        onChange={(e) => onPatchStep(load.id, s.id, { notes: e.target.value })} rows={2}
                        style={{ width: "100%", font: "inherit", fontSize: 13, color: T.ink, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 7, padding: "8px 10px", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.45 }} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                      {s.state !== "done" && (
                        <>
                          <Button size="sm" disabled={!!call || noVoice || !dialable} onClick={() => onCallStep(load.id, s.id)}>{noVoice ? "Push update" : "Call now"}</Button>
                          {/* ⛔ AND THE SAME FOR Hold / Mark due: it moves a real load's plan in
                              local state only and reverts on the next reload. */}
                          <Button size="sm" kind="quiet" disabled={!scripted} onClick={() => onPatchStep(load.id, s.id, { state: s.state === "due" ? "scheduled" : "due" })}>{s.state === "due" ? "Hold" : "Mark due"}</Button>
                        </>
                      )}
                      {s.state === "done" && <span style={{ fontSize: 12.5, color: T.ink3 }}>Completed {s.at}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Disclosure>

      <Disclosure title="Stops" meta={`${load.stops.length} stops`}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 5, top: 10, bottom: 10, width: 1, background: T.hairSoft }} />
          {load.stops.map((s) => (
            <div key={s.seq} style={{ position: "relative", paddingLeft: 22, paddingBottom: 14 }}>
              <span style={{ position: "absolute", left: 0, top: 5, width: 11, height: 11, borderRadius: s.type === "pickup" ? 2 : 999, boxSizing: "border-box", background: s.state === "complete" ? T.ink : "#fff", border: `${s.state === "arrived" ? 2 : 1}px solid ${T.ink}` }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13.5, fontWeight: 500 }}>{s.name}</span>
                <Soft>{s.type}</Soft>
                {s.state === "arrived" && <Tag solid>on site</Tag>}
              </div>
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>
                {s.city} · {fmtDay(s.apptStart, s.tz)}–{fmt(s.apptEnd, s.tz)} {tzAbbr(s.apptEnd, s.tz)}{s.eta && s.state !== "complete" ? ` · ETA ${fmt(s.eta, s.tz)}` : ""}
              </div>
            </div>
          ))}
        </div>
      </Disclosure>

      <Disclosure title="Paperwork" meta={`${load.paperwork.filter((d) => d.v).length}/${load.paperwork.length}`}>
        {load.paperwork.map((d, i) => (
          <div key={d.k} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}` }}>
            <span style={{ fontSize: 13.5, fontWeight: 500, minWidth: 120 }}>{d.k}</span>
            <span style={{ flex: 1, fontFamily: d.v ? mono : sans, fontSize: 13, color: d.v ? T.ink : T.ink3 }}>{d.v || "not captured yet"}</span>
            {d.v ? <span style={{ fontSize: 12, color: T.ink3 }}>{d.at}</span> : <Soft>next call</Soft>}
          </div>
        ))}
      </Disclosure>

      {/* ⛔ THE MARKER HAS TO BE READABLE WITH THIS SHUT. "Call plan" opens by default on a
          covered load, so the ✓ and its wall-clock time were visible while the line saying no
          call was placed sat two clicks away. The meta says it, and a marked history opens. */}
      <Disclosure title="Activity"
        meta={load.activity.length
          ? `${load.activity.length}${load.activity.some((a) => String(a.body || "").includes(SIMULATED)) ? " · scripted" : ""}`
          : "None"}
        defaultOpen={load.activity.some((a) => String(a.body || "").includes(SIMULATED))}>
        {load.activity.length === 0 ? <div style={{ fontSize: 13, color: T.ink3 }}>Nothing yet.</div> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            {[...load.activity].reverse().map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 12 }}>
                <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 56, paddingTop: 2 }}>{a.t}</span>
                <div><div style={{ fontSize: 13, fontWeight: 500 }}>{a.head}</div><div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 2 }}>{a.body}</div></div>
              </div>
            ))}
          </div>
        )}
      </Disclosure>
      <div style={{ height: 20 }} />
    </div>
  );
}

/* -------------------------------- carriers --------------------------------- */

export function CarrierProfile({ carrier, carriers, loads, loadsKnown = true, loadsLoading = false, seat, audit, onBack, onOpenLoad, onSetStatus, writeError = null, pending = false,
                                 statusTrail = null }) {
  const [openCall, setOpenCall] = useState(null);
  const [reason, setReason] = useState("");
  // ⛔ A JUSTIFICATION BELONGS TO ONE DECISION. Left in the box after a successful write, it
  // was sent again as the reason for the NEXT status — so "Double-brokered 88214" could end up
  // stored as the reason a carrier was approved.
  useEffect(() => { setReason(""); }, [carrier.id, carrier.status]);
  const risks = RISK.filter((r) => String(r.carrierId) === String(carrier.id));
  const openRisks = risks.filter((r) => !r.handled);
  const calls = CALL_LOG.filter((c) => String(c.carrierId) === String(carrier.id));
  // ⛔ LOOSE, like every other id compare on this board. Strict, a numeric `carrierId`
  // against a string `carrier.id` showed this carrier ZERO open loads — silently, which on
  // this screen reads as a carrier with nothing running.
  const mine = loads.filter((l) => String(l.carrierId) === String(carrier.id) && !CLOSED.includes(l.status));
  const me = byId(seat);
  const canVet = me && me.can.includes("vetting") && canWrite();
  // ⛔ KEYED ON THE ROW ID, NOT THE MC. `log(..., c.mc)` wrote the MC as the key and this
  // read it back — and most real broker_carriers rows have no MC at all, so `null === null`
  // matched EVERY carrier without one. Block carrier A, open carrier B, and B's status
  // history showed A's block attributed to B. A false attribution of a do-not-use decision.


  // ⛔ THE TRAIL IS A PROP, NOT A FETCH IN HERE. Every other read on this board is owned by
  // `AlfredBrokerBoard` and passed down — carriers, customers, loads — and a fetch inside this
  // component would also be unreachable by any render assertion, because the profile only
  // exists after a click `react-dom/server` cannot make. See `statusTrailNote`.
  // ⛔ A TRAIL STAMPED FOR ANOTHER CARRIER IS NOT THIS CARRIER'S TRAIL, and reads as "not
  // answered yet" rather than as theirs. This is the whole reason the slice carries an id: the
  // alternative was trusting that an effect had already cleared it, and effects run after
  // paint. See the note on `statusTrail` in `AlfredBrokerBoard`.
  //
  // ⛔ AND `sameId`, NOT `String(x) === String(y)`. A slice stamped for NOBODY matched a
  // carrier with no id, because `String(null) === String(null)` is true — the MC bug above,
  // one coercion layer down. An independent review rendered it. See `sameId`.
  //
  // ⛔ AND `Array.isArray` ON THE ROWS. `.map` on a non-array throws — a white screen — and
  // `statusTrailNote` defaults `count` to 0, so the fallback was the one sentence this panel
  // exists to prevent: "nobody has approved or blocked them", said about an answer nobody
  // could read. A malformed payload reads as unreadable, never as a clean record.
  //
  // ⛔ AND NO `typeof`/`Array.isArray` ON THE SLICE. There was a pair, and a mutation harness
  // proved both DEAD: `sameId` needs a real scalar on both sides, and a primitive, an array or
  // the old bare-array shape has no `carrierId` at all, so each is refused one line down. Two
  // clauses no test could fail — deleting them leaves the junk-slice loop as the live proof.
  const trailMine = statusTrail && sameId(statusTrail.carrierId, carrier.id) ? statusTrail : null;
  const trailSafe = trailMine && Array.isArray(trailMine.rows) ? trailMine.rows : [];
  const trailError = trailMine
    ? trailMine.error || (Array.isArray(trailMine.rows) ? null
      : "The earlier decisions could not be read — the answer did not come back as a list.")
    : null;
  const trailNote = statusTrailNote({
    loading: trailMine === null,
    error: trailError,
    count: trailSafe.length,
    // the carrier's CURRENT status, which is what tells an empty trail from a missing one
    status: carrier.status,
    truncated: !!(trailMine && trailMine.truncated),
  });
  const trailRows = trailError ? [] : trailSafe;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <button onClick={onBack} style={{ font: "inherit", fontSize: 13, color: T.ink2, background: "none", border: "none", cursor: "pointer", padding: 0, alignSelf: "flex-start" }}>‹ All carriers</button>

      <Panel>
        {/*
          ⛔ "Every check passed the last time Alfred called" / "clear" / "Nothing flagged" — for a
          carrier nobody has ever called. `risks` comes from the RISK seed array and is empty for
          every real row, and the empty case was written to mean ALL CLEAR rather than NOTHING RAN.
          That is the identical failure the CheckMark fallback had — a green reading produced by an
          absence — sitting one panel away from the honest line this screen now carries, and an
          independent review found it there after I fixed the other one.
          Nothing in this stack produces a risk signal; there is no table for one.
        */}
        <PanelHead title={openRisks.length ? `${openRisks.length} open risk signal${openRisks.length === 1 ? "" : "s"}` : "No risk signals recorded"}
          note={openRisks.length
            ? "Alfred stopped on these. Nothing advances until compliance clears it."
            : "No automated signal has been raised against this carrier — nothing in this stack produces one. An empty list here is not a clean record; the decisions on file are below."}
          right={openRisks.length ? <Tag solid>review</Tag> : <Soft>none run</Soft>} />
        {risks.length === 0 && <div style={{ padding: "16px 20px 20px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13, color: T.ink3 }}>No signals have been recorded against this carrier.</div>}
        {risks.map((r) => (
          <div key={r.id} style={{ padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}`, position: "relative", opacity: r.handled ? 0.55 : 1 }}>
            <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: r.handled ? "transparent" : r.severity === "stop" ? T.ink : T.hair }} />
            <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
              {r.severity === "stop" ? <Tag solid>stop</Tag> : <Soft>watch</Soft>}
              <span style={{ fontSize: 14, fontWeight: 500 }}>{r.signal}</span>
              <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{r.at}</span>
            </div>
            <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 7 }}>{r.detail}</div>
          </div>
        ))}
      </Panel>

      <Panel>
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "18px 20px 16px", flexWrap: "wrap" }}>
          <span style={{ flex: 1, minWidth: 180 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 20, fontWeight: 600, letterSpacing: -0.5 }}>{carrier.name}</span>
              {carrier.status === "approved" ? <Soft>approved</Soft> : <Tag solid>{statusWord(carrier.status)}</Tag>}
              {carrier.dnc && <Tag>do not call</Tag>}
            </span>
            {/* ⛔ RAW JSX CHILDREN OVER NULLABLE FIELDS. For a real carrier — mostly a name and a
              status — this rendered "  · USDOT  · authority": dangling separators and two orphan
              labels, a screen claiming it had something there. React drops the nulls silently, so
              the render test's /undefined|null|NaN/ cannot see it. Only what is known. */}
            <span style={{ display: "block", fontSize: 13, color: T.ink3, marginTop: 3 }}>{joinKnown(
              carrier.mc, carrier.dot && `USDOT ${carrier.dot}`, carrier.authorityAge && `authority ${carrier.authorityAge}`)}</span>
          </span>
          {carrier.onTime !== null && <span style={{ textAlign: "right" }}><Micro>On time</Micro><div style={{ fontFamily: mono, fontSize: 22, letterSpacing: -0.8, marginTop: 4 }}>{pctOf(carrier.onTime)}</div></span>}
        </div>
        <div style={{ padding: "0 20px 16px" }}><VetChecks carrier={carrier} /></div>
        <div style={{ padding: "0 20px 16px" }}>
          <Field label="Contact" value={joinKnown(carrier.contact, carrier.phone)} />
          <Field label="On file at FMCSA" value={absent(carrier.fmcsaPhone)} isMono />
          <Field label="Record changes" value={absent(carrier.recordChanged)} />
          <Field label="Phone and address" value={absent(carrier.voip)} />
          <Field label="Safety" value={joinKnown(carrier.safety, carrier.oos)} />
          {/* ⛔ THE WHOLE INSURANCE PANEL IS CONDITIONAL. `ins` is null for every real carrier —
              nothing in this stack reads a certificate — and eight `carrier.ins.x` reads threw on
              it. An absent panel is honest; eight rows of "Not checked" would be noise, and eight
              rows of invented limits would be a broker's reason to trust a stranger. */}
          {carrier.ins ? (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.hairSoft}` }}>
              <Micro style={{ marginBottom: 6 }}>Insurance</Micro>
              <Field label="Auto liability" value={absent(carrier.ins.auto)} />
              <Field label="Cargo" value={absent(carrier.ins.cargo)} />
              <Field label="General liability" value={absent(carrier.ins.gl)} />
              <Field label="Reefer breakdown" value={absent(carrier.ins.reeferBreakdown)} />
              <Field label="Trailer interchange" value={absent(carrier.ins.interchange)} />
              <Field label="Certificate holder" value={absent(carrier.ins.certHolder)} />
              <Field label="Cancellation notice" value={absent(carrier.ins.cancelNotice)} />
              <Field label="Expires" value={absent(carrier.ins.expires)} />
            </div>
          ) : (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.hairSoft}`, fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>
              <Micro style={{ marginBottom: 6 }}>Insurance</Micro>
              No certificate on file. Nothing in this platform reads one yet — ask the carrier for
              a COI and check the limits yourself before you cover a load with them.
            </div>
          )}
          {carrier.tracking !== null && (
            <div style={{ padding: "12px 0 2px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ fontSize: 13, color: T.ink3 }}>Tracking compliance</span><span style={{ fontFamily: mono, fontSize: 13 }}>{pctOf(carrier.tracking)}</span></div>
              <Bar pct={carrier.tracking} />
            </div>
          )}
          <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.hairSoft}` }}>{carrier.notes || carrier.note || NOT_CHECKED}</div>
        </div>

        <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 190 }}>
              <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>Status</div>
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>{canVet ? "Applies to every open bid immediately. A reason is required and goes in the audit trail." : "Only compliance or the branch manager can change this."}</div>
            </div>
            {/* ⛔ ONLY WHAT THE BACKEND ACCEPTS. `BROKER_CARRIER_STATUSES` is
                ['prospect','approved','blocked'] and this offered "Review" → `flagged`, which
                `setBrokerCarrierStatus` answers with 400 bad_status — a button that could never
                work. It is the same vocabulary mismatch the load STATUS map had, left in place
                for carriers; an independent review found it. `prospect` is the column default
                and now has a name on screen. */}
            {/* ⛔ ONE WRITE AT A TIME. Not awaited and never disabled, this let a broker who saw
                nothing happen click again: two POSTs in flight, Postgres applying them in arrival
                order and the screen in RESOLVE order, so the board could end up showing `approved`
                over a row that is `blocked`. `pending` blocks the second click and says why. */}
            <Segmented value={carrier.status} disabled={pending}
              onChange={(v) => { if (canVet && !pending && reason.trim()) onSetStatus(carrier.id, v, reason); }}
              options={[{ v: "prospect", label: "Not reviewed" }, { v: "approved", label: "Approved" }, { v: "blocked", label: "Do not use" }]} />
          </div>
          {pending && <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 9 }}>{"Saving\u2026"}</div>}
          {/* ⛔ AND A BLANK REASON IS NOT A DEAD BUTTON. With the box empty, clicking a segment
              did nothing and said nothing — the same "indistinguishable from a dead button"
              the write-error channel was added for. */}
          {canVet && !pending && !reason.trim() && <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 9 }}>Type a reason first — the status will not change without one.</div>}
          {writeError && <div style={{ fontSize: 12.5, color: T.ink, marginTop: 9, border: `1px solid ${T.ink}`, borderRadius: 7, padding: "9px 11px", lineHeight: 1.45 }}>{writeError}</div>}
          {canVet && (
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason — required before the status will change"
              style={{ width: "100%", marginTop: 10, font: "inherit", fontSize: 12.5, border: `1px solid ${reason.trim() ? T.hair : T.ink}`, borderRadius: 6, padding: "7px 10px", outline: "none", boxSizing: "border-box" }} />
          )}
          {/* ⛔ THE STORED DECISION, WHICH IS THE ONE THAT SURVIVES A REFRESH.
              Not to be confused with the decision trail further down, which is every earlier
              status this carrier has had. This is the reason, the author and the date the
              backend writes beside the CURRENT status, so the justification for a do-not-use
              record outlives the session that made it. (`audit` is this component's one dead
              prop — it is read in `History`, never here.) */}
          {carrier.statusReason && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.hairSoft}`, fontSize: 12.5, lineHeight: 1.5 }}>
              <Micro style={{ marginBottom: 6 }}>On file for this status</Micro>
              <div style={{ color: T.ink }}>&ldquo;{carrier.statusReason}&rdquo;</div>
              <div style={{ color: T.ink3, marginTop: 4 }}>{joinKnown(carrier.statusBy, fmtDate(carrier.statusAt))}</div>
            </div>
          )}
          {/* ⛔ THE STORED TRAIL, WHICH IS THE RECORD. Four different facts, and an empty list
              used to be the only one of them this screen could show. */}
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.hairSoft}` }}>
            <Micro style={{ marginBottom: 8 }}>Every decision on file</Micro>
            {trailNote && <div style={{ fontSize: 12.5, lineHeight: 1.5, color: trailError ? T.ink : T.ink3,
              ...(trailError ? { border: `1px solid ${T.ink}`, borderRadius: 6, padding: "9px 11px" } : {}) }}>{trailNote}</div>}
            {trailRows.map((e, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "7px 0", flexWrap: "wrap", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}` }}>
                <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3, minWidth: 86 }}>{fmtDate(e.decidedAt) || "\u2014"}</span>
                <span style={{ fontSize: 12.5, fontWeight: 500, minWidth: 150 }}>{statusEventLine(e)}</span>
                <span style={{ fontSize: 12.5, color: T.ink2, flex: 1, minWidth: 180 }}>
                  {e.reason ? `\u201c${e.reason}\u201d` : "No reason recorded"}
                  {e.decidedBy ? ` — ${e.decidedBy}` : ""}
                </span>
              </div>
            ))}
          </div>

        </div>
      </Panel>

      <Panel>
        {/* ⛔ "NOTHING RUNNING WITH THIS CARRIER" IS A CLAIM. On the screen where somebody
            decides whether to block a carrier, an unread load list must not read as a clean
            slate — this is the panel that says whether they are hauling for you right now. */}
        <PanelHead title="Loads on the board" note={!loadsKnown ? `${boardStateNote({ loading: loadsLoading, failed: !loadsLoading })}.` : mine.length ? "Open one to see its call plan." : "Nothing running with this carrier."} />
        {!loadsKnown && <div style={{ padding: "16px 20px 20px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13, color: T.ink3, lineHeight: 1.5 }}>
          {loadsLoading ? "We are still reading your board, so this does not yet say whether this carrier is on a load."
            : "We could not read your board, so this does not say whether this carrier is on a load right now."}</div>}
        {loadsKnown && mine.length === 0 && <div style={{ padding: "16px 20px 20px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13, color: T.ink3 }}>No active loads.</div>}
        {mine.map((l) => (
          <button key={l.id} onClick={() => onOpenLoad(l.ref)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "15px 20px", borderTop: `1px solid ${T.hairSoft}`, background: "none", border: "none", cursor: "pointer", textAlign: "left", font: "inherit" }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{l.ref}</span><Chip status={l.status} />{!l.trackingOk && <Tag>ping stale</Tag>}
              </span>
              <span style={{ display: "block", fontSize: 15, fontWeight: 500, letterSpacing: -0.3, marginTop: 5 }}>{laneLabel(l)}</span>
            </span>
            <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
          </button>
        ))}
      </Panel>

      <Panel>
        <PanelHead title={`Calls with ${carrier.name}`} note={calls.length ? `${calls.length} on record.` : "No calls yet."} />
        {calls.map((c) => <CallRow key={c.id} c={c} carriers={carriers} showParty={false} open={openCall === c.id} onToggle={() => setOpenCall(openCall === c.id ? null : c.id)} />)}
      </Panel>
    </div>
  );
}

/**
 * What is wrong with what somebody typed in a docket field, or null.
 *
 * ⛔ A DOCKET IS A NUMBER, and it was compared as typed. With "MC-555555" blocked, "555555",
 * "mc 555555" and "MC555555" each walked past the do-not-use list and were added as a fresh
 * prospect — three of five spellings, from the field this form puts in front of an operator.
 * The backend keys them now and refuses a field holding a word, because keying on "pending"
 * would collide two carriers that both wrote it. This is the same rule, said before the round
 * trip. Exported so it is testable: as a closure, a source check for it matched the other call
 * site and removing the guard from one of them survived every assertion.
 */

// Re-exported so a screen test can reach the rule through this module; it is DEFINED in
// shared/docket.js, which the backend's test imports to prove the two copies agree.
export { docketComplaint, DOCKET_MAX_DIGITS };

/* ------------------------------ adding a carrier ---------------------------- */

/**
 * The saved carrier, placed into the list the screen is holding.
 *
 * ⛔ MAPPED, NOT SPREAD. The API sends `mcNumber` / `contactName` / `dotNumber`; every row in
 * this list is adapter shape — `mc` / `contact` / `dot`. Pushing the API row in raw puts a
 * carrier on screen with a blank MC and no contact, on the tab where the MC is the thing that
 * tells two carriers apart. Pure and exported so a test can reach it: the root's handler is
 * behind a click and an await, where no render test goes.
 *
 * Sorted by name, because that is the order `listBrokerCarriers` returns and a row that
 * appears somewhere other than where a reload would put it looks like a different carrier.
 */
export const withCarrierAdded = (rows, apiRow) => {
  const row = brokerCarrierFromApi(apiRow);
  if (!row) return rows || [];
  // ⛔ REPLACE BY ID, NEVER APPEND BLINDLY. An unconditional push put the same id in the list
  // twice — duplicate React keys, "Carriers on file" off by one, and `carriers.find(...)`
  // returning whichever sorted first, so opening the carrier could show the PRE-update name
  // and contact while the database held the new ones.
  const without = (rows || []).filter((r) => String(r.id) !== String(row.id));
  return [...without, row]
    .sort((a, b) => String(a.name).toLowerCase().localeCompare(String(b.name).toLowerCase()));
};

/**
 * The only way a carrier gets into the book.
 *
 * ⛔ IT DOES NOT COLLECT VETTING. The eight checks are a record of what somebody actually
 * established, each stamped with who claimed it — `vettingWrite` in the backend attributes
 * every entry to the session and refuses a payload that tries to call itself a platform check.
 * A tick box on an intake form is a self-attestation, and this is the screen that decides
 * whether a stranger is real. Vetting is recorded on the carrier's own page, afterwards.
 *
 * ⛔ AND IT DOES NOT OFFER A ONE-CLICK MERGE. When the name is already in the book the backend
 * refuses with 409 rather than writing onto the matching row, because matching on a name let a
 * save carrying a different company's MC overwrite the first company's DOT, phone, notes and
 * FMCSA callback number. The two honest resolutions are the ones the refusal names: open the
 * carrier that is already there, or give this one's MC number so it can be told apart. A
 * button that posted the row's id on the operator's behalf would be the merge again, one
 * click further away.
 */
/**
 * Put a shipper on the customer file — and record the terms the board is actually about.
 *
 * ⛔ IT COLLECTS WHAT THE TABLE CAN STORE, AND THE TERMS WERE THE POINT. The Customers tab's own
 * heading is "How each account wants to be updated, what their contract says about detention,
 * and what Alfred may commit to", and `broker_customers` held name, contact, phone, email,
 * branch and notes — none of those three. So `doNotCall` was permanently null and nobody could
 * record that a shipper must not be phoned, on the screen whose copy calls phoning a portal-only
 * customer a service failure. The columns exist now; this is the screen that fills them.
 *
 * ⛔ EVERY TERM IS OPTIONAL AND "not recorded" IS A REAL CHOICE. A form that forces an answer
 * manufactures one, and a guessed contact preference is worse here than an admitted gap — the
 * Inspector says out loud when nobody has recorded whether an account takes calls.
 */
export function AddCustomer({ onSave, onOpenExisting, onOpenAdded = null, onCancel, canOpenExisting = null, busy = false,
                              visibleBranch = () => true }) {
  const BLANK = { name: "", contactName: "", phone: "", email: "", branch: "", notes: "",
                  updateVia: "", portalUrl: "", doNotCall: "", detentionFreeMinutes: "",
                  accessorialLimit: "", paymentTerms: "" };
  const [f, setF] = useState(BLANK);
  const set = (k) => (v) => setF((cur) => ({ ...cur, [k]: v }));
  const [problem, setProblem] = useState(null);     // { text, existing } | null
  const [added, setAdded] = useState(null);
  // What the save could not keep, so the success banner can admit it. Both are computed from
  // the SERVER's row against what was typed, never from the form's own hopes.
  const [addedPhoneDropped, setAddedPhoneDropped] = useState(false);
  const [addedOutOfBranch, setAddedOutOfBranch] = useState(false);

  // ⛔ THE SAME TWO RULES THE BACKEND ENFORCES, SHOWN BEFORE THE POST. Not instead of it —
  // `customerTermsComplaint` is the authority and refuses these with words — but a contradiction
  // the operator can see while typing is better than a round trip that rejects it.
  const portalMissing = f.updateVia === "portal" && !f.portalUrl.trim();
  const contradiction = f.doNotCall === "yes" && f.updateVia === "call";
  const blocked = portalMissing || contradiction;

  const submit = async () => {
    if (!f.name.trim() || busy || blocked) return;
    setProblem(null);
    setAdded(null);
    let r;
    try {
      r = await onSave({
        name: f.name.trim(),
        contactName: f.contactName, phone: f.phone, email: f.email,
        branch: f.branch, notes: f.notes,
        // ⛔ "" MEANS UNANSWERED AND MUST GO AS null, NOT AS AN EMPTY STRING OR A ZERO.
        // `Number("")` is 0, and a 0 here is a recorded contract term — no free time at all.
        updateVia: f.updateVia || null,
        portalUrl: f.portalUrl.trim() || null,
        doNotCall: f.doNotCall === "" ? null : f.doNotCall === "yes",
        detentionFreeMinutes: f.detentionFreeMinutes === "" ? null : Number(f.detentionFreeMinutes),
        accessorialLimit: f.accessorialLimit === "" ? null : f.accessorialLimit,
        paymentTerms: f.paymentTerms.trim() || null,
      });
      if (!r || typeof r.ok !== "boolean") throw new Error("the save did not say whether it worked");
    } catch (err) {
      setProblem({ text: `Not added — ${humanError(err)}`, existing: null });
      return;
    }
    if (r.ok) {
      setAdded(r.customer || null);
      setAddedPhoneDropped(!!f.phone.trim() && !(r.customer && r.customer.phone));
      setAddedOutOfBranch(!!(r.customer && r.customer.branch) && !visibleBranch(r.customer.branch));
      setF(BLANK);
      return;
    }
    setProblem({ text: r.text, existing: r.existing || null });
  };

  return (
    <div style={{ padding: "16px 20px 18px", borderTop: `1px solid ${T.hairSoft}`, background: T.wash }}>
      <Micro style={{ marginBottom: 10 }}>Add a customer</Micro>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <BField label="Account name" value={f.name} onChange={set("name")} placeholder="As it appears on their paperwork"
          hint="This is the identity of the account — a shipper has no docket number to fall back on, so a name already on your file is refused rather than added twice." />
        <BField label="Contact" value={f.contactName} onChange={set("contactName")} placeholder="Who answers" half />
        <BField label="Their phone" value={f.phone} onChange={set("phone")} placeholder="(469) 555-0177" half isMono />
        <BField label="Email" value={f.email} onChange={set("email")} placeholder="dana@example.com" half />
        <BField label="Branch" value={f.branch} onChange={set("branch")} placeholder="Houston" half
          hint="Which of your branches owns the account. Leave it blank if it is not branch-specific — the board tells an unbranched account apart from one it cannot see." />

        <div style={{ flex: "1 1 100%", borderTop: `1px solid ${T.hairSoft}`, paddingTop: 12, marginTop: 2 }}>
          <Micro style={{ marginBottom: 5 }}>How Alfred may reach them</Micro>
          <div style={{ fontSize: 12.5, color: T.ink3, lineHeight: 1.45, marginBottom: 10 }}>
            Leave anything blank that nobody has asked them. The board shows an unanswered
            question as unanswered — it does not assume an account takes phone calls.
          </div>
        </div>
        <div style={{ flex: "1 1 44%", minWidth: 180 }}>
          <Micro style={{ marginBottom: 5 }}>Updates go by</Micro>
          <select value={f.updateVia} onChange={(e) => set("updateVia")(e.target.value)} style={{ ...selectStyle, width: "100%" }}>
            <option value="">Not recorded</option>
            <option value="call">A phone call</option>
            <option value="portal">Their portal</option>
            <option value="edi">EDI</option>
            <option value="email">Email</option>
          </select>
        </div>
        <div style={{ flex: "1 1 44%", minWidth: 180 }}>
          <Micro style={{ marginBottom: 5 }}>Alfred may phone them</Micro>
          <select value={f.doNotCall} onChange={(e) => set("doNotCall")(e.target.value)} style={{ ...selectStyle, width: "100%" }}>
            <option value="">Not recorded</option>
            <option value="no">Yes — calls are fine</option>
            <option value="yes">No — never phone this account</option>
          </select>
        </div>
        {/* The column is `portal_url`, and it deliberately holds an EDI identifier too — one
            field for "where the update goes", because EDI is a channel with an address like
            any other and a second nullable column for it would be the same field twice. */}
        <BField label="Portal or EDI address" value={f.portalUrl} onChange={set("portalUrl")}
          placeholder="supplier.example.com, or EDI 214 via SPS" isMono
          problem={portalMissing ? "Updates are set to go through their portal, so this cannot be blank — otherwise the record says to use a portal and names none." : null} />
        <BField label="Free time before detention" value={f.detentionFreeMinutes}
          onChange={(v) => set("detentionFreeMinutes")(v.replace(/[^0-9]/g, ""))}
          placeholder="120" half isMono
          hint="In minutes, as their contract states it, up to a day. 0 is a real answer — no free time at all — and blank means nobody has checked." />
        <BField label="Accessorial authority" value={f.accessorialLimit}
          onChange={(v) => set("accessorialLimit")(v.replace(/[^0-9.]/g, ""))}
          placeholder="150" half isMono
          hint="The most Alfred may agree to on this account without asking a person, in dollars. 0 means nothing without written approval." />
        <BField label="Payment terms" value={f.paymentTerms} onChange={set("paymentTerms")}
          placeholder="Net 30 · POD required with invoice" />
        <BField label="Notes" value={f.notes} onChange={set("notes")} placeholder="Anything worth remembering (optional)" />
      </div>

      {contradiction && (
        <div style={{ marginTop: 12, border: `1px solid ${T.ink}`, borderRadius: 7, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5 }}>
          This says Alfred may never phone the account <b>and</b> that updates go by phone call.
          Pick one — the board reads those two fields in different places, so whichever a person
          happens to look at first is the one they would believe.
        </div>
      )}

      <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 12, lineHeight: 1.45 }}>
        {/* ⛔ AND IT DOES NOT PROMISE A CALL. Saving a customer reaches no telephony: there is
            no dial seam on this board at all. The carrier form carries the same warning about
            the vetting checks it cannot record. */}
        Nothing is called by saving this. These terms are what Alfred would obey once calling is
        connected to the broker board — it is not yet.
      </div>

      {added && (
        <div style={{ marginTop: 12, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5, background: "#fff" }}>
          <b>{added.name}</b> is on your customer file.
          {added.doNotCall === true
            ? " Alfred will not phone them."
            : added.doNotCall === false
              ? " Alfred may phone them."
              : " Nobody has recorded whether Alfred may phone them."}
          {/* ⛔ A PHONE NUMBER THAT DID NOT STORE HAS TO SAY SO. `toE164` keeps 10 digits, 11
              starting with 1, or a valid +E.164 — and returns null for anything else. So a
              nine-digit typo saved the account with NO phone number and said nothing, on the
              screen whose entire subject is how to reach the account. */}
          {addedPhoneDropped && <div style={{ marginTop: 7, color: T.ink }}>
            The phone number was not stored — it needs to be ten digits, or eleven starting
            with 1. Everything else was saved; open the account to add the number.
          </div>}
          {/* ⛔ AND A BRANCH THIS SEAT CANNOT SEE MEANS THE ROW VANISHES FROM THE LIST BELOW,
              which otherwise reads as the save having failed. */}
          {addedOutOfBranch && <div style={{ marginTop: 7, color: T.ink2 }}>
            It is filed under {added.branch}, which this seat does not see — so it will not
            appear in the list below. Switch seats or branches to work it.
          </div>}
          {/* ⛔ AND A WAY TO OPEN IT. The carrier form offers this; naming the account and
              leaving the operator to find it in a filtered list is how the carrier form's
              "it cleared the fields and said nothing" bug half-survived. Not offered when the
              row is filtered out of this screen, which would be a button that does nothing. */}
          {onOpenAdded && !addedOutOfBranch && <> <button onClick={() => onOpenAdded(added.id)}
            style={{ font: "inherit", fontSize: 12.5, background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Open it</button> to record the rest of their terms.</>}
        </div>
      )}
      {problem && (
        <div style={{ marginTop: 12, border: `1px solid ${T.ink}`, borderRadius: 7, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5 }}>
          <div>{problem.text}</div>
          {problem.existing && (
            <div style={{ marginTop: 9, display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
              {/* ⛔ NOT OFFERED WHEN THIS SCREEN CANNOT SHOW IT — the customer file is read once
                  on mount, so a row added elsewhere since is not in the list this form would
                  send the operator to. Same trap as the carrier form. */}
              {(!canOpenExisting || canOpenExisting(problem.existing.id))
                ? <Button kind="quiet" size="sm" onClick={() => onOpenExisting(problem.existing.id)}>Open {problem.existing.name}</Button>
                : <span style={{ color: T.ink3 }}>That account is not in the list this screen loaded — reload the page to see it.</span>}
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 9, marginTop: 14 }}>
        <Button size="sm" disabled={!f.name.trim() || busy || blocked} onClick={submit}>
          {busy ? "Saving\u2026" : "Add customer"}
        </Button>
        <Button size="sm" kind="quiet" onClick={onCancel}>Done</Button>
      </div>
    </div>
  );
}

export function AddCarrier({ onSave, onOpenExisting, onOpenAdded, onCancel, canOpenExisting = null, busy = false }) {
  const [f, setF] = useState({ name: "", mcNumber: "", dotNumber: "", contactName: "", phone: "", fmcsaPhone: "", notes: "" });
  const set = (k) => (v) => setF((cur) => ({ ...cur, [k]: v }));
  const [problem, setProblem] = useState(null);     // { text, existing, blocked } | null

  const [added, setAdded] = useState(null);     // the last carrier this form put in the book

  const docketProblem = !!docketComplaint(f.mcNumber) || !!docketComplaint(f.dotNumber);

  const submit = async () => {
    if (!f.name.trim() || busy || docketProblem) return;
    setProblem(null);
    setAdded(null);
    // ⛔ IF onSave THROWS, THE OPERATOR MUST NOT GET A DEAD BUTTON. `r.ok` on a rejected
    // promise threw inside an un-awaited event handler: nothing rendered, nothing was said,
    // and the button stayed live — which on this screen is the invitation to click again.
    let r;
    try {
      r = await onSave({ ...f, name: f.name.trim() });
      if (!r || typeof r.ok !== "boolean") throw new Error("the save did not say whether it worked");
    } catch (err) {
      setProblem({ text: `Not added — ${humanError(err)}`, existing: null, blocked: false });
      return;
    }
    if (r.ok) {
      // ⛔ AND A SUCCESSFUL ADD MUST SAY SO. It cleared the fields and said nothing; the new
      // row is in the list below, but the list is filtered, so with the Segmented on
      // "Approved" or any search text typed a new `prospect` row is filtered out and the
      // operator sees blank fields and no other change.
      setAdded(r.carrier || null);
      setF({ name: "", mcNumber: "", dotNumber: "", contactName: "", phone: "", fmcsaPhone: "", notes: "" });
      return;
    }
    setProblem({ text: r.text, existing: r.existing || null, blocked: !!r.blocked, matchedOn: r.matchedOn || null });
  };

  return (
    <div style={{ padding: "16px 20px 18px", borderTop: `1px solid ${T.hairSoft}`, background: T.wash }}>
      <Micro style={{ marginBottom: 10 }}>Add a carrier</Micro>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <BField label="Company name" value={f.name} onChange={set("name")} placeholder="As it appears on their authority" />
        <BField label="MC number" value={f.mcNumber} onChange={set("mcNumber")} placeholder="MC-123456" half isMono
          hint="Optional, and it is the number — MC-123456, 123456 and MC123456 are all the same docket. A docket belongs to one carrier, so this is how the system knows two entries are the same company, and it will refuse one already in your book. Leave it blank if you do not have it."
          problem={docketComplaint(f.mcNumber)} />
        <BField label="USDOT" value={f.dotNumber} onChange={set("dotNumber")} placeholder="1234567" half isMono
          problem={docketComplaint(f.dotNumber)} />
        <BField label="Contact" value={f.contactName} onChange={set("contactName")} placeholder="Who answers" half />
        <BField label="Their phone" value={f.phone} onChange={set("phone")} placeholder="(713) 555-0142" half isMono />
        <BField label="Number on the FMCSA record" value={f.fmcsaPhone} onChange={set("fmcsaPhone")} placeholder="(713) 555-0199" isMono
          hint="Kept apart from the number they gave you on purpose. A callback to the registered line is how you find out whether the person bidding is the carrier they claim to be — one &ldquo;phone&rdquo; field would destroy that check. Nothing is called by saving this." />
        <BField label="Notes" value={f.notes} onChange={set("notes")} placeholder="Anything worth remembering (optional)" />
      </div>

      <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 12, lineHeight: 1.45 }}>
        They will be added as <b>not reviewed</b>, and adding does not vet anything.
        {/* ⛔ AND IT DOES NOT POINT AT A SCREEN THAT CANNOT DO IT. This said the eight checks
            "are recorded on the carrier's own page" — there is no write path for vetting
            anywhere in this app: VetChecks only renders, freightApi has no vetting call, and
            the profile offers approve/block and nothing else. That is the same false promise
            the empty state used to make one panel over. */}
        {" "}Recording the eight checks is not built yet — until it is, nothing in this app can
        mark a carrier verified, and the profile will show all eight as unchecked.
      </div>

      {added && (
        <div style={{ marginTop: 12, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5, background: "#fff" }}>
          <b>{added.name}</b> is in your book, as <b>not reviewed</b>.
          {onOpenAdded && <> <button onClick={() => onOpenAdded(added.id)} style={{ font: "inherit", fontSize: 12.5, background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Open it</button> to record what you check.</>}
        </div>
      )}
      {problem && (
        <div style={{ marginTop: 12, border: `1px solid ${T.ink}`, borderRadius: 7, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5 }}>
          <div>{problem.text}</div>
          {problem.existing && (
            <div style={{ marginTop: 9, display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
              {/* ⛔ NOT OFFERED WHEN THIS SCREEN CANNOT SHOW IT. The carrier file is read once
                  on mount, so a row added elsewhere since is not in it — and the button
                  unmounts this form, losing the refusal and every field typed, to land on a
                  profile that renders nothing. */}
              {(!canOpenExisting || canOpenExisting(problem.existing.id)) ? (
                <Button kind="quiet" size="sm" onClick={() => onOpenExisting(problem.existing.id)}>
                  Open {problem.existing.name}
                </Button>
              ) : (
                <span style={{ color: T.ink3 }}>
                  That carrier is not in the list this screen loaded — reload the page to see it.
                </span>
              )}
              {/* ⛔ no "add it anyway" and no "update that one" — see the note above. The
                  remedy depends on WHAT matched, and the old text named one that does not
                  work: an MC does not exempt a duplicate NAME, so "give this carrier's MC
                  number and add again" sent the operator round the same refusal. */}
              {!problem.blocked && problem.matchedOn === "name" && <span style={{ color: T.ink3 }}>
                …or, if this is a different company, add it under a name that tells them apart — its full legal name, including LLC or Inc.
              </span>}
              {!problem.blocked && problem.matchedOn && problem.matchedOn !== "name" && <span style={{ color: T.ink3 }}>
                …or correct the number above if it was a typo.
              </span>}
              {problem.blocked && <span style={{ color: T.ink }}>
                It was not added. Note that a do-not-use entry is matched on the docket and on the exact name —
                the same outfit under a different trading name will not be caught here.
              </span>}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 14, display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
        <Button onClick={submit} disabled={!f.name.trim() || busy || docketProblem}>{busy ? "Adding…" : "Add carrier"}</Button>
        <Button kind="quiet" onClick={onCancel} disabled={busy}>Cancel</Button>
        {!f.name.trim() && <span style={{ fontSize: 12.5, color: T.ink3 }}>A company name is the one thing required.</span>}
      </div>
    </div>
  );
}

/** Board.jsx's own copy of Setup.jsx's Field — same shape, local styling. */
export function BField({ label, value, onChange, placeholder, isMono, hint, half, problem = null }) {
  return (
    <div style={{ flex: half ? "1 1 44%" : "1 1 100%", minWidth: 180 }}>
      <Micro style={{ marginBottom: 5 }}>{label}</Micro>
      <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%", font: "inherit", fontSize: 13, fontFamily: isMono ? mono : sans,
                 border: `1px solid ${problem ? T.ink : T.hair}`, borderRadius: 7, padding: "7px 10px", outline: "none", boxSizing: "border-box", background: "#fff" }} />
      {/* ⛔ SAID BEFORE THE ROUND TRIP. The backend refuses a docket field holding a word, and
          an operator should not have to submit to find that out. */}
      {problem && <div style={{ fontSize: 12, color: T.ink, marginTop: 5, lineHeight: 1.45 }}>{problem}</div>}
      {hint && <div style={{ fontSize: 12, color: T.ink3, marginTop: 5, lineHeight: 1.45 }}>{hint}</div>}
    </div>
  );
}

export function Carriers({ carriers, loads, loadsKnown = true, loadsLoading = false, seat, audit, profileId, setProfileId, onOpenLoad, onSetStatus,
                          statusTrail = null,
                          loading = false, loadError = null, writeError = null, pendingStatusId = null,
                          onAddCarrier = null, addPending = false }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  // Nothing about the book is KNOWN while we are still asking, or after a failed ask.
  const unknown = loading || !!loadError;
  const me = byId(seat);
  const canAdd = !unknown && canWrite() && me.can.includes("vetting");
  // ⛔ COMPARED THE WAY EVERY OTHER ID ON THIS BOARD IS. This was a strict `===` while the
  // Board resolves the same thing with `String(c.id) === String(carrierProfile)`: a `profileId`
  // of "2" against a numeric 2 rendered the clickable LIST with a profile id live, which is the
  // one state that can hand a new profile a trail stamped for the last one.
  const profile = carriers.find((c) => String(c.id) === String(profileId));
  // ⛔ THE WRITE ERROR HAS TO REACH THE PROFILE. Approve/block is only on that screen, and
  // this component returns before the banner below it — so a failed block produced no
  // message, no spinner and no movement: indistinguishable from a dead button, which is an
  // invitation to click again. `pending` disables the control while the write is in flight.
  if (profile) return <CarrierProfile carrier={profile} carriers={carriers} loads={loads} loadsKnown={loadsKnown} loadsLoading={loadsLoading} seat={seat} audit={audit}
    statusTrail={statusTrail} onBack={() => setProfileId(null)} onOpenLoad={onOpenLoad} onSetStatus={onSetStatus}
    writeError={writeError && String(writeError.id) === String(profile.id) ? writeError.text : null}
    pending={String(pendingStatusId) === String(profile.id)} />;
  // ⛔ FILTERED BEFORE JOINING. A template literal over nullable fields puts the literal words
  // "null" and "undefined" into the haystack, so typing "null" matched every carrier. Nothing
  // renders it, so no markup check can see it.
  const rows = carriers.filter((c) => (filter === "all" || c.status === filter)
    && (!q.trim() || [c.name, c.mc, c.contact, c.lanes].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase())));
  const withHistory = carriers.filter((c) => c.tracking !== null);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Panel>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {/* ⛔ THE BIG NUMBER MUST NOT SAY ZERO WHEN WE COULD NOT ASK. The honesty was put
              into the list below and left out of the figure above it, so a failed read still
              printed "Carriers on file 0 · 0 approved" and "Not reviewed 0" — the broker reads
              those first, and on this screen "nobody is blocked" is the dangerous direction. */}
          <Metric label="Carriers on file" value={unknown ? "\u2014" : String(carriers.length)}
            sub={unknown ? (loadError ? "Could not read the file" : "Reading\u2026") : `${carriers.filter((c) => c.status === "approved").length} approved`} />
          {/* ⛔ 0/0 IS NaN. `withHistory` is the carriers with a tracking figure, and no real
              carrier has one — nothing in this stack talks to a visibility provider — so this
              printed "NaN%" the moment the seeds went away. An average of nothing is not zero. */}
          <Metric label="Tracking compliance"
            value={withHistory.length ? pctOf(withHistory.reduce((s, c) => s + c.tracking, 0) / withHistory.length) : "\u2014"}
            sub={withHistory.length ? "Carriers who've run for us" : "No tracking data for any carrier"} />
          {/* ⛔ THIS WAS THE STRING "79%". Nothing in this stack measures how often Alfred
              reaches a carrier's dispatch — there is no answered/attempted pair on a broker
              carrier — so the figure was invented and sat on screen beside real ones, which
              is how an invented number gets believed. An em dash until something counts it. */}
          <Metric label="Avg. answer rate" value={"\u2014"} sub="Not measured for broker carriers yet" />
          {/* ⛔ `flagged` IS NOT A STATUS THE DATABASE STORES, so this counted zero forever. The
              real "needs a human" set is the default state: nobody has reviewed them. `flagged`
              is kept in the test so the seed rows still count. */}
          <Metric label="Not reviewed" value={unknown ? "\u2014" : String(carriers.filter((c) => c.status === "prospect" || c.status === "flagged").length)}
            sub={unknown ? "Not known until the file is read" : "Nobody has approved or blocked these"} emphasis />
        </div>
      </Panel>
      <Panel>
        <PanelHead title="Carrier file" note="Open a carrier for risk signals, full insurance, and every call."
          right={<span style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
            <Segmented value={filter} onChange={setFilter} options={[{ v: "all", label: "All" }, { v: "prospect", label: "Not reviewed" }, { v: "approved", label: "Approved" }, { v: "blocked", label: "Blocked" }]} />
            {/* ⛔ THE ONLY WRITE ON THIS BOARD THAT HAD NO GATE. Status needs vetting
                permission and canWrite(); the ceiling needs canWrite(); the header renders
                "writes disabled" — and adding a row to the fraud book checked nothing. It
                is also hidden while the book is unread, because the tab is simultaneously
                saying "nothing on this tab is current". */}
            {onAddCarrier && !adding && canAdd && <Button size="sm" onClick={() => setAdding(true)}>Add carrier</Button>}
            {onAddCarrier && !adding && !canAdd && !unknown
              && <span style={{ fontSize: 12.5, color: T.ink3 }}>{canWrite() ? "Needs vetting permission to add a carrier." : "Writes are off for this seat."}</span>}
          </span>} />
        {/* ⛔ ONLY OFFER TO OPEN A ROW THIS SCREEN ACTUALLY HOLDS. The list is read once on
            mount; a carrier a colleague added since is not in it, and setProfileId would
            unmount the form — losing the refusal and everything typed — to land on a profile
            that renders nothing at all. */}
        {adding && onAddCarrier && <AddCarrier onSave={onAddCarrier} busy={addPending}
          onOpenExisting={(id) => { if (carriers.some((c) => String(c.id) === String(id))) { setAdding(false); setProfileId(id); } }}
          canOpenExisting={(id) => carriers.some((c) => String(c.id) === String(id))}
          onOpenAdded={(id) => { setAdding(false); setProfileId(id); setFilter("all"); setQ(""); }}
          onCancel={() => setAdding(false)} />}
        {/* ⛔ THREE DIFFERENT THINGS THAT ALL USED TO LOOK LIKE AN EMPTY LIST: we have not
            asked yet, we asked and could not get an answer, and the book really is empty.
            On a screen whose job is refusing strangers, "we could not read your block list"
            must never render as "you have blocked nobody". */}
        {loadError && <Empty>{loadError} Nothing on this tab is current — do not treat it as the carrier file.</Empty>}
        {!loadError && loading && <Empty>Reading your carrier file…</Empty>}
        <div style={{ padding: "0 20px 14px" }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, MC, contact, lane" style={{ width: "100%", font: "inherit", fontSize: 13, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "7px 10px", outline: "none", boxSizing: "border-box" }} />
        </div>
        {!loading && !loadError && !carriers.length
          && <Empty>No carriers in your book yet. Add one with the button above — they arrive as &ldquo;not reviewed&rdquo; until somebody approves or blocks them.</Empty>}
        {!loading && !loadError && carriers.length > 0 && !rows.length
          && <Empty>No carrier matches that search or filter.</Empty>}
        {rows.map((c) => {
          const open = RISK.filter((r) => String(r.carrierId) === String(c.id) && !r.handled).length;
          return (
            <button key={c.id} onClick={() => setProfileId(c.id)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "15px 20px", borderTop: `1px solid ${T.hairSoft}`, background: "none", border: "none", cursor: "pointer", textAlign: "left", font: "inherit" }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{c.name}</span>
                  {c.status === "approved" && <Soft>approved</Soft>}
                  {(c.status === "prospect" || c.status === "flagged") && <Tag solid>not reviewed</Tag>}
                  {c.status === "blocked" && <Tag>do not use</Tag>}
                  {open > 0 && <Soft>{open} signal{open === 1 ? "" : "s"}</Soft>}
                </span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 2 }}><span style={{ fontFamily: mono }}>{c.mc || "no MC"}</span>{[c.equipment, c.lanes].filter(Boolean).length ? ` · ${[c.equipment, c.lanes].filter(Boolean).join(" · ")}` : ""}</span>
              </span>
              <span style={{ fontFamily: mono, fontSize: 12.5 }}>{c.onTime !== null && c.onTime !== undefined ? pctOf(c.onTime) : "new"}</span>
              <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
            </button>
          );
        })}
      </Panel>
    </div>
  );
}

/* -------------------------------- customers -------------------------------- */

export function Customers({ loads, loadsKnown = true, loadsLoading = false, seat, onOpenLoad, customers = null,
                           loading = false, loadError = null, onAddCustomer = null, addPending = false }) {
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const me = byId(seat);
  // ⛔ WHAT THE SCREEN WAS GIVEN, not a module-level fallback. `customerList()` returns the
  // seed array whenever the live registry is empty — which is also what "we could not read
  // it" and "the brokerage has no customers" look like. A prop can tell those apart.
  const unknown = loading || !!loadError;
  // ⛔ ONE SAFE LOCAL. `customers` defaults to null, and reading `.length` off it is the
  // white-screen this file keeps finding — the render test caught it here.
  const book = customers || [];
  const rows = book.filter((c) => !c.branch || sees(me, c.branch));
  // ⛔ THE SAME SHAPE AS THE CARRIER TAB'S `canAdd`: a real permission, writes on, and not
  // while the file is unread — a form over a list that is saying "nothing here is current"
  // invites a duplicate of a row nobody can see.
  const canAddCustomer = !unknown && canWrite() && me.can.includes("cover");
  // ⛔ ONLY THE CUSTOMERS WHERE THE FACT IS ACTUALLY RECORDED. Counting across all of them
  // turned "nobody has asked" into a positive statement about every one.
  const voiceKnown = rows.filter((c) => c.doNotCall === true || c.doNotCall === false);
  const freeKnown = rows.filter((c) => c.detentionFree !== null && c.detentionFree !== undefined);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Panel>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {/* ⛔ The subtitle said "Houston" while the count included every customer with no
              branch recorded. (`=== "All"` never matched either — `sees` compares the string
              "All branches".) */}
          <Metric label="Accounts" value={unknown ? "\u2014" : String(rows.length)}
            sub={me.branch === "All branches" ? "All branches"
              : rows.some((c) => !c.branch) ? `${me.branch} · plus unassigned` : me.branch} />
          {/* ⛔ `!c.doNotCall` is true for null AND undefined, so this counted every customer
              as taking phone calls — including every customer nobody has ever asked. The
              screen's own words are that calling a portal-only customer is a service failure. */}
          <Metric label="Take voice updates"
            value={voiceKnown.length ? String(voiceKnown.filter((c) => c.doNotCall !== true).length) : "\u2014"}
            sub={voiceKnown.length ? `of ${voiceKnown.length} where it is recorded` : "Not recorded for any customer"} />
          {/* ⛔ and `c.detentionFree !== 120` is true for an absent value, so every customer
              counted as having non-standard detention terms. */}
          <Metric label="Non-standard free time"
            value={freeKnown.length ? String(freeKnown.filter((c) => c.detentionFree !== 120).length) : "\u2014"}
            sub={freeKnown.length ? `of ${freeKnown.length} with terms on file` : "No detention terms on file"} />
          {/* The loads tab is wired now, so this is a real count of real rows — `customerId`
              is the brokerage's own integer on both sides. It is an em dash whenever the board
              could not be read: "0 open loads" across every account is what a failed read used
              to look like, and it reads as a quiet week rather than as a broken screen. */}
          <Metric label="Open loads" value={loadsKnown ? String(loads.filter((l) => !CLOSED.includes(l.status) && sees(me, l.branch)).length) : "\u2014"}
            sub={loadsKnown ? "Across these accounts, in your branches"
              : boardStateNote({ loading: loadsLoading, failed: !loadsLoading })} />
        </div>
      </Panel>
      <Panel>
        <PanelHead title="Customer file" note="How each account wants to be updated, what their contract says about detention, and what Alfred may commit to."
          right={<span style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
            {/* ⛔ GATED LIKE EVERY OTHER WRITE ON THIS BOARD, and hidden while the file is
                unread — the tab is simultaneously saying "nothing here is current".
                `cover` is the permission: these are the accounts a rep works, and the
                "Customer/account manager" seat carries exactly that one. Accessorial
                authority is money, which argues for `money`; it is recorded here because it
                is a term OF the account, and the money permission gates spending against it.
                Said out loud because the choice is arguable and the next reader will wonder. */}
            {onAddCustomer && !adding && canAddCustomer
              && <Button size="sm" onClick={() => setAdding(true)}>Add customer</Button>}
            {onAddCustomer && !adding && !canAddCustomer && !unknown
              && <span style={{ fontSize: 12.5, color: T.ink3 }}>{canWrite() ? "Needs cover permission to add a customer." : "Writes are off for this seat."}</span>}
          </span>} />
        {adding && onAddCustomer && <AddCustomer onSave={onAddCustomer} busy={addPending}
          visibleBranch={(branch) => sees(me, branch)}
          onOpenExisting={(id) => { if (rows.some((c) => String(c.id) === String(id))) { setAdding(false); setOpenId(id); } }}
          canOpenExisting={(id) => rows.some((c) => String(c.id) === String(id))}
          onOpenAdded={(id) => { setAdding(false); setOpenId(id); }}
          onCancel={() => setAdding(false)} />}
        {/* ⛔ THE SAME THREE EMPTINESSES AS THE CARRIER FILE: not asked yet, asked and
            failed, and genuinely empty. An empty list meant all three. */}
        {loadError && <Empty>{loadError} Nothing on this tab is current — do not treat it as the customer file.</Empty>}
        {!loadError && loading && <Empty>Reading your customer file…</Empty>}
        {/* ⛔ FOUR THINGS, and this used to say one. The fourth is the branch filter: a real
            customer on another branch left a Houston seat reading "No customers in your book
            yet" about a book that has one. The Carriers tab tells these apart in adjacent
            lines and the comment above claimed this did too. */}
        {!unknown && !book.length && <Empty>No customers in your book yet. There is no screen for adding one on this tab — the carrier file has one; until this tab does, a customer can only arrive through the API.</Empty>}
        {!unknown && book.length > 0 && !rows.length && <Empty>No customer on your branch. {book.length} {book.length === 1 ? "is" : "are"} in the book, on other branches.</Empty>}
        {rows.map((c) => {
          const open = openId === c.id;
          // ⛔ String-compared and branch-scoped. Branch-scoped because the Loads tab filters
          // by branch and this panel did not, so an unbranched customer listed loads a seat
          // cannot otherwise see. String-compared because the id is an integer from the
          // database on one side and has been a string on the other.
          const theirs = loads.filter((l) => String(l.customerId) === String(c.id)
            && !CLOSED.includes(l.status) && sees(me, l.branch));
          return (
            <div key={c.id} style={{ borderTop: `1px solid ${T.hairSoft}` }}>
              <button onClick={() => setOpenId(open ? null : c.id)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "15px 20px", background: "none", border: "none", cursor: "pointer", textAlign: "left", font: "inherit" }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{c.name}</span>
                    {c.updateVia && <Soft>{updateChannel(c)}</Soft>}
                    {c.doNotCall === true && <Tag>no voice</Tag>}
                    {c.detentionFree !== null && c.detentionFree !== undefined && c.detentionFree !== 120 && <Soft>{c.detentionFree}m free</Soft>}
                  </span>
                  {/* ⛔ NOT "0 open loads" WHEN THE BOARD DID NOT ANSWER. `joinKnown` drops a
                      null, so an unknown count says nothing rather than claiming none. */}
                  <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 2 }}>{joinKnown(c.branch, loadsKnown ? `${theirs.length} open load${theirs.length === 1 ? "" : "s"}` : null)}</span>
                </span>
                <span style={{ color: T.ink4, fontSize: 13, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
              </button>
              {open && (
                <div className="alf-fade" style={{ padding: "0 20px 18px" }}>
                  <Field label="Contact" value={joinKnown(c.contact, c.phone)} />
                  <Field label="Update channel" value={joinKnown(updateChannel(c), c.portal)} />
                  <Field label="Detention free time" value={freeTimeLabel(c)} />
                  <Field label="Accessorial authority" value={absent(c.accessorial)} />
                  <Field label="Terms" value={absent(c.terms)} />
                  {(c.note || c.notes) && <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 10 }}>{c.note || c.notes}</div>}
                  {theirs.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.hairSoft}` }}>
                      <Micro style={{ marginBottom: 8 }}>Open loads</Micro>
                      {theirs.map((l) => (
                        <button key={l.id} onClick={() => onOpenLoad(l.ref)} style={{ display: "flex", gap: 10, alignItems: "center", font: "inherit", background: "none", border: "none", cursor: "pointer", padding: "4px 0", textAlign: "left" }}>
                          <span style={{ fontFamily: mono, fontSize: 12, color: T.ink2, textDecoration: "underline", textUnderlineOffset: 3 }}>{l.ref}</span>
                          <span style={{ fontSize: 13 }}>{laneLabel(l)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Panel>
    </div>
  );
}

/* --------------------------------- settings -------------------------------- */

function IntegrationRow({ item, onConnect, onDisconnect }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [testing, setTesting] = useState(false);
  const connected = item.state === "connected";
  return (
    <div style={{ borderTop: `1px solid ${T.hairSoft}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", flexWrap: "wrap" }}>
        <span style={{ flex: 1, minWidth: 170 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{item.name}</span><Soft>{item.kind}</Soft>{connected && <Tag solid>connected</Tag>}
          </span>
          <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>{connected ? `${item.pulls} · last sync ${item.lastSync}` : item.pulls}</span>
        </span>
        {connected ? <><span style={{ fontFamily: mono, fontSize: 12, color: T.ink2 }}>{item.keyMask}</span><Button size="sm" kind="quiet" onClick={() => onDisconnect(item.id)}>Disconnect</Button></>
          : <Button size="sm" kind="quiet" onClick={() => setOpen((o) => !o)}>{open ? "Cancel" : "Connect"}</Button>}
      </div>
      {open && !connected && (
        <div className="alf-fade" style={{ padding: "0 20px 18px" }}>
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 8, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <Micro style={{ marginBottom: 5 }}>API key</Micro>
              <input value={key} onChange={(e) => setKey(e.target.value)} placeholder={item.keyHint} style={{ width: "100%", font: "inherit", fontFamily: mono, fontSize: 13, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "9px 11px", outline: "none", boxSizing: "border-box" }} />
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 6 }}>{item.where}</div>
            </div>
            <Button size="sm" disabled={!key.trim() || testing} onClick={() => { setTesting(true); setTimeout(() => { setTesting(false); onConnect(item.id, key); setOpen(false); setKey(""); }, 900); }}>{testing ? "Testing…" : "Test and connect"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function Settings({ cfg, onSetCfg }) {
  const [tab, setTab] = useState("scope");
  const [flags, setFlags] = useState(TOGGLEABLE);
  const [items, setItems] = useState(INTEGRATIONS);
  const connect = (id, key) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, state: "connected", lastSync: "just now", keyMask: `••••${key.slice(-4) || "44b1"}` } : x)));
  const disconnect = (id) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, state: "off", lastSync: null } : x)));
  const groups = [["TMS", "System of record"], ["Load board", "Where bids come from"], ["Vetting", "Carrier identity"], ["Visibility", "Tracking"], ["Customer", "Non-voice channels"]];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22, maxWidth: 760 }}>
      <Segmented value={tab} onChange={setTab} options={[{ v: "scope", label: "Scope" }, { v: "integrations", label: "Integrations" }, { v: "calling", label: "Calling" }, { v: "consent", label: "Consent" }, { v: "limits", label: "Limits" }]} />
      {tab === "scope" && (
        <>
          <Panel>
            <PanelHead title="What Alfred touches" note="Set during onboarding, changeable here. Widening this is the one setting that changes how much of your board an AI is working — it is deliberately not buried." />
            <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
              <Micro style={{ marginBottom: 8 }}>Branches</Micro>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["Houston", "Lafayette", "Dallas"].map((b) => {
                  const on = cfg.scope.branches.includes(b);
                  return <button key={b} onClick={() => onSetCfg({ ...cfg, scope: { ...cfg.scope, branches: on ? cfg.scope.branches.filter((x) => x !== b) : [...cfg.scope.branches, b] } })}
                    style={{ font: "inherit", fontSize: 12.5, padding: "6px 12px", borderRadius: 7, cursor: "pointer", border: `1px solid ${on ? T.ink : T.hair}`, background: on ? T.ink : "#fff", color: on ? "#fff" : T.ink2 }}>{b}</button>;
                })}
              </div>
            </div>
            <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
              <Micro style={{ marginBottom: 8 }}>Equipment</Micro>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["Dry van", "Reefer", "Flatbed"].map((e) => {
                  const on = cfg.scope.mode === "all" || cfg.scope.equipment.includes(e);
                  return <button key={e} onClick={() => onSetCfg({ ...cfg, scope: { ...cfg.scope, mode: "equipment", equipment: on ? cfg.scope.equipment.filter((x) => x !== e) : [...cfg.scope.equipment, e] } })}
                    style={{ font: "inherit", fontSize: 12.5, padding: "6px 12px", borderRadius: 7, cursor: "pointer", border: `1px solid ${on ? T.ink : T.hair}`, background: on ? T.ink : "#fff", color: on ? "#fff" : T.ink2 }}>{e}</button>;
                })}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Answer the carrier line after hours</div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>6pm to 6am. Off means carriers get voicemail.</div>
              </div>
              <Toggle on={cfg.scope.inbound} onChange={(v) => onSetCfg({ ...cfg, scope: { ...cfg.scope, inbound: v } })} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Only loads where the signed-in rep is the load's rep</div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>For agent models where reps won't accept Alfred on someone else's freight.</div>
              </div>
              <Toggle on={cfg.scope.myLoadsOnly} onChange={(v) => onSetCfg({ ...cfg, scope: { ...cfg.scope, myLoadsOnly: v } })} />
            </div>
          </Panel>
          <Panel>
            <PanelHead title="Connection mode" note={TMS_MODES[cfg.tms.mode].note} />
            <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}`, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {Object.entries(TMS_MODES).map(([k, m]) => (
                <button key={k} onClick={() => onSetCfg({ ...cfg, tms: { ...cfg.tms, mode: k } })}
                  style={{ font: "inherit", fontSize: 12.5, padding: "6px 12px", borderRadius: 7, cursor: "pointer", border: `1px solid ${cfg.tms.mode === k ? T.ink : T.hair}`, background: cfg.tms.mode === k ? T.ink : "#fff", color: cfg.tms.mode === k ? "#fff" : T.ink2 }}>{m.label}</button>
              ))}
            </div>
          </Panel>
        </>
      )}

      {tab === "integrations" && (
        <Panel>
          {/* ⛔ THE TMS CLAIM I DELETED FROM THE ENGINE, ASSERTED ONE TAB OVER — with a masked
              live key and a two-minute-old sync, which is the version a buyer's IT person
              acts on. Nothing in this app connects to a TMS, a load board or a visibility
              provider; every row below is an example of what the screen will look like. */}
          <PanelHead title="Connected systems" note="⚠️ Examples, not connections. Nothing here is connected to anything yet — the keys, states and sync times below are placeholders showing what this screen will hold. PowerBroker is intended as the system of record." />
          {groups.map(([kind, label]) => {
            const rows = items.filter((i) => i.kind === kind);
            if (!rows.length) return null;
            return <div key={kind}><div style={{ padding: "12px 20px 6px", borderTop: `1px solid ${T.hairSoft}` }}><Micro>{label}</Micro></div>{rows.map((i) => <IntegrationRow key={i.id} item={i} onConnect={connect} onDisconnect={disconnect} />)}</div>;
          })}
        </Panel>
      )}
      {tab === "calling" && (
        <Panel>
          <PanelHead title="How Alfred calls" note="Voice only. Numbers are read back and confirmed on the call." />
          {flags.map((f) => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{f.label}</div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>{f.note}</div>
              </div>
              <Toggle on={f.on} onChange={(v) => setFlags((xs) => xs.map((x) => (x.id === f.id ? { ...x, on: v } : x)))} />
            </div>
          ))}
        </Panel>
      )}
      {tab === "consent" && (
        <Panel>
          <PanelHead title="Recording and consent" note="Notice is not consent. You dial carriers in two-party states every day, so the policy is per state and the decline path is real." />
          {CONSENT.map(([k, v]) => (
            <div key={k} style={{ padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
              <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{k}</div>
              <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.55, marginTop: 3 }}>{v}</div>
            </div>
          ))}
        </Panel>
      )}
      {tab === "limits" && (
        <Panel>
          <PanelHead title="What Alfred will never do" note="Fixed. Hand this to a customer's procurement team or your insurer." />
          {LIMITS.map((l, i) => (
            <div key={l.t} style={{ padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}`, display: "flex", gap: 12 }}>
              <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 18, paddingTop: 2 }}>{String(i + 1).padStart(2, "0")}</span>
              <div><div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{l.t}</div><div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 3 }}>{l.d}</div></div>
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}

/* ----------------------------------- app ----------------------------------- */

/* ─────────────────────────────────────────────────────────────────────────────
   PROTOTYPE BANNER — AND IT IS NOW TAB-SPECIFIC, BECAUSE THE TABS DIFFER.
   Most of this screen is still seed data defined in this file or in the config
   store: the loads, the customers, the handoff queue, the risk signals, the call
   log, the timers and every transcript. The CARRIERS tab is not — it reads the
   brokerage's real book and approve/block writes to it.
   ⛔ So this banner must not say "not saved" over the Carriers tab. An operator
   screen that looks live and isn't is the failure this platform keeps paying for;
   a screen that IS saving and says it isn't is the same failure pointed the other
   way, and it sits above the button that records "this outfit defrauded us".
   ───────────────────────────────────────────────────────────────────────────── */
export function PrototypeBanner({ what, saved = null }) {
  return (
    <div style={{
      background: "#000", color: "#fff", fontFamily: sans, fontSize: 12.5,
      padding: "7px 22px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
    }}>
      <span style={{
        fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase",
        border: "1px solid #fff", borderRadius: 3, padding: "1px 5px"
      }}>prototype</span>
      {/* ⛔ THE CLAIM CHANGES PER TAB, and the Customers tab's changed again: it reads AND
          writes the real customer file now — the intake screen it was missing exists, so
          "nothing here can change it yet" became the wrong half-truth. The `read` state stays
          because the next tab to be wired will pass through it. */}
      <span>{what}{saved === "write"
        ? " This tab reads and writes your real file. The rest of this board is sample data and no call is placed."
        : saved === "read"
          ? " This tab reads your real file — nothing here can change it yet. The rest of this board is sample data and no call is placed."
          : " Sample data — not connected to live calls, and not saved."}</span>
      <Link to="/broker" style={{ marginLeft: "auto", color: "#fff", fontSize: 12.5 }}>← Brokerage screens</Link>
    </div>
  );
}

export default function AlfredBrokerBoard() {
  const [cfg, setCfgState] = useState(() => {
    const existing = getConfig();
    if (existing) { setCfgMirror(existing); return existing; }
    // ⛔ `save: false` — THIS IS A PLACEHOLDER THE BOARD INVENTED, NOT A FACT ABOUT THE
    // BROKERAGE. Saved, it overwrote the real team, permissions, branches, scope, ladder and
    // vetting rules on every sign-in, because the store is empty for the whole of one.
    const demo = commitConfig(DEMO_CONFIG, { save: false });
    setCfgMirror(demo);
    return demo;
  });
  useEffect(() => subscribe((c) => { setCfgMirror(c); setCfgState(c); }), []);

  // The carrier book. One call, and a failure says so rather than showing an empty file —
  // "no carriers" and "we could not ask" look identical on screen and mean opposite things.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await freight.listBrokerCarriers();
        if (!alive) return;
        setCarriers(brokerCarriersFromApi(rows));
        setCarrierError(null);
      } catch (err) {
        if (!alive) return;
        setCarriers([]);
        setCarrierError(`Could not read your carrier file — ${humanError(err)}`);
      }
    })();
    return () => { alive = false; };
  }, []);

  // The loads. Separate read again, so one failure does not blank the others.
  //
  // ⛔ EVERY LOAD THAT COMES BACK IS MARKED `fromApi`. The scripted call engine writes a plan,
  // a status and activity lines into local state and nothing persists them — which was a demo
  // on seed data and would be a demo on somebody's real freight. The marker on those lines
  // keeps it honest; this flag is what stops it running at all. See `dialable`.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = brokerLoadsFromApi(await freight.listBrokerLoads(), { buildPlan });
        if (!alive) return;
        setLoads(rows.map((l) => ({ ...l, fromApi: true })));
        setLoadError(null);
      } catch (err) {
        if (!alive) return;
        setLoads([]);
        setLoadError(`Could not read your loads — ${humanError(err)}`);
      }
    })();
    return () => { alive = false; };
  }, []);

  // The shipper list. Separate from the carrier read on purpose: one failing must not blank
  // the other, and the two tabs say different things about their own emptiness.
  //
  // ⛔ IT ALSO FEEDS `setCustomerRegistry`, which had no caller at all — so `custById` fell
  // back to the CUSTOMERS seed array for every load on the board, and the Inspector showed a
  // real load's customer as one of five invented accounts with invented detention terms.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = brokerCustomersFromApi(await freight.listBrokerCustomers());
        if (!alive) return;
        setCustomers(rows);
        setCustomerError(null);
      } catch (err) {
        if (!alive) return;
        // ⛔ NOT "the book is empty" — the read failed. `custById` then says "Customer file did
        // not load" rather than asserting the customer is absent from the book. Published here
        // and not through the effect below, because the UNREADABLE flag is a fact about the
        // read, not about the list — and the list is [] in both cases.
        setCustomerRegistry([], { unreadable: true });
        setCustomers([]);
        setCustomerError(`Could not read your customer file — ${humanError(err)}`);
      }
    })();
    return () => { alive = false; };
  }, []);

  // The brokerage's own loads. `null` until the fetch answers — which is NOT the same as
  // "no loads", and the three must read differently. See the carrier file above.
  const [loads, setLoads] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // ⛔ KEYED BY LOAD ID, like the carrier write error above. A bare string followed the
  // operator to the next load they opened, so "the ceiling on GB-88201 was NOT changed" sat
  // under a different load's ceiling — a false alarm about a number nobody touched.
  const [customerAddPending, setCustomerAddPending] = useState(false);
  // ⛔ THE DECISION TRAIL FOR WHICHEVER CARRIER IS OPEN. `broker_carrier_status_events` is
  // written on every status change and its route existed from the start; nothing had ever
  // called it, so an earlier block's justification was in the database and on no screen.
  /**
   * ⛔ THE TRAIL CARRIES THE CARRIER IT BELONGS TO, and the profile refuses to render one
   * stamped for anybody else.
   *
   * It was three unstamped slices — rows, error, truncated — and nothing tied them to a
   * carrier: safety came entirely from this effect clearing them, and `useEffect` runs AFTER
   * paint. So a `carrierProfile: A → B` transition in one batch would paint B's profile with
   * A's decisions for one frame. An independent review traced every setter and found it
   * genuinely unreachable today — both `openCarrier` call sites take an id straight out of
   * `carriers`, so every path to a new profile goes through null — and then named the one
   * function that is a single new call site away from it: `openCarrier`, which sets a profile
   * id from outside the carriers view and does not clear the trail itself. A later review
   * corrected the REASON once given here (that "the list only renders when no profile is
   * open"): the list renders when no profile is FOUND, which is not the same thing, and was
   * itself reachable by an id that did not match strictly. See `Carriers`.
   *
   * ⛔ THIS FILE HAS ALREADY SHIPPED THIS EXACT BUG ONCE. The profile's audit list matched on
   * the MC, which is null for most real carriers, so `null === null` attributed one carrier's
   * block to every carrier without an MC. The payload here is worse: the full decision history,
   * with reasons, on the screen that decides whether a carrier defrauded this brokerage.
   * Stamped, it is impossible by construction rather than by luck of call ordering.
   *
   * `null` = nothing asked for. `{ carrierId, rows, truncated, error }` = an answer about THAT
   * carrier.
   */
  const [statusTrail, setStatusTrail] = useState(null);
  const [ceilingError, setCeilingError] = useState(null);   // { id, text } | null
  const [ceilingPending, setCeilingPending] = useState(null);
  // The brokerage's own carrier book, read from the API. `null` means "not answered yet",
  // which is NOT the same as an empty book and must not render as one.
  const [carriers, setCarriers] = useState(null);
  // ⛔ TWO ERRORS, NOT ONE. "We could not read your carrier file" and "this carrier was not
  // moved to blocked" are different facts, shown in different places. One variable carrying
  // both appended the read-failure warning ("nothing on this tab is current") to a write
  // failure, where it is false — the read succeeded — and left it there all session, because
  // nothing cleared it but a later success.
  const [carrierError, setCarrierError] = useState(null);
  // ⛔ KEYED TO THE CARRIER IT IS ABOUT. Held as a bare string and passed unconditionally,
  // a failed block on carrier A rendered inside carrier B's profile under B's name — on the
  // panel that records "this outfit defrauded us", that reads as a failed block on B.
  const [carrierWriteError, setCarrierWriteError] = useState(null);   // { id, text } | null
  const [statusPending, setStatusPending] = useState(null);
  const [addPending, setAddPending] = useState(false);
  // The brokerage's shipper list. `null` until the fetch answers — see the carrier file above.
  const [customers, setCustomers] = useState(null);
  const [customerError, setCustomerError] = useState(null);
  const [queue, setQueue] = useState(SEED_QUEUE);
  const [audit, setAudit] = useState([]);
  const [seat, setSeat] = useState(cfg.team[0] ? cfg.team[0].id : "u1");
  // ⛔ THE SEAT HAS TO FOLLOW THE CONFIG. `seat` is initialised once from `cfg.team[0]`, and
  // the real config normally arrives AFTER this mounts — so the seat held a demo id, `byId`
  // returned NO_SEAT (fails closed, correctly) and the `<select>` had no matching option, so
  // the browser displayed the first real member while the app acted as nobody.
  useEffect(() => {
    const team = (cfg.team || []).filter((t) => !t.ooo);
    if (!team.length) return;
    if (!team.some((t) => String(t.id) === String(seat))) setSeat(team[0].id);
  }, [cfg.team, seat]);
  const [view, setView] = useState("overview");
  const [selected, setSelected] = useState(null);
  const [carrierProfile, setCarrierProfile] = useState(null);
  const [call, setCall] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [nextOutcome, setNextOutcome] = useState("connect");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  // ⛔ ONE LIST FOR EVERY READER. `carriers` is null until the fetch answers, and a dozen
  // call sites do `carriers.find(...)`; null.find is a TypeError that takes the whole board
  // down on first paint. The loading state is carried separately, in `carriersLoading`.
  const carrierList = carriers || [];
  const carriersLoading = carriers === null;
  // ⛔ WHETHER THE BOOK IS KNOWN AT ALL, which is not the same as whether it is empty. Every
  // screen that hides a row because its carrier "is not in the book" has to know the
  // difference: on a failed read, and on the whole first paint, `carrierList` is also [] —
  // so those filters were dropping real rows and the Overview was stating "no carriers in
  // your file" as a fact about a book it could not read.
  const carriersKnown = carriers !== null && !carrierError;
  // ⛔ ONE LIST FOR EVERY READER. `loads` is null until the fetch answers and a great many call
  // sites do `loads.find(...)`; null.find is a TypeError on first paint.
  const loadList = loads || [];
  // ⛔ AN ERROR OUTRANKS THE IN-FLIGHT FLAG, the same order `loadListNote` uses. The failure
  // path sets both in one batch so this is belt and braces — but "still reading" printed over a
  // read that already failed is the one combination that would never resolve itself on screen.
  const loadsLoading = loads === null && !loadError;
  const loadsKnown = loads !== null && !loadError;

  /**
   * Read the open carrier's decision trail.
   *
   * ⛔ KEYED ON THE STATUS AS WELL AS THE ID. A successful approve/block updates `carriers`,
   * so `statusAt` changes and the trail re-reads — otherwise the list sat one decision behind
   * the status shown directly above it, which is worse than showing nothing.
   */
  const openProfile = carrierProfile == null ? null : (carriers || []).find((c) => String(c.id) === String(carrierProfile));
  useEffect(() => {
    if (carrierProfile == null) { setStatusTrail(null); return undefined; }
    let alive = true;
    setStatusTrail(null);
    (async () => {
      try {
        const { events, truncated } = await freight.listBrokerCarrierStatusEvents(carrierProfile);
        if (!alive) return;
        // ⛔ STAMPED WITH THE CARRIER THE READ WAS FOR, not with whichever is open when it lands.
        setStatusTrail({ carrierId: carrierProfile, rows: Array.isArray(events) ? events : [],
                         truncated: truncated === true, error: null });
      } catch (err) {
        if (!alive) return;
        // ⛔ NOT AN EMPTY TRAIL. On the screen that decides whether a carrier defrauded this
        // brokerage, "no earlier decisions" reads as "never been blocked".
        setStatusTrail({ carrierId: carrierProfile, rows: [], truncated: false,
                         error: `The earlier decisions could not be read — ${humanError(err)}` });
      }
    })();
    return () => { alive = false; };
  }, [carrierProfile, openProfile && openProfile.statusAt]);

  /**
   * ⛔ ONE PUBLISHER FOR `custById`'s REGISTRY, derived from state so it cannot be stale.
   *
   * Three call sites wrote it before: the fetch, the failed fetch, and the add — and the add's
   * was `[...(customers || []), row]` against the render-time closure while the line beside it
   * used the functional updater. Two spellings of one list. `custById` is what every load on
   * the board reads to name its customer, so a registry that lags by one row shows a real
   * account as "Customer not on file" in the Inspector while the Customers tab lists it.
   *
   * The failed read still publishes directly, because `{ unreadable: true }` is a fact about
   * the READ and the list is `[]` either way — the effect cannot tell those apart.
   */
  useEffect(() => { if (customerError === null && customers !== null) setCustomerRegistry(customers); }, [customers, customerError]);
  const customerRows = customers || [];
  const customersLoading = customers === null;
  const me = byId(seat) || cfg.team[0];
  const selectedLoad = loadList.find((l) => l.id === selected) || null;
  const log = (what, ref) => setAudit((a) => [{ at: stamp(), actor: `${me.name} (${me.role})`, what, ref }, ...a]);
  // ⛔ THROUGH THE COLUMN'S OWN ROUTE. This used to be `commitConfig({ ...cfg, live: v })`,
  // which strips `live` on the way out and hard-sets it false on the way back — so the master
  // switch for calling carriers wrote nothing and reset to Dark on every reload.
  const setLive = (v) => { const next = setLiveFlag(v); log(`Turned Alfred ${v ? "on" : "off"} for the whole brokerage`); return next; };

  /* the call engine — one effect for the script, one for the clock */
  useEffect(() => {
    if (!call || call.done) return;
    if (call.phase === "dialing") {
      const t = setTimeout(() => {
        const o = call.outcome;
        if (o === "noanswer") { setCall((c) => c && { ...c, phase: "noanswer", done: true }); return; }
        if (o === "voicemail") { setCall((c) => c && { ...c, phase: "voicemail", done: true, lines: [{ who: "alfred", text: `Voicemail: this is Alfred with Gulf Bridge on load ${c.ref}. Please call us back at ${cfg.broker.officePhone || "the office"}.` }] }); return; }
        setCall((c) => c && { ...c, phase: "connected" });
      }, 1300);
      return () => clearTimeout(t);
    }
    if (call.consentPending) return;
    if (call.lines.length < call.script.length) {
      const t = setTimeout(() => setCall((c) => {
        if (!c) return c;
        const nextLine = c.script[c.lines.length];
        const lines = [...c.lines, nextLine];
        if (c.outcome === "declined" && lines.length === 2) return { ...c, lines: [...lines, { who: "carrier", text: "Actually, I'd rather you didn't record this." }, { who: "alfred", text: "Understood. I've stopped the recording. I'm still an AI assistant — is it alright if I carry on?" }], recording: false, consentPending: true };
        if (c.outcome === "wrongco" && lines.length === 2) return { ...c, lines: [...lines, { who: "carrier", text: "This isn't Northline. You've got the wrong company." }, { who: "alfred", text: "Thank you — I'm ending this call and flagging the number. I won't discuss the load with you." }], done: true, phase: "connected", flagged: "wrong company on the FMCSA number" };
        if (c.outcome === "hangup" && lines.length === 3) return { ...c, phase: "hangup", done: true };
        return { ...c, lines };
      }), call.lines.length === 0 ? 400 : 1350);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => finishCall(), 1400);
    return () => clearTimeout(t);
  }, [call && call.phase, call && call.lines.length, call && call.consentPending, call && call.done]);

  useEffect(() => {
    if (!call) { setElapsed(0); return; }
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [!!call]);

  function callStep(loadId, stepId) {
    const load = loadList.find((l) => l.id === loadId);
    // ⛔ THE PROVENANCE RULE LIVES HERE TOO, not only in the button's `disabled`. A UI-only rule
    // is one stale tab or one new call site away from being bypassed — the argument
    // `assertCarrierAssignable` makes on the backend for the same class of decision.
    if (!load || load.fromApi) return;
    if (!canCall() || !inScope(load, cfg, cfg.scope.myLoadsOnly ? seat : null).ok) return;
    const st = load.plan.find((s) => s.id === stepId);
    const carrier = load.carrierId ? carrierList.find((c) => c.id === load.carrierId) : null;
    const customer = custById(load.customerId);
    setElapsed(0);
    setCall({
      loadId, stepId, ref: load.ref, party: st.party, prevStatus: load.status, outcome: nextOutcome,
      partyName: st.party === "customer" ? customer.name : carrier ? carrier.contact : "the carrier",
      phase: "dialing", lines: [], recording: true, consentPending: false, done: false,
      script: scriptFor(st.party, st, carrier, load, customer),
    });
    setLoads((ls) => (ls || []).map((l) => (l.id === loadId ? { ...l, status: "calling" } : l)));
  }

  function finishCall() {
    setCall((c) => {
      if (!c) return null;
      const good = !["noanswer", "hangup", "wrongco"].includes(c.outcome);
      setLoads((ls) => (ls || []).map((l) => {
        if (l.id !== c.loadId) return l;
        const step = l.plan.find((s) => s.id === c.stepId);
        if (!good) {
          return { ...l, status: c.outcome === "wrongco" ? "attention" : c.prevStatus,
            activity: [...l.activity, { t: stamp(), head: step.title, body: simulated(c.outcome === "noanswer" ? "No answer. Attempt logged." : c.outcome === "wrongco" ? "Wrong company answered the FMCSA number. Alfred ended the call and flagged the carrier." : "Carrier hung up. Logged, no immediate callback.") }] };
        }
        const plan = l.plan.map((s) => (s.id === c.stepId ? { ...s, state: "done", at: stamp() } : s));
        const idx = plan.findIndex((s) => s.id === c.stepId);
        const nextIdx = plan.findIndex((s, i) => i > idx && s.state === "scheduled");
        if (nextIdx > -1) plan[nextIdx] = { ...plan[nextIdx], state: "due" };
        let status = c.prevStatus === "attention" || c.prevStatus === "covering" ? "tracking" : c.prevStatus;
        if (step.id === "custclose") status = "delivered";
        if (step.id === "vet") status = "tracking";
        // ⛔ THE LINE THAT WAS HERE CLAIMED A TMS RECEIVED THE DATA — and it simulated BOTH
        // branches of that write, "Written to PowerBroker." or "Not written — read-only mode.",
        // for a write no code in this app performs. Nothing here reaches a TMS.
        return { ...l, status, plan, activity: [...l.activity, { t: stamp(), head: step.title, body: simulated((c.party === "customer" ? `${custById(l.customerId).name} updated.` : "Carrier confirmed, details read back.") + (c.recording ? "" : " Unrecorded at the carrier's request.")) }] };
      }));
      return null;
    });
  }

  function endCall() {
    setCall((c) => {
      if (!c) return null;
      const load = loadList.find((l) => l.id === c.loadId);
      const step = load.plan.find((s) => s.id === c.stepId);
      log(simulated(`Ended a scripted call mid-conversation — "${step.title}"`), load.ref);
      setLoads((ls) => (ls || []).map((l) => (l.id === c.loadId ? { ...l, status: c.prevStatus, activity: [...l.activity, { t: stamp(), head: "Call ended by an operator", body: simulated(`${me.name} took the line off "${step.title}".`) }] } : l)));
      return null;
    });
  }

  function transferCall() {
    setCall((c) => {
      if (!c) return null;
      const load = loadList.find((l) => l.id === c.loadId);
      const step = load.plan.find((s) => s.id === c.stepId);
      log(simulated(`Took over a scripted call from Alfred — "${step.title}"`), load.ref);
      setLoads((ls) => (ls || []).map((l) => (l.id === c.loadId ? { ...l, status: c.prevStatus, activity: [...l.activity, { t: stamp(), head: "Human took the call", body: simulated(`${me.name} joined and Alfred stepped off. ${c.partyName} stayed on the line.`) }] } : l)));
      return null;
    });
  }

  function stopRecording() {
    setCall((c) => (c ? { ...c, consentPending: false, recording: false } : c));
    if (call) { const load = loadList.find((l) => l.id === call.loadId); log(simulated("Continued a call unrecorded after the other party declined"), load.ref); }
  }

  // ⛔ REFUSES ON A REAL LOAD, for the same reason `callStep` does: it writes to local state
  // and nothing persists it, so it moves a plan a dispatcher is reading and then forgets.
  const patchStep = (loadId, stepId, patch) => setLoads((ls) => (ls || []).map((l) => (
    l.id === loadId && !l.fromApi ? { ...l, plan: l.plan.map((s) => (s.id === stepId ? { ...s, ...patch } : s)) } : l)));

  /**
   * The most Alfred may agree to pay a carrier for this load.
   *
   * ⛔ IT WRITES BEFORE IT MOVES THE SCREEN. This was local state only, so a rep set a ceiling,
   * watched it appear, and it was gone on the next page load — the same way blocking a carrier
   * used to be. This one is money: the ceiling is what Alfred is allowed to commit the
   * brokerage to, and a ceiling that silently reverts to a higher number is the expensive
   * direction. If the write fails the screen says so and the figure does not move.
   */
  async function setCeiling(loadId, v) {
    const load = loadList.find((l) => l.id === loadId);
    // ⛔ `load.ref` below is unguarded, and so is the one in the catch — a miss here threw out
    // of the catch block as an unhandled rejection.
    if (!load) return;
    // ⛔ `v >= null` IS `v >= 0`, so every ceiling was rejected on an unpriced load — and the
    // message blamed the customer rate that does not exist. The cosmetic half of this function
    // got the null guard and the logic half did not.
    // ⛔ THE REFUSAL GOES TO THE PERSON TYPING, not only to the audit trail — see
    // `ceilingRefusal`, which is where the two rules live so they can be tested.
    const why = ceilingRefusal(v, load);
    if (why) {
      setCeilingError({ id: loadId, text: why });
      log(`Rejected a ceiling of $${v.toLocaleString()} on ${load.ref} — ${why}`, load.ref);
      return;
    }
    setCeilingError(null);
    setCeilingPending(loadId);
    try {
      // ⛔ DOLLARS ON THE WIRE, NOT CENTS. `upsertBrokerLoad` runs the value through
      // `toCents`, so posting cents would store a ceiling a HUNDRED TIMES TOO HIGH — Alfred
      // authorised to pay $290,000 to cover a $3,400 load. The column is `ceiling_cents`;
      // the API's contract is dollars.
      const saved = await freight.saveBrokerLoad({ ref: load.ref, ceiling: v });
      // ⛔ THE VALUE, NOT JUST THE FIELD'S EXISTENCE. `upsertBrokerLoad` COALESCEs the ceiling,
      // so any payload `toCents` cannot read is a silent no-op that returns the row unchanged —
      // which the old check read as success, logged as a change, and moved the figure for.
      if (!saved || saved.ceilingCents !== Math.round(v * 100)) {
        throw new Error("the server did not store the ceiling it was sent.");
      }
      // ⛔ AND IT MUST BE THIS LOAD'S ROW. The write is keyed on `ref` — a display string — so
      // this is the check that the row written is the row the operator was looking at. The
      // backend now refuses a ceiling-only body on a ref it cannot find (`no_such_load`) rather
      // than inserting a phantom, so this is the second line of that defence, not the first.
      //
      // ⛔ IT NAMES THE IDS, NOT THE REF. The first version said "the server wrote a different
      // load (${saved.ref})" — and `saved.ref` is the SAME ref, because that is the key the
      // write used. It printed the load's own name as the name of a different load.
      if (String(saved.id) !== String(load.id)) {
        throw Object.assign(
          new Error(`the server answered for a different row (id ${saved.id}, not ${load.id}).`),
          { status: 409 });
      }
      // ⛔ `overCeiling` TRAVELS WITH IT. The server computes buy > ceiling, so raising the
      // ceiling above the buy rate has to clear the warning; recomputing it here would be a
      // second opinion about money, and leaving the old one on screen is a stale alarm.
      const cents = saved.ceilingCents;
      setLoads((ls) => (ls || []).map((l) => (l.id === loadId
        ? { ...l, ceilingCents: cents, ceiling: Math.round(cents / 100), ceilingText: saved.ceiling ?? null, overCeiling: !!saved.overCeiling }
        : l)));
      log(`Ceiling changed from ${load.ceiling == null ? "unset" : `$${load.ceiling.toLocaleString()}`} to $${Math.round(cents / 100).toLocaleString()}`, load.ref);
    } catch (err) {
      // ⛔ A 4xx IS A KNOWN OUTCOME. The server read the request, refused it and said why, so
      // nothing was written — telling the operator to reload after that is noise. Anything else
      // leaves it genuinely unknown. See `ceilingFailure`.
      // `ceilingFailure` classifies the error itself — see its `definite` default.
      setCeilingError({ id: loadId, text: ceilingFailure(load, err) });
    } finally {
      // ⛔ `finally`, so a throw cannot leave the button saying "Saving…" for the rest of the
      // session with no way back.
      setCeilingPending(null);
    }
  }
  function claim(id) {
    const item = queue.find((x) => x.id === id);
    if (item.assignedTo) return;
    log(`Took the ${KIND[item.kind].toLowerCase()} handoff`, item.loadRef);
    setQueue((xs) => xs.map((x) => (x.id === id && !x.assignedTo ? { ...x, assignedTo: seat, takenAt: stamp() } : x)));
  }
  function release(id) {
    const item = queue.find((x) => x.id === id);
    log(`Put the ${KIND[item.kind].toLowerCase()} handoff back in the queue`, item.loadRef);
    setQueue((xs) => xs.map((x) => (x.id === id ? { ...x, assignedTo: null, takenAt: null } : x)));
  }
  function resolve(id, outcome, note) {
    const item = queue.find((x) => x.id === id);
    log(`${KIND[item.kind]} closed — ${outcome}${note ? ` (${note})` : ""}`, item.loadRef);
    setQueue((xs) => xs.filter((x) => x.id !== id));
  }
  /**
   * Approve or block a carrier.
   *
   * ⛔ IT WRITES BEFORE IT MOVES THE SCREEN. Blocking is the whole point of this tab: a
   * brokerage that has been defrauded needs it to STICK. This used to update local state
   * only, so the tick moved, the audit line was written, and the next page load showed the
   * carrier approved again — the most convincing possible way to not block somebody. If the
   * write fails the screen says so and nothing moves.
   */
  async function setStatus(id, v, reason) {
    const c = carrierList.find((x) => x.id === id);
    if (!c) return;
    // ⛔ ONE WRITE AT A TIME, or two in flight resolve in the wrong order and the screen ends
    // up disagreeing with the row. The Segmented is disabled on this too.
    if (statusPending !== null) return;
    setStatusPending(id);
    setCarrierWriteError(null);
    try {
      const saved = await freight.setBrokerCarrierStatus(id, v, reason);
      // ⛔ NO GUESSING ON THIS PATH. `saved?.status ?? v` treated a 2xx with no carrier in the
      // body as a successful move and advanced the row on the strength of the request alone.
      if (!saved || !saved.status) throw new Error("the server did not confirm the change");
      setCarriers((xs) => (xs || []).map((x) => (x.id === id ? { ...x, ...saved } : x)));
      log(`${c.name}: ${statusWord(c.status)} \u2192 ${statusWord(saved.status)} — "${reason}"`, c.id);
    } catch (err) {
      // NOT written, so NOT logged as though it were, and the row keeps its real status.
      setCarrierWriteError({ id, text: writeFailure(c, v, err) });
    } finally {
      setStatusPending(null);
    }
  }
  /**
   * Add a carrier to the book.
   *
   * ⛔ IT RETURNS THE REFUSAL RATHER THAN THROWING IT AWAY. The backend answers 409 when the
   * name is already in the book — naming the row and saying whether it is blocked — because
   * writing onto the matching row is how one company's FMCSA callback number ended up on
   * another's record. The form shows that answer and lets a person decide; nothing here
   * resolves it automatically.
   */
  /**
   * Put a shipper on the customer file.
   *
   * ⛔ WHAT LANDED IS DECIDED OUTSIDE THE try, for the reason `addCarrier` below carries: a
   * try spanning the POST and the state updates reported "Not added" for a row that was in the
   * database, and "Not added" is what invites the retry that creates the duplicate.
   */
  async function addCustomer(fields) {
    setCustomerAddPending(true);
    let committed = null;
    try {
      const saved = await freight.saveBrokerCustomer(fields);
      if (!saved || !saved.id) throw new Error("the server did not say which customer it saved");
      committed = { saved, row: brokerCustomerFromApi(saved) };
    } catch (err) {
      const body = err?.body || {};
      // ⛔ A NAME ALREADY ON FILE IS A REFUSAL, NOT A SECOND ROW. A shipper has no docket, so
      // the name is the identity; two rows for one account means a load's customer resolves to
      // whichever was written first while somebody edits the other.
      if (body.error === 'customer_exists') {
        return { ok: false, text: body.message || 'That account is already on your customer file.',
          existing: body.existing || null };
      }
      // Every other refusal the backend can give carries its own sentence — the contradiction
      // between "never phone them" and "update by phone", a portal channel with no portal,
      // a free-time window that is not minutes. `humanError` falls through to it.
      if (body.message) return { ok: false, text: body.message };
      return { ok: false, text: `Not added — ${humanError(err)}` };
    } finally {
      setCustomerAddPending(false);
    }
    // ⛔ OUTSIDE THE try. And the registry is updated too, or `custById` on every load still
    // answers from the list this screen read on mount — the new account would show on the
    // Customers tab and read as "Customer not on file" in the Inspector.
    setCustomers((xs) => [...(xs || []), committed.row]);
    // the registry follows `customers` through the effect below — see the note on it
    log(`Added customer ${committed.row.name}`, null);
    return { ok: true, customer: committed.row };
  }

  async function addCarrier(fields) {
    setAddPending(true);
    // ⛔ WHAT ACTUALLY LANDED, kept outside the try. The try used to span the POST AND the
    // state updates AND log(), so anything throwing after the write returned "Not added" for
    // a carrier that was in the database — and "Not added" is what invites the retry.
    let committed = null;
    try {
      const saved = await freight.saveBrokerCarrier(fields);
      if (!saved || !saved.id) throw new Error("the server did not say which carrier it saved");
      committed = { saved, row: brokerCarrierFromApi(saved) };
    } catch (err) {
      const body = err?.body || {};
      if (body.error === 'carrier_exists') {
        return { ok: false, text: body.message || 'That carrier is already in your book.',
          existing: body.existing || null, blocked: !!body.blocked, matchedOn: body.matchedOn || null };
      }
      if (body.error === 'name_required') return { ok: false, text: "A company name is required." };
      // ⛔ A DOCKET FIELD HOLDING A WORD. The backend refuses it rather than keying on it (two
      // carriers that both wrote "pending" would collide) or storing it unkeyed (the unique
      // index on the raw column answers with a bare 500).
      if (body.error === 'bad_docket') return { ok: false, text: body.message || "That is not a docket number." };
      return { ok: false, text: `Not added — ${humanError(err)}` };
    } finally {
      setAddPending(false);
    }
    // ⛔ OUTSIDE THE try, so a throw in here cannot be reported as a failed write. And the
    // audit line records the status the SERVER returned, not the one this screen assumed —
    // it used to say "not reviewed" unconditionally.
    setCarriers((xs) => withCarrierAdded(xs, committed.saved));
    log(`Added carrier ${committed.row.name} — ${statusWord(committed.saved.status)}`, committed.row.id);
    return { ok: true, carrier: committed.row };
  }

  function openLoadByRef(ref) { const l = loadList.find((x) => x.ref === ref); if (l) { setView("loads"); setCarrierProfile(null); setSelected(l.id); } }
  function openCarrier(id) { setView("carriers"); setCarrierProfile(id); }

  const visible = loadList.filter((l) => {
    if (!sees(me, l.branch)) return false;
    if (statusFilter === "active" && CLOSED.includes(l.status)) return false;
    // ⛔ `CLOSED` HERE TOO. This was the one reader of a load's status that did not consult it,
    // and it is the filter a rep uses to find freight that needs a truck — so a cancelled load
    // with no carrier was listed under exactly the heading that means "call somebody about
    // this". The same defect as the six figures, on the control instead of the count.
    if (statusFilter === "uncovered" && (l.carrierId || CLOSED.includes(l.status))) return false;
    if (statusFilter === "scope" && !inScope(l, cfg, cfg.scope.myLoadsOnly ? seat : null).ok) return false;
    if (!q.trim()) return true;
    const carrier = l.carrierId ? carrierList.find((c) => c.id === l.carrierId) : null;
    // Same as the carrier search: a stop with no name put the word "undefined" in the haystack.
    return [l.ref, custById(l.customerId).name, carrier ? carrier.name : "",
            ...(l.stops || []).map((st) => [st.name, st.city].filter(Boolean).join(" "))]
      .filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase());
  });

  // One sentence for whichever of the four facts is true — see `loadListNote`.
  const listNote = loadListNote({ loading: loadsLoading, loadError, total: loadList.length, matching: visible.length, branch: me.branch });

  return (
    <div className="alf-root" style={{ fontFamily: sans, background: T.wash, color: T.ink, minHeight: "100vh", WebkitFontSmoothing: "antialiased" }}>
      <style>{`
        .alf-bar { animation: alfBar 1s ease-in-out infinite; transform-origin: bottom; }
        @keyframes alfBar { 0%,100% { transform: scaleY(.28);} 50% { transform: scaleY(1);} }
        .alf-dot { width:5px; height:5px; border-radius:999px; background:#fff; animation: alfPulse 1.4s ease-in-out infinite; }
        @keyframes alfPulse { 0%,100% { opacity:.3;} 50% { opacity:1;} }
        .alf-fade { animation: alfFade .26s cubic-bezier(.4,0,.2,1); }
        @keyframes alfFade { from { opacity:0; transform: translateY(4px);} to { opacity:1; transform:none;} }
        .alf-row:hover { background:#FAFAFA; }
        .alf-btn { transition: opacity .15s, transform .1s; }
        .alf-btn:active { transform: scale(.975); }
        .alf-close:hover { color:#000; }
        .alf-root input::placeholder, .alf-root textarea::placeholder { color:${T.ink4}; }
        .alf-root input:focus, .alf-root select:focus, .alf-root textarea:focus { border-color:#000 !important; }
        .alf-root button:focus-visible, .alf-root input:focus-visible, .alf-root select:focus-visible, .alf-root textarea:focus-visible { outline:2px solid #000; outline-offset:2px; }
        @media (prefers-reduced-motion: reduce) { .alf-bar,.alf-dot,.alf-fade { animation:none !important; } }
      `}</style>

      <PrototypeBanner what="Brokerage board." saved={view === "carriers" || view === "customers" ? "write" : null} />

      <header style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(255,255,255,0.9)", backdropFilter: "saturate(180%) blur(20px)", borderBottom: `1px solid ${T.hair}` }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 22px", minHeight: 54, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.4 }}>Alfred</span>
          <span style={{ fontSize: 12, color: T.ink3 }}>Gulf Bridge</span>
          <Segmented value={view} onChange={(v) => { setView(v); if (v !== "carriers") setCarrierProfile(null); }} options={[
            { v: "overview", label: "Overview" }, { v: "loads", label: "Loads" }, { v: "carriers", label: "Carriers" },
            { v: "customers", label: "Customers" }, { v: "history", label: "History" }, { v: "settings", label: "Settings" }]} />
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {call && <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5 }}><Waveform active /> 1 scripted call</span>}
            <span style={{ display: "flex", alignItems: "center", gap: 7 }} title="Next simulated call outcome">
              <Micro>outcome</Micro>
              <select value={nextOutcome} onChange={(e) => setNextOutcome(e.target.value)} style={selectStyle}>
                {Object.entries(OUTCOMES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </span>
            <select value={seat} onChange={(e) => setSeat(e.target.value)} style={selectStyle} title="Who's at the keyboard">
              {cfg.team.filter((t) => !t.ooo).map((p) => <option key={p.id} value={p.id}>{String(p.name || "Unnamed seat").split(" ")[0]} — {p.role}{p.branch !== "All branches" ? ` · ${p.branch}` : ""}</option>)}
            </select>
            <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ fontSize: 12.5, color: cfg.live ? T.ink : T.ink3 }}>{cfg.live ? "Live" : "Dark"}</span>
              <Toggle on={cfg.live} onChange={setLive} />
            </span>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 22px 60px" }}>
        {view === "overview" && <Overview queue={queue} loads={loadList} carriers={carrierList} carriersKnown={carriersKnown} loadsKnown={loadsKnown} loadsLoading={loadsLoading} seat={seat} call={call} onOpenLoad={openLoadByRef} onOpenCarrier={openCarrier} onResolve={resolve} onClaim={claim} onRelease={release} />}
        {view === "history" && <History carriers={carrierList} carriersKnown={carriersKnown} audit={audit} />}
        {view === "settings" && <Settings cfg={cfg} onSetCfg={(c) => commitConfig(c)} />}
        {view === "customers" && <div style={{ maxWidth: 820 }}><Customers loads={loadList} loadsKnown={loadsKnown} loadsLoading={loadsLoading} seat={seat} onOpenLoad={openLoadByRef} customers={customerRows} loading={customersLoading} loadError={customerError} onAddCustomer={addCustomer} addPending={customerAddPending} /></div>}
        {view === "carriers" && <div style={{ maxWidth: 820 }}><Carriers carriers={carrierList} loading={carriersLoading} loadError={carrierError} writeError={carrierWriteError} pendingStatusId={statusPending} loads={loadList} loadsKnown={loadsKnown} loadsLoading={loadsLoading} statusTrail={statusTrail} seat={seat} audit={audit} profileId={carrierProfile} setProfileId={setCarrierProfile} onOpenLoad={openLoadByRef} onSetStatus={setStatus} onAddCarrier={addCarrier} addPending={addPending} /></div>}

        {view === "loads" && (
          <div style={{ display: "flex", gap: 22, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 400px", minWidth: 300, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ display: "flex", gap: 8, padding: "12px 14px", borderBottom: `1px solid ${T.hairSoft}`, flexWrap: "wrap" }}>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search load, customer, carrier, city" style={{ flex: 1, minWidth: 170, font: "inherit", fontSize: 13, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "7px 10px", outline: "none" }} />
                <Segmented value={statusFilter} onChange={setStatusFilter} options={[{ v: "active", label: "Active" }, { v: "scope", label: "In scope" }, { v: "uncovered", label: "Uncovered" }, { v: "all", label: "All" }]} />
              </div>
              {/* ⛔ FOUR DIFFERENT FACTS, NOT ONE EMPTY LIST. "we have not asked yet", "we
                  asked and could not read it", "your board has no loads" and "none match this
                  filter" were all rendering as "No loads match in Houston" — which on a failed
                  read tells a rep their branch is quiet when the truth is that nobody knows.
                  Same ladder as the Carriers tab above. */}
              {listNote && <div style={{ padding: "24px 20px", fontSize: 13.5, color: T.ink3, lineHeight: 1.55 }}>{listNote}</div>}
              {visible.map((l, i) => {
                const carrier = l.carrierId ? carrierList.find((c) => c.id === l.carrierId) : null;
                const flags = queue.filter((x) => x.loadRef === l.ref).length;
                const due = l.plan.find((s) => s.state === "due");
                const late = lateRisk(l);
                const sc = inScope(l, cfg, cfg.scope.myLoadsOnly ? seat : null);
                return (
                  <button key={l.id} onClick={() => setSelected(l.id)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "16px 20px", border: "none", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}`, background: l.id === selected ? "#F0F0F0" : "transparent", cursor: "pointer", textAlign: "left", font: "inherit", position: "relative", opacity: sc.ok ? 1 : 0.5 }}>
                    <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: (call && call.loadId === l.id) || flags ? T.ink : "transparent" }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{l.ref}</span>
                        <Chip status={call && call.loadId === l.id ? "calling" : l.status} />
                        <Soft>{l.freight.equipment}</Soft>
                        {!sc.ok && <Soft>out of scope</Soft>}
                        {late && <Tag>late risk</Tag>}
                        {l.carrierId && !l.trackingOk && <Tag>ping stale</Tag>}
                        {flags > 0 && <span style={{ fontSize: 11.5, borderBottom: `1px solid ${T.ink}` }}>{flags} waiting</span>}
                      </span>
                      <span style={{ display: "block", fontSize: 15, fontWeight: 500, letterSpacing: -0.3, marginTop: 5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{laneLabel(l)}</span>
                      <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {custById(l.customerId).name} · {carrier ? carrier.name : "no carrier"}{due ? ` · next: ${due.title}` : ""}
                      </span>
                    </span>
                    <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
                  </button>
                );
              })}
            </div>

            <div style={{ flex: "1 1 400px", minWidth: 300, position: "sticky", top: 80, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 10, minHeight: 340, maxHeight: "calc(100vh - 110px)", overflow: "auto" }}>
              <Inspector key={selectedLoad ? selectedLoad.id : "none"} load={selectedLoad} carriers={carrierList} carriersKnown={carriersKnown} call={call} elapsed={elapsed} seat={seat}
                onCallStep={callStep} onEndCall={endCall} onTransfer={transferCall} onStopRecording={stopRecording} onPatchStep={patchStep} onSetCeiling={setCeiling} onClose={() => setSelected(null)} onOpenCarrier={openCarrier}
                {...(selectedLoad ? {
                  ceilingError: ceilingError && String(ceilingError.id) === String(selectedLoad.id) ? ceilingError.text : null,
                  // ⛔ `String(null) === String(null)` IS TRUE, so the first version reported a
                  // write in flight whenever nothing was selected. Harmless only because the
                  // Inspector returns early on a missing load — which is luck, not design.
                  ceilingPending: ceilingPending != null && String(ceilingPending) === String(selectedLoad.id),
                } : {})} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
